import * as THREE from 'three';
import { tuning } from '@/config/tuning';
import type { WaterTune } from '@/config/cities';
import {
  doubleDeckerCanvases,
  paintedBoxMaterials,
  vehicleFrontCanvas,
  vehicleRoofCanvas,
  vehicleSideCanvas,
} from './propKit';
import {
  LEAN_PALM,
  LEAN_PALM_FRONDS,
  LEAN_SHAPES,
  leanPalmFronds,
  type LeanSpecies,
  type LeanTree,
} from './leanTrees';
import {
  DAKAR_SAND,
  HILL_GREEN,
  LONDON_STONE,
  MORAINE,
  MUMBAI_GOTHIC,
  RIO_JUNGLE,
  TOKYO_TIMBER,
} from './palettes';
import {
  buildPondGrid,
  buildRunGrid,
  planPonds,
  resolveWaterCfg,
  splitWater,
  WATER,
  type WaterGrid,
  type WaterTrackLike,
} from './water';
import type {
  AdScreenInst,
  BoxInst,
  CarInst,
  FlatInst,
  HillInst,
  NeonInst,
  PlaceInst,
  SceneryArrays,
  TreeInst,
} from './types';

/**
 * scenery/builders.ts — turns the zones' instance lists into a handful of
 * InstancedMesh draw calls. Stable plumbing: zones decide WHAT and WHERE;
 * these decide HOW it's drawn (geometry, materials, shader tricks).
 */

const UNIT_BOX = new THREE.BoxGeometry(1, 1, 1);

// Shared scratch objects (builders run once per city load; no per-frame cost).
const M = new THREE.Matrix4();
const Q = new THREE.Quaternion();
const E = new THREE.Euler();
const P = new THREE.Vector3();
const S = new THREE.Vector3();
const C = new THREE.Color();
const ID_Q = new THREE.Quaternion();

function yawQ(rotY: number): THREE.Quaternion {
  E.set(0, rotY, 0);
  return Q.setFromEuler(E);
}

/** Cheap deterministic 0..1 hash — used to vary instances (tree rotation, canopy
 *  blob jitter) FROM THE INSTANCE INDEX, so positions stay identical to before
 *  and only the look gets richer (no new rng draws, no layout reshuffle). */
function hash(n: number): number {
  const s = Math.sin(n * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

/** Bake a vertical light→dark gradient into a geometry's vertex colours (top
 *  bright, bottom shaded). Reads as soft canopy volume / ambient occlusion and
 *  is essentially free; it MULTIPLIES the per-instance colour at draw time. */
function bakeYGradient(geo: THREE.BufferGeometry, bottomMul: number, topMul: number): void {
  const pos = geo.attributes.position;
  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < pos.count; i++) {
    minY = Math.min(minY, pos.getY(i));
    maxY = Math.max(maxY, pos.getY(i));
  }
  const span = maxY - minY || 1;
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = (pos.getY(i) - minY) / span;
    const m = bottomMul + (topMul - bottomMul) * t;
    col[i * 3] = col[i * 3 + 1] = col[i * 3 + 2] = m;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
}

// ---- Procedural greenery/ground micro-textures (M36) — grayscale detail maps,
//      generated once, cached + shared, and MULTIPLIED by each surface's
//      per-instance colour (so one grass map serves jungle dark → park bright).
//      Deterministic (own tiny PRNG) so they're identical every load. ----
let _texSeed = 0;
function texRand(): number {
  _texSeed = (_texSeed * 1664525 + 1013904223) >>> 0;
  return _texSeed / 4294967296;
}
function texCanvas(size: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const x = c.getContext('2d');
  if (!x) throw new Error('Scenery: no 2D context for texture');
  return [c, x];
}
function shareTex(t: THREE.CanvasTexture, repeat = true): THREE.CanvasTexture {
  if (repeat) t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.userData.shared = true; // cached + reused by every city → never freed by the disposer
  return t;
}

const groundTexCache = new Map<string, THREE.CanvasTexture>();
/** A grayscale grass/sand grain map (mottle + speckle), tiled by world size.
 *  Exported (GS4b/GS5) so Scenery's sand-slick domes + sand/dune ramp shells
 *  wear the same grain as the ground sheets. SHARED + cached — consumers must
 *  clone() before touching repeat/offset. */
export function groundTexture(kind: 'grass' | 'sand'): THREE.CanvasTexture {
  const hit = groundTexCache.get(kind);
  if (hit) return hit;
  _texSeed = kind === 'grass' ? 0x6a51 : 0x5a4d;
  const [c, x] = texCanvas(128);
  if (kind === 'grass') {
    x.fillStyle = gray(208);
    x.fillRect(0, 0, 128, 128);
    // Soft darker clumps (uneven turf).
    for (let i = 0; i < 26; i++) {
      x.globalAlpha = 0.16;
      x.fillStyle = gray(150 + ((texRand() * 50) | 0));
      const r = 10 + texRand() * 26;
      x.beginPath();
      x.arc(texRand() * 128, texRand() * 128, r, 0, Math.PI * 2);
      x.fill();
    }
    // Fine blade streaks, light + dark.
    x.globalAlpha = 0.5;
    for (let i = 0; i < 520; i++) {
      x.strokeStyle = gray(
        texRand() < 0.5 ? 150 + ((texRand() * 40) | 0) : 220 + ((texRand() * 32) | 0),
      );
      x.lineWidth = 1;
      const bx = texRand() * 128;
      const by = texRand() * 128;
      x.beginPath();
      x.moveTo(bx, by);
      x.lineTo(bx + (texRand() - 0.5) * 2, by - 2 - texRand() * 3);
      x.stroke();
    }
  } else {
    x.fillStyle = gray(224);
    x.fillRect(0, 0, 128, 128);
    // Faint wind ripples.
    x.globalAlpha = 0.1;
    for (let i = 0; i < 128; i += 2) {
      x.strokeStyle = gray(200 + ((Math.sin(i * 0.5) * 18) | 0));
      x.lineWidth = 1;
      x.beginPath();
      x.moveTo(0, i + Math.sin(i) * 1.5);
      x.lineTo(128, i + Math.sin(i + 2) * 1.5);
      x.stroke();
    }
    // Grain speckle.
    x.globalAlpha = 0.5;
    for (let i = 0; i < 900; i++) {
      x.fillStyle = gray(
        texRand() < 0.5 ? 196 + ((texRand() * 26) | 0) : 236 + ((texRand() * 18) | 0),
      );
      x.fillRect(texRand() * 128, texRand() * 128, 1, 1);
    }
  }
  x.globalAlpha = 1;
  const t = shareTex(new THREE.CanvasTexture(c));
  groundTexCache.set(kind, t);
  return t;
}

// ---- PHOTOGRAPHIC FOLIAGE SET (tree round) --------------------------------
//
// THE DEFECT the owner reported: "the trees are still very 'poly'". Root cause
// was not the polygon count — it was that there was NO LEAF SILHOUETTE anywhere
// in the game. Every canopy was a SOLID IcosahedronGeometry ball wearing a
// 64px canvas of grey noise circles (the M36 `foliageTexture`, deleted here).
// A solid 20-face ball with mottle on it reads as exactly what it is.
//
// THE FIX is the standard racing-game one: ALPHA-CUT CARDS. Three real
// photographic textures, extracted from CC-BY Sketchfab tree models in Blender
// (scripts/blender/make_foliage.py re-bakes them):
//   foliage_atlas.webp  1024² RGBA — a 2×2 grid of 512px canopy tiles
//                       (0,0) broadleaf A · (1,0) broadleaf B
//                       (0,1) conifer/needle · (1,1) dry/sparse
//   palm_frond.webp     512² RGBA — one frond, rachis up the image centre
//   bark.webp           512² RGB, seamless, de-lit
//
// THE ART IS DELIBERATELY DESATURATED AND NEAR-NEUTRAL. Every canopy's real
// colour is its per-instance setColorAt tint (each city's greens, NYC's autumn,
// Tokyo's sakura pink, Dakar's dry olive) multiplied by the baked Y gradient —
// exactly as it was with the grey-noise canvas. Saturating the atlas would
// fight all of that; don't "fix" it, and don't remove the instance colouring.
//
// LAZY BY RULE: builders.ts is imported by the headless placement tests, where
// THREE.TextureLoader (ImageLoader → document) throws. Nothing loads until a
// city actually builds; core/preload.ts warms the HTTP cache beforehand.
const texLoader = new THREE.TextureLoader();
const FOLIAGE_BASE = `${import.meta.env.BASE_URL}textures/foliage/`;

/** Alpha cut-off for every leaf/frond card. Deliberately below the usual 0.5:
 *  mipmapping averages a canopy tile's alpha DOWN with distance, and a higher
 *  cut makes distant canopies visibly thin out into scaffolding. */
const LEAF_ALPHA_TEST = 0.42;

/** Materials built before their texture finished decoding — flagged for a
 *  recompile on load so a cold first city can never be stuck on the pre-load
 *  state (the alpha test has nothing to test until the image lands). */
const pendingLeafMats: THREE.Material[] = [];
function onFoliageLoad(): void {
  for (const m of pendingLeafMats) m.needsUpdate = true;
  pendingLeafMats.length = 0;
}

const foliageTexCache = new Map<string, THREE.Texture>();
/** Load (once) one of the shared foliage files. `repeat` switches on tiling —
 *  bark tiles, the alpha atlases must CLAMP (a wrapped atlas would bleed one
 *  tile's leaves into the next). */
function foliageFile(file: string, repeat = false): THREE.Texture {
  const hit = foliageTexCache.get(file);
  if (hit) return hit;
  const t = texLoader.load(`${FOLIAGE_BASE}${file}`, onFoliageLoad);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  if (repeat) t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.userData.shared = true; // shared by every city → never freed by the disposer
  foliageTexCache.set(file, t);
  return t;
}
const foliageAtlas = (): THREE.Texture => foliageFile('foliage_atlas.webp');
const palmFrondTexture = (): THREE.Texture => foliageFile('palm_frond.webp');
const barkTexture = (): THREE.Texture => foliageFile('bark.webp', true);

/** Bark is a de-lit photo averaging ~0.58 in linear light, so mapping it onto
 *  a trunk would darken every trunk by that factor. Trunk colours are scaled by
 *  this to land back where they read today (clamped, so nothing blows out). */
const BARK_GAIN = 1.6;
function barkTint(hex: number): THREE.Color {
  const c = new THREE.Color(hex);
  const peak = Math.max(c.r, c.g, c.b, 1e-4);
  return c.multiplyScalar(Math.min(BARK_GAIN, 1 / peak));
}

/** A trunk material: bark photo × the species colour, smooth-shaded (the extra
 *  radial segments below + a real bark map make flat shading read as facets). */
function trunkMaterial(hex: number): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({
    color: barkTint(hex),
    roughness: 1,
    map: barkTexture(),
  });
  pendingLeafMats.push(m);
  return m;
}

/** Scale a geometry's UVs in place — how each trunk sets its own bark density
 *  (one shared texture, no per-geometry texture clones). */
function scaleUv(geo: THREE.BufferGeometry, su: number, sv: number): void {
  const uv = geo.getAttribute('uv') as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * su, uv.getY(i) * sv);
  uv.needsUpdate = true;
}

/** One atlas tile as [u0, v0, u1, v1], INSET a few texels so low mip levels
 *  can't bleed a neighbour tile's leaves in. NOTE the row flip: three uploads
 *  images flipY, so the atlas's TOP row is the v ∈ [0.5, 1] half. */
const TILE_INSET = 0.005;
function atlasTile(col: 0 | 1, row: 0 | 1): readonly [number, number, number, number] {
  const u0 = col * 0.5 + TILE_INSET;
  const v0 = (1 - row) * 0.5 + TILE_INSET;
  const span = 0.5 - TILE_INSET * 2;
  return [u0, v0, u0 + span, v0 + span];
}
const TILE_LEAF_A = atlasTile(0, 0);
const TILE_LEAF_B = atlasTile(1, 0);
const TILE_NEEDLE = atlasTile(0, 1);
const TILE_DRY = atlasTile(1, 1);

/** One card of a canopy cluster: which way it faces, how far it is tilted out
 *  of vertical, which atlas tile it wears, and whether the tile is mirrored
 *  (so repeated tiles inside one canopy don't read as clones). */
interface LeafCard {
  yaw: number;
  tilt: number;
  tile: readonly [number, number, number, number];
  flip?: boolean;
}

/**
 * A CANOPY CARD CLUSTER — intersecting alpha-cut quads that replace a solid
 * foliage ball. Two rules make this work rather than look like cardboard:
 *
 * 1. EVERY VERTEX SITS EXACTLY ON THE SPHERE of the given radius (each card is
 *    a regular hexagon whose circumradius is that radius, built in an
 *    orthonormal card frame). That keeps the ENVELOPE CONTRACT byte-exact:
 *    TREE_CANOPY_REACH, finishClear's 4·s canopy radius and the lean-tree
 *    clearance model all assume the old IcosahedronGeometry's reach, whose
 *    vertices sat on the same sphere.
 * 2. VERTEX NORMALS ARE SPHERICAL (normalize(position)), NOT the quads' own
 *    face normals, and the material is smooth-shaded. This is the whole trick:
 *    flat cards lit by their own normals read as flat cards, cards lit by a
 *    sphere's normals read as one rounded leafy mass. Without it this change
 *    looks WORSE than the ball it replaces.
 *
 * Hexagons (fan-triangulated, 4 tris each) rather than quads: same corner
 * reach, ~30% more of the tile's leaf disc kept, and 4 cards come to 16 tris —
 * FEWER than the 20-triangle icosahedron they replace.
 */
function leafClusterGeo(radius: number, cards: readonly LeafCard[]): THREE.BufferGeometry {
  const pos: number[] = [];
  const nor: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  for (const card of cards) {
    const base = pos.length / 3;
    // Orthonormal card frame: ex across, ez the untilted normal, ey up (rolled
    // out of vertical by `tilt` toward ez — a near-horizontal card gives the
    // cluster a real top surface, which a fan of vertical cards has none of).
    const ex = [Math.cos(card.yaw), 0, Math.sin(card.yaw)] as const;
    const ez = [-Math.sin(card.yaw), 0, Math.cos(card.yaw)] as const;
    const st = Math.sin(card.tilt);
    const ct = Math.cos(card.tilt);
    const ey = [ez[0] * st, ct, ez[2] * st] as const;
    for (let k = 0; k < 6; k++) {
      const th = (k / 6) * Math.PI * 2; // vertices at 0°/180° → wider than tall
      const px = Math.cos(th);
      const py = Math.sin(th);
      const x = (ex[0] * px + ey[0] * py) * radius;
      const y = (ex[1] * px + ey[1] * py) * radius;
      const z = (ex[2] * px + ey[2] * py) * radius;
      pos.push(x, y, z);
      nor.push(x / radius, y / radius, z / radius); // THE spherical-normal trick
      const u = card.flip ? 0.5 - 0.5 * px : 0.5 + 0.5 * px;
      const v = 0.5 + 0.5 * py;
      uv.push(
        card.tile[0] + u * (card.tile[2] - card.tile[0]),
        card.tile[1] + v * (card.tile[3] - card.tile[1]),
      );
    }
    for (let k = 1; k < 5; k++) idx.push(base, base + k, base + k + 1);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(nor), 3));
  g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(uv), 2));
  g.setIndex(idx);
  return g;
}

/** The conifer cousin: tapered cards on the unit cone's own profile (radius 1
 *  at y −0.5, narrowing to the apex at +0.5), so the crown keeps the cone
 *  silhouette CylinderGeometry(0, 1, 1, 6) gave it while the needle tile's
 *  alpha eats the hard edges. 4 cards × 2 tris = 8 (the cone was 12). */
function needleConeGeo(): THREE.BufferGeometry {
  const OUTLINE: readonly [number, number][] = [
    [-1, -0.5],
    [1, -0.5],
    [0.3, 0.5],
    [-0.3, 0.5],
  ];
  const pos: number[] = [];
  const nor: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  for (let c = 0; c < 4; c++) {
    const yaw = (c / 4) * Math.PI;
    const base = pos.length / 3;
    const ex = [Math.cos(yaw), 0, Math.sin(yaw)] as const;
    for (const [px, py] of OUTLINE) {
      const x = ex[0] * px;
      const z = ex[2] * px;
      const len = Math.hypot(x, py, z) || 1;
      pos.push(x, py, z);
      nor.push(x / len, py / len, z / len);
      const u = c % 2 ? 1 - (0.5 + 0.5 * px) : 0.5 + 0.5 * px;
      uv.push(
        TILE_NEEDLE[0] + u * (TILE_NEEDLE[2] - TILE_NEEDLE[0]),
        TILE_NEEDLE[1] + (0.5 + py) * (TILE_NEEDLE[3] - TILE_NEEDLE[1]),
      );
    }
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(nor), 3));
  g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(uv), 2));
  g.setIndex(idx);
  return g;
}

/** The broadleaf card set: three crossed vertical cards 60° apart (so no
 *  horizontal viewing angle is ever more than 30° off a card's face) plus one
 *  laid nearly flat for the crown's top surface. Tiles alternate + mirror so a
 *  single canopy never shows the same leaf photo twice the same way. */
const BROADLEAF_CARDS: readonly LeafCard[] = [
  { yaw: 0, tilt: 0, tile: TILE_LEAF_A },
  { yaw: Math.PI / 3, tilt: 0, tile: TILE_LEAF_B, flip: true },
  { yaw: (2 * Math.PI) / 3, tilt: 0, tile: TILE_LEAF_A, flip: true },
  // MEASURED: a near-horizontal fourth card (tilt 1.2) renders as a bright
  // horizontal STREAK across the canopy from the chase camera, which rides
  // level with the crowns. 0.85 rad is diagonal enough to still roof the
  // cluster from above without ever being exactly edge-on from the road.
  { yaw: Math.PI / 6, tilt: 0.85, tile: TILE_LEAF_B },
];
/** Same layout on the dry/sparse tile — the Sahel crowns (baobabs). */
const DRY_CARDS: readonly LeafCard[] = BROADLEAF_CARDS.map((c) => ({ ...c, tile: TILE_DRY }));

/** A leaf/frond material. `flatShading` is FALSE on purpose everywhere here:
 *  flat shading recomputes face normals and defeats the spherical-normal trick
 *  the whole cluster is built around. */
function leafMaterial(
  map: THREE.Texture,
  extra: Partial<THREE.MeshStandardMaterialParameters> = {},
): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({
    map,
    alphaTest: LEAF_ALPHA_TEST,
    side: THREE.DoubleSide,
    roughness: 1,
    vertexColors: true,
    ...extra,
  });
  pendingLeafMats.push(m);
  return m;
}

/** Tile a flat-ground material's map by its instance's WORLD x/z size, so the
 *  grass/sand grain stays a constant real size on small verges and huge fields
 *  alike (the horizontal cousin of patchFacadeTiling).
 *
 *  `world` (GS7 — the Rio-flagged seam fix): anchor the grain in WORLD space
 *  instead of per-instance UV space. Per-instance anchoring restarts the
 *  pattern at every sheet's corner, so the 12u slab trains along beaches and
 *  verges showed a tread of seam lines from any high angle in every city.
 *  World-anchored UVs (the ground cousin of patchWaterTiling's world-space
 *  ripple UVs) make the grain CONTINUOUS across adjacent sheets — same real
 *  grain size (world · tile ≡ uv · instanceSize · tile), different anchor.
 *  Only the flat ground SHEETS opt in: roof caps keep per-instance anchoring
 *  (their seam courses should follow each roof's own yaw, and roofs never
 *  abut, so they have no seam problem to fix). */
function patchGroundTiling(mat: THREE.MeshStandardMaterial, tile: number, world = false): void {
  mat.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader.replace(
      '#include <uv_vertex>',
      world
        ? `#include <uv_vertex>
      #if defined( USE_INSTANCING ) && defined( USE_MAP )
        vMapUv = ( modelMatrix * instanceMatrix * vec4( position, 1.0 ) ).xz * ${tile.toFixed(3)};
      #endif`
        : `#include <uv_vertex>
      #if defined( USE_INSTANCING ) && defined( USE_MAP )
        vMapUv = uv * vec2(length(instanceMatrix[0].xyz), length(instanceMatrix[2].xyz)) * ${tile.toFixed(3)};
      #endif`,
    );
  };
}

// ---- Water (M37) — rivers/seas/lagoons/lakes. One shared procedural ripple
//      NORMAL map (tiling) breaks up a low-roughness surface so it reflects each
//      city's sky (IBL) with a moving shimmer + sun sparkle, instead of reading
//      as a flat coloured slab. The ripples SCROLL via a single shared time
//      uniform ticked once per frame (tickWater), and tile by world size so a
//      river and an ocean get the same real ripple scale. Per-instance colour
//      keeps every city's water (turquoise, murky lagoon, pink lake, ink bay). ----
const WATER_UNIFORMS = { uTime: { value: 0 } };
/** Advance every water surface's ripple animation. Called once per render frame
 *  by Game with the elapsed seconds (frozen by reduced-motion). */
export function tickWater(seconds: number): void {
  WATER_UNIFORMS.uTime.value = seconds;
}

let waterNormalTex: THREE.CanvasTexture | null = null;
function waterNormalTexture(): THREE.CanvasTexture {
  if (waterNormalTex) return waterNormalTex;
  const N = 128;
  const [c, x] = texCanvas(N);
  const img = x.createImageData(N, N);
  // A seamless height field from periodic octaves (integer cycles → tiles), then
  // finite-differenced into an RGB normal map.
  const h = (i: number, j: number): number => {
    const u = (i / N) * Math.PI * 2;
    const v = (j / N) * Math.PI * 2;
    return (
      Math.sin(u * 3 + Math.cos(v * 2) * 1.4) +
      0.5 * Math.sin(v * 5 + Math.cos(u * 3) * 1.2) +
      0.3 * Math.sin((u + v) * 4)
    );
  };
  const strength = 1.7;
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      const hl = h((i - 1 + N) % N, j);
      const hr = h((i + 1) % N, j);
      const hd = h(i, (j - 1 + N) % N);
      const hu = h(i, (j + 1) % N);
      let nx = (hl - hr) * strength;
      let ny = (hd - hu) * strength;
      const len = Math.hypot(nx, ny, 1);
      nx /= len;
      ny /= len;
      const o = (j * N + i) * 4;
      img.data[o] = (nx * 0.5 + 0.5) * 255;
      img.data[o + 1] = (ny * 0.5 + 0.5) * 255;
      img.data[o + 2] = (1 / len) * 0.5 * 255 + 128;
      img.data[o + 3] = 255;
    }
  }
  x.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.NoColorSpace; // a normal map is data, never sRGB
  t.userData.shared = true;
  waterNormalTex = t;
  return t;
}

/** WORLD-SPACE ripple UVs + time scroll (GS1). The merged shoreline meshes are
 *  built in world coordinates at the origin, so `position.xz` IS the world
 *  plane: one continuous ripple field across a whole bay — no per-slab anchor,
 *  no seams, no repeats. `aRipple` is a per-vertex density multiplier (small
 *  ponds run denser so they never read as one giant swirl; Patagonia broader).
 *  The fragment edit fades the ripple normal with VIEW DISTANCE — near-field
 *  ripples keep their loved sparkle while aerial/far water calms down instead
 *  of shimmering white noise (measured complaint in Tokyo/London highs). */
function patchWaterTiling(mat: THREE.MeshStandardMaterial): void {
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = WATER_UNIFORMS.uTime;
    shader.vertexShader =
      'uniform float uTime;\nattribute float aRipple;\n' +
      shader.vertexShader.replace(
        '#include <uv_vertex>',
        `#include <uv_vertex>
        #ifdef USE_NORMALMAP
          vNormalMapUv = position.xz * 0.07 * aRipple + vec2(uTime * 0.015, uTime * 0.009);
        #endif`,
      );
    // Ripple strength ≈ full inside ~20u, easing off with distance (a no-op
    // if the chunk ever renames the line — string replace just misses).
    shader.fragmentShader = shader.fragmentShader.replace(
      'mapN.xy *= normalScale;',
      'mapN.xy *= normalScale * min( 1.0, 1.16 / ( 1.0 + 0.008 * length( vViewPosition ) ) );',
    );
  };
}

/** The building box with its index REORDERED into two material groups:
 *  [4 wall faces | top+bottom]. GS6 — the shared unit box paints the facade
 *  on every face, so every tower TOP wore the lit-window texture (glowing
 *  windows across the rooftops, glaring from the NYC highline / Tokyo
 *  expressway shortcut decks that ride ABOVE them). Two groups = only 2 draw
 *  calls per city instead of 6 with the naïve per-face material array.
 *  BoxGeometry face order is [+x, −x, +y, −y, +z, −z], 6 indices each. */
let facadeBoxGeo: THREE.BufferGeometry | null = null;
function facadeBox(): THREE.BufferGeometry {
  if (facadeBoxGeo) return facadeBoxGeo;
  const geo = new THREE.BoxGeometry(1, 1, 1);
  const idx = geo.getIndex() as THREE.BufferAttribute;
  const a = Array.from(idx.array as ArrayLike<number>);
  const walls = [...a.slice(0, 12), ...a.slice(24, 36)]; // ±x, ±z
  const caps = a.slice(12, 24); // +y roof, −y underside
  geo.setIndex([...walls, ...caps]);
  geo.clearGroups();
  geo.addGroup(0, walls.length, 0); // material 0: the facade
  geo.addGroup(walls.length, caps.length, 1); // material 1: the roof cap
  facadeBoxGeo = geo;
  return geo;
}

/** Lit, flat-shaded box field (buildings). Two draw calls, per-instance
 *  colour. `style` (the city id) picks a procedural facade — a grayscale wall
 *  pattern that multiplies the per-instance palette colour + an aligned
 *  lit-window emissive map, so each city's generic buildings read as that
 *  city's architecture. Tops wear a separate DARK roof-cap material (GS6):
 *  felt/gravel courses via the M36 roofTexture, no emissive, world-tiled. */
export function buildBoxes(insts: BoxInst[], style = 'generic'): THREE.Object3D {
  // Mumbai-ts: the Fort district wears its own Bombay-Gothic facade. The
  // variant is keyed to the PALETTE — fort blocks draw from MUMBAI_GOTHIC,
  // every other Mumbai zone draws deco creams (see zones/mumbai.ts) — so the
  // zone builders, SceneryArrays and the rng sequence stay untouched: the
  // same instances land in the same places, split across two meshes.
  if (style === 'mumbai') {
    const gothic: BoxInst[] = [];
    const deco: BoxInst[] = [];
    for (const b of insts) (MUMBAI_GOTHIC.includes(b.color) ? gothic : deco).push(b);
    if (gothic.length && deco.length) {
      const g = new THREE.Group();
      g.add(facadeMesh(deco, 'mumbai'), facadeMesh(gothic, 'mumbaiGothic'));
      return g;
    }
    if (gothic.length) return facadeMesh(gothic, 'mumbaiGothic');
  }
  // Tokyo-ts: Asakusa's low machiya wear their own warm temple-town facade.
  // Same mechanism as Mumbai above — keyed to the PALETTE (only the asakusa
  // zone draws TOKYO_TIMBER; Shibuya/Minato/wharf palettes are disjoint,
  // test-pinned in tests/world/tokyoScenery.test.ts), so zone builders,
  // SceneryArrays and the rng sequence stay untouched.
  if (style === 'tokyo') {
    const machiya: BoxInst[] = [];
    const rest: BoxInst[] = [];
    for (const b of insts) (TOKYO_TIMBER.includes(b.color) ? machiya : rest).push(b);
    if (machiya.length && rest.length) {
      const g = new THREE.Group();
      g.add(facadeMesh(rest, 'tokyo'), facadeMesh(machiya, 'asakusa'));
      return g;
    }
    if (machiya.length) return facadeMesh(machiya, 'asakusa');
  }
  // london-ts: Westminster's civic STONE wears the Gothic-revival frontage
  // (pointed sashes in pinnacled bays) while The City's glass towers and the
  // far-bank skyline keep the plain ashlar. Same mechanism as the two above —
  // keyed to the PALETTE (only LONDON_STONE blocks go Gothic; GLASS/SKYLINE
  // are disjoint from it, test-pinned in tests/world/londonScenery.test.ts) —
  // so zone builders, SceneryArrays and the rng sequence stay untouched.
  if (style === 'london') {
    const stone: BoxInst[] = [];
    const rest: BoxInst[] = [];
    for (const b of insts) (LONDON_STONE.includes(b.color) ? stone : rest).push(b);
    if (stone.length && rest.length) {
      const g = new THREE.Group();
      g.add(facadeMesh(rest, 'london'), facadeMesh(stone, 'londonGothic'));
      return g;
    }
    if (stone.length) return facadeMesh(stone, 'londonGothic');
  }
  return facadeMesh(insts, style);
}

/** One instanced facade mesh in one style (the pre-split buildBoxes body). */
function facadeMesh(insts: BoxInst[], style: string): THREE.InstancedMesh {
  const f = cityFacade(style);
  const mat = new THREE.MeshStandardMaterial({
    flatShading: true,
    roughness: 1, // × the roughness map: matte wall, glossy window glass
    metalness: 0,
    map: f.map, // brick/stone/stucco/glass/timber pattern × the palette colour
    emissive: 0xffffff,
    emissiveMap: f.emissive,
    emissiveIntensity: 0.6,
    bumpMap: f.bump, // brick/plank relief + recessed windows for raking sun
    bumpScale: 0.85,
    roughnessMap: f.rough,
  });
  patchFacadeTiling(mat, shopFacade(style), STREET_GLOW[style]);
  // The roof cap: material colour is a DARK multiplier — instance colours are
  // bright facade palettes, so the product lands at asphalt-felt luminance
  // while still varying warm/cool per building.
  const roofMat = new THREE.MeshStandardMaterial({
    flatShading: true,
    color: 0x4a4e53,
    roughness: 0.96,
    metalness: 0,
    map: roofTexture(), // course lines read as roofing seams from above
  });
  patchGroundTiling(roofMat, 0.14); // tile the seams by world footprint
  const mesh = new THREE.InstancedMesh(facadeBox(), [mat, roofMat], insts.length);
  insts.forEach((b, i) => {
    P.set(b.x, b.y + b.h / 2, b.z);
    S.set(b.w, b.h, b.d);
    mesh.setMatrixAt(i, M.compose(P, yawQ(b.rotY), S));
    mesh.setColorAt(i, C.set(b.color));
  });
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  return mesh;
}

/**
 * Plain flat-shaded box field (no windows) — stoops, cornices, trims.
 *
 * shared-2b (patagonia flag): `tex` is an OPTIONAL surface canvas. The plain
 * box had no map at all, so anything a zone dressed with it (cabin chimneys,
 * kerbstones, planked stubs) read as a solid colour chip at any distance.
 * Omit it and the material is byte-identical to before, so every existing
 * call site (`a.plain`, `a.adBuildings`) is untouched — a zone opts in by
 * passing a kind. Grain tiles by WORLD footprint (patchGroundTiling), so a
 * 0.4u chimney and a 12u parapet wear the same real stone.
 */
/**
 * london-ts nit round: the plain-box colours that are real MASONRY and want the
 * shared stone grain. The Thames embankment parapet is the offender that forced
 * this — a continuous run of untextured 0xb9b095 boxes right along the finish
 * approach, which QA read as "untextured cream slabs" (the guardrail beside it
 * carries TrackMesh's wall painter, so the bare wall behind it looked like an
 * unfinished plinth).
 *
 * Keyed to the COLOUR, exactly like the facade variants in buildBoxes: the zone
 * builders, SceneryArrays and the rng sequence are untouched, the same
 * instances land in the same places, split across two meshes (+1 draw call, and
 * only for cities that use these colours). Deliberately a TINY explicit set —
 * London's other plain boxes (the near-black door leaves, the cream string
 * courses, the brick chimney stacks) must NOT get masonry coursing, and no
 * other city may be captured by accident.
 */
export const LONDON_MASONRY: readonly number[] = [
  0xb9b095, // embankment parapet body
  0xaaa287, // its piers
  0x9c9479, // its plinth course
];

/** Plain-box assembly with the per-city masonry split (see LONDON_MASONRY). */
export function buildPlain(insts: BoxInst[], style = 'generic'): THREE.Object3D {
  if (style === 'london') {
    const stone: BoxInst[] = [];
    const rest: BoxInst[] = [];
    for (const b of insts) (LONDON_MASONRY.includes(b.color) ? stone : rest).push(b);
    if (stone.length && rest.length) {
      const g = new THREE.Group();
      g.add(buildPlainBoxes(rest), buildPlainBoxes(stone, 'stone'));
      return g;
    }
    if (stone.length) return buildPlainBoxes(stone, 'stone');
  }
  return buildPlainBoxes(insts);
}

export function buildPlainBoxes(
  insts: BoxInst[],
  tex?: PropTexKind,
  glow?: number,
): THREE.InstancedMesh {
  const mat = new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.9, metalness: 0 });
  if (tex) {
    mat.map = propTexture(tex);
    mat.bumpMap = propTexture(tex);
    mat.bumpScale = tex === 'plank' ? 0.5 : 0.7;
    patchInstanceTiling(mat, tex === 'plank' ? 0.5 : 0.34);
  }
  if (glow !== undefined) patchStreetGlow(mat, glow);
  const mesh = new THREE.InstancedMesh(UNIT_BOX, mat, insts.length);
  insts.forEach((b, i) => {
    P.set(b.x, b.y + b.h / 2, b.z);
    S.set(b.w, b.h, b.d);
    mesh.setMatrixAt(i, M.compose(P, yawQ(b.rotY), S));
    mesh.setColorAt(i, C.set(b.color));
  });
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  return mesh;
}

/** M158c: the warm street light on the lower storeys of a plain instanced
 *  box (Tokyo's dark Shibuya towers), fading out by ~10 m. Chains any earlier
 *  onBeforeCompile on the material. */
function patchStreetGlow(mat: THREE.MeshStandardMaterial, glow: number): void {
  const prev = mat.onBeforeCompile;
  mat.onBeforeCompile = (shader, renderer) => {
    prev.call(mat, shader, renderer);
    shader.uniforms.streetGlow = { value: new THREE.Color(glow) };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying float vGlowH;')
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        #ifdef USE_INSTANCING
          vGlowH = (position.y + 0.5) * length(instanceMatrix[1].xyz);
        #else
          vGlowH = 100.0;
        #endif`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform vec3 streetGlow;\nvarying float vGlowH;')
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
        totalEmissiveRadiance += streetGlow * max( diffuseColor.rgb, vec3( 0.16 ) ) * ( 1.0 - smoothstep( 0.0, 10.0, vGlowH ) );`,
      );
  };
}

let roofTex: THREE.CanvasTexture | null = null;
/**
 * Horizontal course lines with a shadowed underlap — multiplied by the roof's
 * own colour it reads as terracotta tiles, slates or corrugated sheet.
 *
 * shared-2b (patagonia flag: "cabin roofs read flat at distance"). The old
 * course was a nearly FLAT value band (200-222) with one gray(150) hairline
 * under it — a 82-step range that a distant roof's mip chain averages away to
 * a plain tint. Each course now carries a real cross-course RAMP (bright just
 * under the sheet above, darkening into its own underlap) between a hot lip
 * and a deep shadow: a 126-step range, so the corrugation survives distance
 * and mip filtering. The course MEAN is held at ~204 (was ~206), so no city's
 * roofs get darker — only their relief gets legible (Mumbai/Napoli/Rio
 * control shots verified they don't over-sharpen).
 */
function roofTexture(): THREE.CanvasTexture {
  if (roofTex) return roofTex;
  _texSeed = 0x400f;
  const [c, x] = texCanvas(64);
  x.fillStyle = gray(210);
  x.fillRect(0, 0, 64, 64);
  for (let y = 0; y < 64; y += 8) {
    // Per-course value jitter, then the sheet's own light ramp: rows y+1..y+6
    // fall from 240 (catching the sky right under the lip above) to 184 where
    // the next sheet laps over it.
    const j = ((texRand() * 22) | 0) - 11;
    for (let k = 1; k <= 6; k++) {
      x.fillStyle = gray(Math.max(0, Math.min(255, 240 - ((240 - 184) * (k - 1)) / 5 + j)));
      x.fillRect(0, y + k, 64, 1);
    }
    x.fillStyle = gray(118); // deep shadow where the course above overlaps
    x.fillRect(0, y + 7, 64, 1);
    x.fillStyle = gray(244);
    x.fillRect(0, y, 64, 1); // sun-catch on the lip
    // Vertical seams between individual tiles/sheets (staggered per course).
    x.globalAlpha = 0.45;
    x.fillStyle = gray(140);
    const off = ((y / 8) % 2) * 5;
    for (let sx = off; sx < 64; sx += 10) x.fillRect(sx, y, 1, 7);
    x.globalAlpha = 1;
  }
  roofTex = shareTex(new THREE.CanvasTexture(c));
  return roofTex;
}

// ---- shared-2b: prop surface canvases (stone / plank) ----------------------
// Grayscale like every other shared map here, so the per-instance colour still
// decides the hue: one plank canvas dresses Lagos's stilt huts AND its
// pirogues (same village timber), one stone canvas is available to any zone
// that wants masonry on a plain box.
export type PropTexKind = 'stone' | 'plank';
const propTexCache = new Map<PropTexKind, THREE.CanvasTexture>();
function propTexture(kind: PropTexKind): THREE.CanvasTexture {
  const hit = propTexCache.get(kind);
  if (hit) return hit;
  _texSeed = kind === 'stone' ? 0x5701 : 0x91a1;
  const [c, x] = texCanvas(64);
  if (kind === 'stone') {
    x.fillStyle = gray(150); // mortar
    x.fillRect(0, 0, 64, 64);
    for (let r = 0; r < 8; r++) {
      const off = (r % 2) * 8;
      for (let bx = -16; bx < 64; bx += 16) {
        x.fillStyle = gray(184 + ((texRand() * 46) | 0));
        x.fillRect(bx + off + 1, r * 8 + 1, 14, 6);
      }
    }
    // Weathering blotches so a big face never reads as a repeated stamp.
    x.globalAlpha = 0.12;
    for (let i = 0; i < 22; i++) {
      x.fillStyle = gray(texRand() < 0.5 ? 120 : 236);
      x.beginPath();
      x.arc(texRand() * 64, texRand() * 64, 3 + texRand() * 8, 0, Math.PI * 2);
      x.fill();
    }
  } else {
    x.fillStyle = gray(120); // the dark gap between boards
    x.fillRect(0, 0, 64, 64);
    for (let b = 0; b < 8; b++) {
      const v = 176 + ((texRand() * 54) | 0);
      x.fillStyle = gray(v);
      x.fillRect(0, b * 8, 64, 7);
      // Lengthwise grain + the odd knot.
      x.globalAlpha = 0.4;
      for (let g = 0; g < 5; g++) {
        x.strokeStyle = gray(Math.max(0, v - 30 - ((texRand() * 34) | 0)));
        x.lineWidth = 1;
        const gy = b * 8 + 1 + texRand() * 5;
        x.beginPath();
        x.moveTo(0, gy);
        x.bezierCurveTo(20, gy + 0.8, 44, gy - 0.8, 64, gy);
        x.stroke();
      }
      x.globalAlpha = 0.55;
      if (texRand() < 0.5) {
        x.fillStyle = gray(Math.max(0, v - 62));
        x.beginPath();
        x.ellipse(texRand() * 64, b * 8 + 3.5, 1.6, 1.1, 0, 0, Math.PI * 2);
        x.fill();
      }
      x.globalAlpha = 1;
    }
  }
  x.globalAlpha = 1;
  const t = shareTex(new THREE.CanvasTexture(c));
  propTexCache.set(kind, t);
  return t;
}

/** Tile the roof courses by world size: around the pyramid (u) and up its
 *  slope (v) — a big temple roof and a cabin get the same real course height. */
function patchRoofTiling(mat: THREE.MeshStandardMaterial): void {
  mat.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader.replace(
      '#include <uv_vertex>',
      `#include <uv_vertex>
      #if defined( USE_INSTANCING ) && defined( USE_MAP )
        vMapUv = uv * vec2(
          max(length(instanceMatrix[0].xyz), length(instanceMatrix[2].xyz)) * 0.8,
          length(instanceMatrix[1].xyz) * 0.55
        );
      #endif`,
    );
  };
}

/** Pyramid roofs (4-sided cones) dropped onto cabins / temples. y = roof BASE. */
export function buildRoofs(insts: BoxInst[]): THREE.InstancedMesh {
  const geo = new THREE.CylinderGeometry(0, Math.SQRT1_2, 1, 4); // unit pyramid, corners at ±0.5
  const mat = new THREE.MeshStandardMaterial({
    flatShading: true,
    roughness: 0.85,
    map: roofTexture(),
  });
  patchRoofTiling(mat);
  const mesh = new THREE.InstancedMesh(geo, mat, insts.length);
  insts.forEach((b, i) => {
    E.set(0, b.rotY + Math.PI / 4, 0); // rotate the square base to align with the box under it
    Q.setFromEuler(E);
    P.set(b.x, b.y + b.h / 2, b.z);
    S.set(b.w, b.h, b.d);
    mesh.setMatrixAt(i, M.compose(P, Q, S));
    mesh.setColorAt(i, C.set(b.color));
  });
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  return mesh;
}

/** Unlit full-bright neon panels (Times Square billboards). */
export function buildNeon(insts: NeonInst[]): THREE.InstancedMesh {
  const mat = new THREE.MeshBasicMaterial({ toneMapped: false });
  const mesh = new THREE.InstancedMesh(UNIT_BOX, mat, insts.length);
  insts.forEach((b, i) => {
    P.set(b.x, b.y, b.z);
    S.set(0.6, b.h, b.w); // thin panel facing across the street
    mesh.setMatrixAt(i, M.compose(P, yawQ(b.rotY), S));
    mesh.setColorAt(i, C.set(b.color));
  });
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  return mesh;
}

/** Flat ground patches (grass / water / sand). thinY = thickness, lift = y offset.
 *  `tex` adds a procedural grain map (grass blades / sand grain) that multiplies
 *  the per-instance colour and tiles by world size — the big greenery upgrade. */
export function buildFlat(
  insts: FlatInst[],
  lift: number,
  roughness: number,
  thinY: number,
  tex?: 'grass' | 'sand',
): THREE.InstancedMesh {
  const mat = new THREE.MeshStandardMaterial({ roughness, metalness: roughness < 0.5 ? 0.3 : 0 });
  if (tex) {
    mat.map = groundTexture(tex);
    // nyc-lake: the grain map alone still renders as a PLATE — a flat sheet
    // lit by one normal has no self-shading whatever its albedo, which is half
    // of why the park apron read as a slab. The same canvas as a bump map
    // costs nothing (shared texture, same UV patch) and gives the turf/sand
    // relief under the low dusk sun.
    mat.bumpMap = groundTexture(tex);
    mat.bumpScale = tex === 'grass' ? 0.5 : 0.35;
    // World-anchored grain (GS7): continuous across the 12u slab trains.
    patchGroundTiling(mat, tex === 'grass' ? 0.1 : 0.075, true);
  }
  const mesh = new THREE.InstancedMesh(UNIT_BOX, mat, insts.length);
  insts.forEach((f, i) => {
    P.set(f.x, f.y + lift, f.z);
    S.set(f.w, thinY, f.d);
    mesh.setMatrixAt(i, M.compose(P, yawQ(f.rotY), S));
    mesh.setColorAt(i, C.set(f.color));
  });
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  return mesh;
}

/**
 * GS7 (mumbai-nits): FAR-FIELD TERRAIN CLAMP — kill the level-disagreement
 * walls. buildRunGrid eases each row's far water UP toward the run's median
 * level (capped at grade + 4), so where a looping course encloses a bay at
 * two very different grades (Mumbai's Sea Link S-bend: 3.7 → 14 across one
 * run), the two limbs' far sheets overlap 2-6 units apart — the upper
 * sheet's edge + skirt hang over the lower one as a dark wall (the
 * re-review's "long dark diagonal streak" + "half-submerged paneled slab",
 * both raycast-verified as stacked water at 14.06 over 11.96 over an 11.5
 * local grade). Fix at assembly (this module owns the track handle): far
 * columns clamp DOWN to the terrain actually under them (track.project) +
 * lift + 0.63, so overlapping sheets converge to the same local level and
 * a fold's drop spreads across a 40-75u column span (≤ ~2°) instead of a
 * vertical quad. min() only — shore bands, tapers and everything that
 * already rides at/below local grade is untouched, and the cap sits 1.13
 * above any flat apron top (grade − 0.5), so the old "green strips knifing
 * through" class cannot come back. Skirts re-derive from the clamped edge
 * (same formula: min(edge − 3.5, terrain − 2.5)).
 */
function clampFarField(g: WaterGrid, track: WaterTrackLike): WaterGrid {
  const runCols = 3 + WATER.bandCols.length + WATER.farCols.length + 1;
  if (g.cols !== runCols) return g; // ponds never sweep far — leave them be
  const far0 = 3 + WATER.bandCols.length;
  const farN = WATER.farCols.length;
  for (let r = 0; r < g.rows; r++) {
    let edgeY = 0;
    for (let c = far0; c < far0 + farN; c++) {
      const i = (r * g.cols + c) * 3;
      const pr = track.project(g.pos[i], g.pos[i + 2]);
      const cap = pr.y + WATER.lift + 0.63;
      if (g.pos[i + 1] > cap) g.pos[i + 1] = cap;
      edgeY = g.pos[i + 1];
    }
    const si = (r * g.cols + (g.cols - 1)) * 3;
    const prS = track.project(g.pos[si], g.pos[si + 2]);
    g.pos[si + 1] = Math.min(g.pos[si + 1], edgeY - WATER.farSkirtDrop, prS.y - 2.5);
  }
  return g;
}

/** Water (GS1 rebuild) — every water body in the city as ONE merged
 *  shoreline-following mesh: welded ribbon runs (seas/rivers/lagoons/lakes
 *  swept along the course with land skirts, far fields and end tapers) plus
 *  organic disc ponds with wet banks. All geometry planning is PURE in
 *  scenery/water.ts; this assembles the lattices into a single indexed
 *  BufferGeometry (one draw call — same as the old InstancedMesh) with the
 *  same loved ripple material on world-anchored UVs. The surface sits at
 *  ground + 0.13, exactly the old slabs' top, so boats/icebergs/stilt houses
 *  still sit right. */
/**
 * nyc-lake: a standalone sheet of the SAME rippled water the welded pass and
 * the disc ponds use — shared normal map, shared scrolling `uTime`, shared
 * world-anchored UVs — for water that is not part of a body plan at all. The
 * Bow Bridge FORD is the one caller: it lies on the road, so it is a course
 * FEATURE (physics in trackFeatures.ts), but it has to read as the same lake
 * it is continuous with on both banks, and re-rolling a bespoke material for
 * it is exactly how two water surfaces end up looking like two lakes.
 */
export function courseWaterMaterial(tune?: WaterTune): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({
    roughness: tune?.roughness ?? 0.14,
    metalness: 0,
    side: THREE.DoubleSide,
  });
  mat.normalMap = waterNormalTexture();
  const ns = tune?.normalScale ?? 0.55;
  mat.normalScale = new THREE.Vector2(ns, ns);
  patchWaterTiling(mat);
  return mat;
}

export function buildWater(
  a: Pick<SceneryArrays, 'ponds' | 'hills'>,
  track: WaterTrackLike,
  city: { water?: WaterTune },
): THREE.Mesh | null {
  const cfg = resolveWaterCfg(city.water);
  const { runs, ponds } = splitWater(a.ponds);
  const grids: WaterGrid[] = [];
  runs.forEach((run, i) => grids.push(clampFarField(buildRunGrid(run, track, cfg, i), track)));
  for (const plan of planPonds(ponds, city.water?.lakes ?? [], track, a.hills, cfg)) {
    grids.push(buildPondGrid(plan));
  }
  if (!grids.length) return null;

  let vtx = 0;
  let tri = 0;
  for (const g of grids) {
    vtx += g.rows * g.cols;
    tri += (g.rows - 1) * (g.cols - 1) * 2;
  }
  const positions = new Float32Array(vtx * 3);
  const colors = new Float32Array(vtx * 3);
  const ripple = new Float32Array(vtx);
  const index = vtx > 65535 ? new Uint32Array(tri * 3) : new Uint16Array(tri * 3);

  let vo = 0;
  let io = 0;
  for (const g of grids) {
    const count = g.rows * g.cols;
    positions.set(g.pos, vo * 3);
    for (let v = 0; v < count; v++) {
      C.set(g.color[v]);
      colors[(vo + v) * 3] = C.r;
      colors[(vo + v) * 3 + 1] = C.g;
      colors[(vo + v) * 3 + 2] = C.b;
      ripple[vo + v] = g.ripple;
    }
    // Orientation-aware triangulation: keep the surface normal UP whichever
    // way the lattice happens to wind (side −1 runs + pond rings wind the
    // other way; DoubleSide + faceDirection then light both correctly).
    const mid = Math.max(0, Math.floor(g.rows / 2) - 1) * g.cols + Math.floor(g.cols / 2);
    const flip = gridCellNormalY(g, mid) < 0;
    for (let r = 0; r < g.rows - 1; r++) {
      for (let c = 0; c < g.cols - 1; c++) {
        const a0 = vo + r * g.cols + c;
        const a1 = a0 + 1;
        const b0 = a0 + g.cols;
        const b1 = b0 + 1;
        if (flip) {
          index[io++] = a0;
          index[io++] = a1;
          index[io++] = b0;
          index[io++] = a1;
          index[io++] = b1;
          index[io++] = b0;
        } else {
          index[io++] = a0;
          index[io++] = b0;
          index[io++] = a1;
          index[io++] = a1;
          index[io++] = b0;
          index[io++] = b1;
        }
      }
    }
    vo += count;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.setAttribute('aRipple', new THREE.BufferAttribute(ripple, 1));
  geo.setIndex(new THREE.BufferAttribute(index, 1));
  geo.computeVertexNormals(); // skirt folds shade away naturally (dark banks)

  const tune = city.water;
  const mat = new THREE.MeshStandardMaterial({
    roughness: tune?.roughness ?? 0.14,
    metalness: 0,
    vertexColors: true,
    side: THREE.DoubleSide, // skirt folds must read from BOTH banks
  });
  mat.normalMap = waterNormalTexture();
  const ns = tune?.normalScale ?? 0.55;
  mat.normalScale = new THREE.Vector2(ns, ns);
  // ⚠ shared-2b: THIS LINE IS INERT, and so is every other `envMapIntensity`
  // set on an in-scene material in this repo. Tokyo QA measured 0 vs 1.6 vs 20
  // on the bay at ZERO pixels of difference; the cause is in three.js itself —
  // WebGLRenderer.js (r184, ~line 2685):
  //
  //   if ( ( material.isMeshStandardMaterial || … ) && material.envMap === null
  //        && scene.environment !== null )
  //     m_uniforms.envMapIntensity.value = scene.environmentIntensity;
  //
  // …which runs EVERY DRAW, and WebGLMaterials only copies the material's own
  // value `if ( material.envMap )`. Nothing in src/ ever assigns
  // `material.envMap` — the game's IBL is `scene.environment` (Game.ts) — so
  // the renderer overwrites this with `scene.environmentIntensity` (the city's
  // `env.intensity`, else tuning.render.envMapIntensity) unconditionally.
  // NOT fixed here on purpose: the honest fixes are to drop the knob from
  // WaterTune (a cities.ts change — NYC 2.0 and Patagonia 0.85 use it) or to
  // plumb the environment texture in as a real `mat.envMap`, and that CHANGES
  // how those two cities' water looks today. To retune water reflectivity now,
  // the live knob is `roughness` (which does respond, measured).
  mat.envMapIntensity = tune?.envMapIntensity ?? 1.6; // (see above — inert)
  patchWaterTiling(mat);
  return new THREE.Mesh(geo, mat);
}

/** Approximate a grid cell's face-normal Y (for orientation-aware winding). */
function gridCellNormalY(g: WaterGrid, i: number): number {
  const j = i + 1;
  const k = i + g.cols;
  const ax = g.pos[j * 3] - g.pos[i * 3];
  const az = g.pos[j * 3 + 2] - g.pos[i * 3 + 2];
  const bx = g.pos[k * 3] - g.pos[i * 3];
  const bz = g.pos[k * 3 + 2] - g.pos[i * 3 + 2];
  // ((k−i) × (j−i)).y with y-terms dropped (the surface is near-horizontal).
  return bz * ax - bx * az;
}

// Trunks: 8 radial segments (was 5 — a five-sided trunk reads as a pencil at
// kerb distance and the pentagon is unmissable once real bark lands on it) and
// UVs scaled so the shared bark tiles at a believable real size.
const TRUNK_GEO = new THREE.CylinderGeometry(0.32, 0.45, 2.2, 8);
scaleUv(TRUNK_GEO, 2, 1.4);
/** The broadleaf canopy: a card cluster on the SAME 1.55 sphere the old
 *  IcosahedronGeometry(1.55, 0) used, so every consumer's `r / 1.55` scaling
 *  and the whole canopy envelope are unchanged. */
const FOLIAGE_GEO = leafClusterGeo(1.55, BROADLEAF_CARDS);
bakeYGradient(FOLIAGE_GEO, 0.72, 1.16); // sunlit top, shaded underside

/**
 * The canopy blob layout (london-ts) — PURE so the envelope below can be
 * test-pinned. Returns, in units of the instance scale `s`: the blob's radial
 * offset from the trunk, its radius, and its vertical squash.
 *
 * WHY IT CHANGED. M36 built the canopy from `treeBlobs` overlapping balls, but
 * the satellites were offset only 0.55-1.25 while the main blob's radius was
 * 2.25 — so each satellite sat ~85% BURIED inside the main ball, and with no
 * vertical squash the result rendered as one perfect sphere. That is the
 * "gumdrop" QA saw in London beside the M98 lean trees (whose 'park' species
 * spreads its blobs dx −0.9..+2.4 and squashes them sy ~0.8, which is exactly
 * why those read as canopies). Same failure was called "lollipop blob trees"
 * in Napoli — it is the shared builder, not a per-city choice.
 *
 * THE ENVELOPE IS THE CONTRACT. Positions (trunks) are untouched and this must
 * never grow the canopy: `a.trees` are placed as close as hw+2 with no
 * clearance model of their own (unlike leanTrees' minCanopyClearance), and
 * finishClear assumes a 4·s canopy radius. So the blobs are REDISTRIBUTED
 * inside the old silhouette — max reach 3.31·s vs the old 3.34·s, and every
 * blob's underside sits HIGHER than before (the squash lifts it) — never
 * widened. tests/world/londonScenery.test.ts pins both bounds.
 */
export function treeCanopyBlob(b: number, h1: number): { off: number; r: number; sy: number } {
  if (b === 0) return { off: 0, r: 1.25 * 1.55, sy: 0.84 }; // the crown, squashed
  // Sized against the lean-tree 'park' species, the canopy QA praised: its
  // lobes stand 24-48% of the crown's radius proud of the crown's silhouette.
  // These land at 24-72% while still finishing inside the M36 envelope.
  return { off: 1.45 + h1 * 0.45, r: (0.62 + h1 * 0.3) * 1.55, sy: 0.8 };
}

/** The widest a canopy blob may reach from its trunk, in units of `s` — the
 *  M36 layout's own envelope, which treeCanopyBlob must stay inside. */
export const TREE_CANOPY_REACH = 3.34;

/** Low-poly trees (M36): trunks + a fuller, irregular canopy built from a few
 *  overlapping foliage blobs per tree, each given its own rotation, size and
 *  colour jitter (all derived from the index, so trunk POSITIONS are unchanged).
 *  A baked top-down gradient + a soft leaf map give the canopy texture/volume. */
export function buildTrees(insts: TreeInst[]): THREE.InstancedMesh[] {
  const blobs = Math.max(1, Math.min(4, Math.round(tuning.scenery.treeBlobs)));
  const trunkMat = trunkMaterial(0x5a3f2a);
  // baked canopy gradient × per-instance leaf colour × the photo atlas
  const foliageMat = leafMaterial(foliageAtlas());
  const trunks = new THREE.InstancedMesh(TRUNK_GEO, trunkMat, insts.length);
  const foliage = new THREE.InstancedMesh(FOLIAGE_GEO, foliageMat, insts.length * blobs);
  insts.forEach((t, i) => {
    const s = t.s;
    P.set(t.x, t.y + 1.1 * s, t.z);
    S.set(s, s, s);
    trunks.setMatrixAt(i, M.compose(P, yawQ(hash(i) * Math.PI * 2), S));
    const cy = t.y + 3.4 * s; // canopy centre (unchanged from the old single ball)
    for (let b = 0; b < blobs; b++) {
      const h1 = hash(i * 4 + b + 1);
      const ang = hash(i * 4 + b + 37) * Math.PI * 2;
      // Satellites now clear the crown's own radius, so the lobes actually
      // show; the squash turns each ball into a broadleaf mass (see the
      // treeCanopyBlob header — same envelope, different distribution).
      const shape = treeCanopyBlob(b, h1);
      const off = shape.off * s;
      const bx = t.x + Math.cos(ang) * off;
      const bz = t.z + Math.sin(ang) * off;
      const by = cy + (b === 0 ? 0 : (h1 - 0.4) * 1.3 * s);
      const bs = (shape.r * s) / 1.55; // FOLIAGE_GEO radius is 1.55
      P.set(bx, by, bz);
      S.set(bs, bs * shape.sy, bs);
      foliage.setMatrixAt(i * blobs + b, M.compose(P, yawQ(hash(i * 9 + b) * Math.PI * 2), S));
      foliage.setColorAt(i * blobs + b, C.set(t.color).multiplyScalar(0.9 + hash(i * 5 + b) * 0.2));
    }
  });
  trunks.instanceMatrix.needsUpdate = true;
  foliage.instanceMatrix.needsUpdate = true;
  if (foliage.instanceColor) foliage.instanceColor.needsUpdate = true;
  return [trunks, foliage];
}

/**
 * GS7 (mumbai-nits): the shared palm-crown FROND is a real leaf now, not a
 * plank. The old crown geometry was a flat 0.95×0.1×4.4 BoxGeometry, so every
 * palm in the game (the standing kerb palms AND the M98 lean palms — both
 * builders share this constant) wore an asterisk of green boards, worst in
 * Mumbai's dense Marine Drive rows (the re-review's "old flat-rectangle-frond
 * species" — there is no second species; the shared frond itself was the
 * fossil). Same footprint as the box (z −2.2..+2.2 around the crown pivot,
 * max width 0.95) so the crown silhouette and the leanTrees clearance/reach
 * model stay honest: a narrow tail over the crown centre → broad mid-blade →
 * pointed tip, ARCHED along its length (the droop lives in the geometry, so
 * the fan pitch stays shallow) and creased down the midrib so the two halves
 * catch light differently.
 */
function palmFrondGeo(): THREE.BufferGeometry {
  const ROWS = 9;
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  for (let r = 0; r < ROWS; r++) {
    const t = r / (ROWS - 1);
    const z = -2.2 + 4.4 * t;
    // Width: tail ~0.56 wide over the centre, widest (0.95) just past the
    // pivot, collapsing to a point at the tip.
    const w = 0.95 * Math.pow(Math.sin(Math.PI * (0.18 + 0.82 * t)), 0.75);
    // Arch: flat petiole to ~30% length, then a rising-then-sagging curve —
    // the tip hangs 0.52 under the blade axis (this + the shallow fan pitch
    // keeps the crown's real dip inside the old fan's ~1.3·s envelope).
    const u2 = Math.max(0, (t - 0.3) / 0.7);
    const arch = 0.2 * u2 - 0.72 * u2 * u2;
    const crease = 0.16 * (w / 0.95); // edges fold down off the midrib
    pos.push(-w / 2, arch - crease, z, 0, arch, z, w / 2, arch - crease, z);
    // TREE ROUND: v runs 0..1 along the blade (was 0..2.2, tiling the old noise
    // map). The frond PHOTO is a single leaf with its rachis up the image
    // centre, so u 0..1 across puts the rachis on the midrib column and v maps
    // the leaflets along the blade exactly once. Positions are untouched — the
    // crown reach/dip contract (leanTrees' LEAN_FROND) is geometry, not UVs.
    uv.push(0, t, 0.5, t, 1, t);
  }
  for (let r = 0; r < ROWS - 1; r++) {
    for (let c = 0; c < 2; c++) {
      const a0 = r * 3 + c;
      const b0 = a0 + 3;
      idx.push(a0, b0, a0 + 1, a0 + 1, b0, b0 + 1);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(uv), 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}
const PALM_FROND = palmFrondGeo();
/** Frond fan euler — 'YXZ' (yaw first, then pitch about the frond's OWN
 *  lateral axis) so every blade droops outward along its own azimuth. The old
 *  fan fed the shared 'XYZ' scratch euler, which pitched about world-ish X:
 *  half the blades tipped UP, half DOWN, and the crown read as a jumble of
 *  boards. Dedicated scratch object — never change the shared E's order. */
const FROND_E = new THREE.Euler(0, 0, 0, 'YXZ');

/**
 * Standing kerb palms, rebuilt on the M98 lean-palm anatomy (mumbai-ts round):
 * a CURVED three-segment trunk bowing gently in the palm's own facing
 * direction + the 7-frond textured crown seated on the integrated tip. The
 * old build was a single straight cylinder with 8 fronds — at kerb distance
 * (Marine Drive's most-seen prop) it read a generation older than the lean
 * palms. Same inputs, same instancing pattern, all variety from index
 * hashes — layout positions are byte-identical, only geometry changed.
 * (Trunk segments reuse LEAN_SEG_GEO, declared with the M98 block below —
 * module init finishes before any builder runs, so the forward ref is safe.)
 */
export function buildPalms(insts: PlaceInst[]): THREE.InstancedMesh[] {
  const trunkMat = trunkMaterial(0xa8845a);
  // The frond geometry finally carries a real frond PHOTO (alpha-cut leaflets
  // with gaps between them) instead of grey mottle on a solid blade.
  const frondMat = leafMaterial(palmFrondTexture(), {
    color: 0x3aa050,
    vertexColors: false, // PALM_FROND carries no baked colour attribute
  });
  const segs = new THREE.InstancedMesh(LEAN_SEG_GEO, trunkMat, insts.length * 3);
  const fronds = new THREE.InstancedMesh(PALM_FROND, frondMat, insts.length * 7);
  insts.forEach((t, i) => {
    const s = t.s ?? 1;
    // Bow along the palm's own yaw (rotY is already a random placement draw),
    // magnitude from the index hash — the old straight build's total height
    // (~7·s, crown at 6.9·s) is preserved by the segment length.
    const bow = 0.06 + hash(i * 3 + 1) * 0.14;
    const segLen = (7 / 3) * s;
    const dirX = -Math.cos(t.rotY); // the M98 lean-palm frame with side = +1
    const dirZ = Math.sin(t.rotY);
    let px = t.x;
    let py = t.y;
    let pz = t.z;
    [0.55, 1.0, 1.45].forEach((k, j) => {
      const ang = bow * k; // segments bend progressively (the M98 curve)
      E.set(0, t.rotY, ang);
      Q.setFromEuler(E);
      segs.setMatrixAt(i * 3 + j, M.compose(P.set(px, py, pz), Q, S.set(s * 1.2, segLen, s * 1.2)));
      px += dirX * Math.sin(ang) * segLen;
      py += Math.cos(ang) * segLen;
      pz += dirZ * Math.sin(ang) * segLen;
    });
    // Crown: the shared 7-frond fan, yaw-offset per palm so no two crowns
    // align, with per-frond droop + length jitter. GS7: shallow TRUE-radial
    // pitch (FROND_E, 'YXZ') over the arched leaf geometry — the deep pitch
    // belonged to the flat plank era, and the old 'XYZ' euler tipped half the
    // fan upward besides. Real envelope stays inside the old fan's (reach
    // ≤ ~3.9·s, dip ≤ ~1.3·s under the trunk tip — the leanTrees model).
    for (let f = 0; f < 7; f++) {
      const yaw = hash(i * 7 + 3) * Math.PI * 2 + (f / 7) * Math.PI * 2;
      FROND_E.set(0.16 + hash(i * 11 + f) * 0.12, yaw, 0); // droop outward, varied
      Q.setFromEuler(FROND_E);
      P.set(px + Math.sin(yaw) * 1.7 * s, py - 0.1 * s, pz + Math.cos(yaw) * 1.7 * s);
      fronds.setMatrixAt(i * 7 + f, M.compose(P, Q, S.set(s, s, s * (0.86 + hash(i * 13 + f) * 0.3))));
    }
  });
  segs.instanceMatrix.needsUpdate = true;
  fronds.instanceMatrix.needsUpdate = true;
  return [segs, fronds];
}

const PINE_TRUNK = new THREE.CylinderGeometry(0.22, 0.34, 5.5, 8);
scaleUv(PINE_TRUNK, 2, 3);
/** Umbrella (stone pine) crowns are squashed WIDE by the instance scale, so
 *  the near-flat top card becomes the parasol — needles, not broadleaves. */
const PINE_CROWN = leafClusterGeo(
  1,
  BROADLEAF_CARDS.map((c) => ({ ...c, tile: TILE_NEEDLE })),
);
const CONE_CROWN = needleConeGeo();
bakeYGradient(PINE_CROWN, 0.74, 1.14);
bakeYGradient(CONE_CROWN, 0.7, 1.18);

/** 'umbrella': tall bare trunk + a wide flat crown (Mediterranean stone pine).
 *  'conifer': short trunk + two stacked cones (southern beech / pine). M36 adds
 *  the shared leaf texture + baked gradient + a little per-instance spin. */
export function buildPines(
  insts: PlaceInst[],
  style: 'umbrella' | 'conifer',
): THREE.InstancedMesh[] {
  const trunkMat = trunkMaterial(0x6d4f30);
  const crownMat = leafMaterial(foliageAtlas());
  const trunks = new THREE.InstancedMesh(PINE_TRUNK, trunkMat, insts.length);
  const crowns = new THREE.InstancedMesh(
    style === 'umbrella' ? PINE_CROWN : CONE_CROWN,
    crownMat,
    insts.length * (style === 'conifer' ? 2 : 1),
  );
  insts.forEach((t, i) => {
    const s = t.s ?? 1;
    const spin = yawQ(hash(i) * Math.PI * 2);
    P.set(t.x, t.y + 2.6 * s, t.z);
    S.set(s, s, s);
    trunks.setMatrixAt(i, M.compose(P, spin, S));
    if (style === 'umbrella') {
      P.set(t.x, t.y + 5.8 * s, t.z);
      S.set(3.4 * s, 1.3 * s, 3.4 * s); // squashed wide = the umbrella canopy
      crowns.setMatrixAt(i, M.compose(P, spin, S));
      crowns.setColorAt(i, C.set(t.color ?? 0x3a7a40).multiplyScalar(0.92 + hash(i * 5) * 0.16));
    } else {
      P.set(t.x, t.y + 4.6 * s, t.z);
      S.set(2.4 * s, 3.4 * s, 2.4 * s);
      crowns.setMatrixAt(i * 2, M.compose(P, spin, S));
      crowns.setColorAt(i * 2, C.set(t.color ?? 0x2c5a3c).multiplyScalar(0.9 + hash(i * 5) * 0.2));
      P.set(t.x, t.y + 6.8 * s, t.z);
      S.set(1.7 * s, 2.6 * s, 1.7 * s);
      crowns.setMatrixAt(i * 2 + 1, M.compose(P, spin, S));
      crowns.setColorAt(
        i * 2 + 1,
        C.set(t.color ?? 0x2c5a3c).multiplyScalar(1.0 + hash(i * 7) * 0.14),
      );
    }
  });
  trunks.instanceMatrix.needsUpdate = true;
  crowns.instanceMatrix.needsUpdate = true;
  if (crowns.instanceColor) crowns.instanceColor.needsUpdate = true;
  return [trunks, crowns];
}

const BAOBAB_TRUNK = new THREE.CylinderGeometry(1.0, 1.7, 7, 10);
scaleUv(BAOBAB_TRUNK, 4, 2.5);
/** The Sahel crown wears the DRY/sparse tile — a baobab's canopy is thin. */
const BAOBAB_CROWN = leafClusterGeo(1, DRY_CARDS);
bakeYGradient(BAOBAB_CROWN, 0.74, 1.14);

/** Every canopy geometry with its CONTRACTED circumradius — exported so the
 *  envelope test can prove the card clusters never reach further than the
 *  solid balls/cones they replaced (finishClear's 4·s canopy radius,
 *  TREE_CANOPY_REACH and the lean-tree clearance model all depend on it). */
export const CANOPY_GEOS: readonly {
  name: string;
  geo: THREE.BufferGeometry;
  /** Max allowed |vertex| — the sphere the replaced solid's vertices sat on. */
  radius: number;
  /** Max allowed horizontal reach (the number every clearance rule uses). */
  hRadius: number;
}[] = [
  // The three clusters put EVERY vertex exactly on their sphere, so radius and
  // horizontal reach are the same number the icosahedra had.
  { name: 'foliage', geo: FOLIAGE_GEO, radius: 1.55, hRadius: 1.55 },
  { name: 'pineCrown', geo: PINE_CROWN, radius: 1, hRadius: 1 },
  { name: 'baobabCrown', geo: BAOBAB_CROWN, radius: 1, hRadius: 1 },
  // The cone cluster stands on the unit cone's own profile: radius 1 at the
  // base (y −0.5), narrowing to the apex — exactly CylinderGeometry(0,1,1,6).
  { name: 'coneCrown', geo: CONE_CROWN, radius: Math.hypot(1, 0.5), hRadius: 1 },
];

/** Baobabs — fat bottle trunks with a sparse flat crown. Sahel giants. M36 adds
 *  the shared leaf texture + canopy gradient + per-instance crown rotation. */
export function buildBaobabs(insts: PlaceInst[]): THREE.InstancedMesh[] {
  const trunkMat = trunkMaterial(0x9a7a58);
  const crownMat = leafMaterial(foliageAtlas(), { color: 0x55683a });
  const trunks = new THREE.InstancedMesh(BAOBAB_TRUNK, trunkMat, insts.length);
  const crowns = new THREE.InstancedMesh(BAOBAB_CROWN, crownMat, insts.length);
  insts.forEach((t, i) => {
    const s = t.s ?? 1;
    P.set(t.x, t.y + 3.5 * s, t.z);
    S.set(s, s, s);
    trunks.setMatrixAt(i, M.compose(P, yawQ(t.rotY), S));
    P.set(t.x, t.y + 7.6 * s, t.z);
    S.set(4.6 * s, 1.5 * s, 4.6 * s);
    crowns.setMatrixAt(i, M.compose(P, yawQ(t.rotY + hash(i)), S));
  });
  trunks.instanceMatrix.needsUpdate = true;
  crowns.instanceMatrix.needsUpdate = true;
  return [trunks, crowns];
}

// ---- Per-city building facades v2 (procedural — zero download, cached per
//      style, shared across every building in a city). Four aligned 128px maps:
//      `map`      grayscale wall pattern × the per-instance palette colour, with
//                 framed windows (recess shading, sky-gradient glass, sills,
//                 lintels, grime streaks);
//      `emissive` lights a subset of those same windows (warm two-tone glow);
//      `bump`     height relief — bricks/planks proud, windows recessed, sills
//                 out — so the sun rakes across facades;
//      `rough`    matte wall vs GLOSSY window glass, so unlit windows glint
//                 with the city's sky (IBL) reflection.
//      All tile by world size (patchFacadeTiling) so features stay a constant
//      real size on tall and short buildings alike. ----
const F = 128; // facade LOGICAL size (px); one tile ≈ 7.7 world units at 0.13 tiling
/** M158: facades paint at FACADE_SCALE× their logical size (the painters keep
 *  their 128-unit coordinates; the canvas is pre-scaled), so windows, sashes
 *  and bricks stay crisp at the road's grazing angles. Phones take half, and
 *  less anisotropic filtering. */
const COARSE_POINTER = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
const FACADE_SCALE = COARSE_POINTER ? 2 : 4;
const FACADE_ANISO = COARSE_POINTER ? 4 : 8;
interface FacadeCfg {
  cols: number;
  rows: number;
  /** Draws the wall into the colour map AND its relief into the bump map. */
  wall: (map: CanvasRenderingContext2D, bump: CanvasRenderingContext2D) => void;
  win: 'tall' | 'square' | 'wide';
  lit: number; // fraction of windows glowing
  litCols: string[];
  glass: string; // unlit window glass (dark) on the map
  frame: string; // window surround on the map
  seed: number;
  mullion?: boolean; // divided sashes (a cross bar in each window)
  bands?: boolean; // floor bands (string courses) at each row boundary
  arch?: boolean; // pointed-arch window heads (Bombay Gothic, mumbai-ts)
  shoji?: boolean; // kumiko lattice over each opening (Asakusa machiya, tokyo-ts)
  ac?: boolean; // M158: window air conditioners under some sills
}

function c2d(w = F, h = F, scale = FACADE_SCALE): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w * scale;
  c.height = h * scale;
  const x = c.getContext('2d');
  if (!x) throw new Error('Scenery: no 2D context for facade');
  x.scale(scale, scale); // painters draw in logical units
  return [c, x];
}
const gray = (v: number): string => `rgb(${v},${v},${v})`;

// Wall painters — each draws the COLOUR pattern (map) and matching RELIEF (bump,
// white = proud, dark = recessed) so mortar lines and grooves shade correctly.
/** One running-bond course pattern at REAL scale (M158: the old bricks were
 *  ~1 m long and the ashlar ~2.4 m, so walls read as toy blocks). Unit
 *  colours are kept per (row, column mod cols) so the tile wraps seamlessly. */
function bond(
  mx: CanvasRenderingContext2D,
  bx2: CanvasRenderingContext2D,
  rows: number,
  cols: number,
  joint: number,
  tone: (r: number, c: number) => number,
  bevel: number,
): void {
  const rh = F / rows;
  const cw = F / cols;
  const shade: number[][] = [];
  const relief: number[][] = [];
  for (let r = 0; r < rows; r++) {
    shade.push([]);
    relief.push([]);
    for (let c = 0; c < cols; c++) {
      shade[r].push(tone(r, c));
      relief[r].push(196 + ((texRand() * 34) | 0));
    }
  }
  for (let r = 0; r < rows; r++) {
    const off = (r % 2) * (cw / 2);
    for (let c = -1; c <= cols; c++) {
      const k = ((c % cols) + cols) % cols;
      const x = c * cw + off + joint / 2;
      const y = r * rh + joint / 2;
      const w = cw - joint;
      const h = rh - joint;
      mx.fillStyle = gray(shade[r][k]);
      mx.fillRect(x, y, w, h);
      if (bevel > 0) {
        mx.fillStyle = 'rgba(255,255,255,0.14)'; // the arris catching the sun
        mx.fillRect(x, y, w, bevel);
        mx.fillStyle = 'rgba(0,0,0,0.16)';
        mx.fillRect(x, y + h - bevel, w, bevel);
      }
      bx2.fillStyle = gray(relief[r][k]);
      bx2.fillRect(x, y, w, h);
    }
  }
}

function wallBrick(mx: CanvasRenderingContext2D, bx2: CanvasRenderingContext2D): void {
  _texSeed = 0xb51c;
  mx.fillStyle = gray(150);
  mx.fillRect(0, 0, F, F); // mortar
  bx2.fillStyle = gray(110);
  bx2.fillRect(0, 0, F, F); // mortar sits back
  // ~22 x 7 cm bricks: 72 courses x 28 bricks a tile, the odd dark header
  bond(mx, bx2, 72, 28, 0.34, () => 194 + ((texRand() * 40) | 0) - (texRand() < 0.1 ? 44 : 0), 0);
}
function wallAshlar(mx: CanvasRenderingContext2D, bx2: CanvasRenderingContext2D): void {
  _texSeed = 0xa54a;
  mx.fillStyle = gray(174);
  mx.fillRect(0, 0, F, F); // joints
  bx2.fillStyle = gray(120);
  bx2.fillRect(0, 0, F, F);
  // ~1 m x 0.5 m dressed stones: 16 courses x 8 a tile
  bond(mx, bx2, 16, 8, 0.5, () => 214 + ((texRand() * 20) | 0), 0.5);
}
function wallRender(
  base: number,
): (mx: CanvasRenderingContext2D, bx2: CanvasRenderingContext2D) => void {
  return (mx, bx2) => {
    _texSeed = 0x5e4d + base;
    mx.fillStyle = gray(base);
    mx.fillRect(0, 0, F, F);
    // Big soft plaster blotches (sun-baked patchiness), then fine speckle.
    for (let i = 0; i < 22; i++) {
      mx.globalAlpha = 0.07;
      mx.fillStyle = gray(base - 26 + ((texRand() * 44) | 0));
      mx.beginPath();
      mx.arc(texRand() * F, texRand() * F, 10 + texRand() * 26, 0, Math.PI * 2);
      mx.fill();
    }
    mx.globalAlpha = 0.16;
    for (let i = 0; i < 240; i++) {
      mx.fillStyle = gray(base - 16 + ((texRand() * 30) | 0));
      mx.fillRect(texRand() * F, texRand() * F, 2, 2);
    }
    // Faint weather streaks running down the render.
    mx.globalAlpha = 0.05;
    for (let i = 0; i < 14; i++) {
      mx.fillStyle = gray(base - 30);
      mx.fillRect(texRand() * F, 0, 1 + texRand() * 2, F);
    }
    mx.globalAlpha = 1;
    bx2.fillStyle = gray(200); // plaster is near-smooth
    bx2.fillRect(0, 0, F, F);
    bx2.globalAlpha = 0.12;
    for (let i = 0; i < 120; i++) {
      bx2.fillStyle = gray(160 + ((texRand() * 80) | 0));
      bx2.fillRect(texRand() * F, texRand() * F, 2, 2);
    }
    bx2.globalAlpha = 1;
  };
}
function wallPanels(mx: CanvasRenderingContext2D, bx2: CanvasRenderingContext2D): void {
  _texSeed = 0x70a1;
  mx.fillStyle = gray(66);
  mx.fillRect(0, 0, F, F);
  bx2.fillStyle = gray(200);
  bx2.fillRect(0, 0, F, F);
  // Per-panel value variance (vision glass vs spandrel), then the mullion grid.
  for (let py = 0; py < F; py += 16) {
    for (let px = 0; px < F; px += 16) {
      mx.fillStyle = gray(58 + ((texRand() * 22) | 0));
      mx.fillRect(px, py, 16, 16);
    }
  }
  for (let i = 0; i <= F; i += 16) {
    mx.fillStyle = gray(44);
    mx.fillRect(i, 0, 2, F);
    mx.fillRect(0, i, F, 2);
    mx.fillStyle = gray(92); // thin catch-light on each mullion
    mx.fillRect(i + 2, 0, 1, F);
    mx.fillRect(0, i + 2, F, 1);
    bx2.fillStyle = gray(130); // grid sits back a hair
    bx2.fillRect(i, 0, 2, F);
    bx2.fillRect(0, i, F, 2);
  }
}
function wallBoards(mx: CanvasRenderingContext2D, bx2: CanvasRenderingContext2D): void {
  _texSeed = 0xb0a2;
  mx.fillStyle = gray(196);
  mx.fillRect(0, 0, F, F);
  bx2.fillStyle = gray(120);
  bx2.fillRect(0, 0, F, F);
  for (let bp = 0; bp < F; bp += 12) {
    const v = 188 + ((texRand() * 26) | 0);
    mx.fillStyle = gray(v);
    mx.fillRect(bp + 1, 0, 10, F);
    bx2.fillStyle = gray(200 + ((texRand() * 20) | 0));
    bx2.fillRect(bp + 1, 0, 10, F);
    mx.fillStyle = gray(142);
    mx.fillRect(bp, 0, 1, F); // groove between boards
    // Wood grain: wavy vertical strokes + the odd knot.
    mx.globalAlpha = 0.18;
    mx.strokeStyle = gray(v - 34);
    mx.lineWidth = 1;
    for (let g = 0; g < 3; g++) {
      const gx0 = bp + 2 + texRand() * 8;
      mx.beginPath();
      mx.moveTo(gx0, 0);
      mx.bezierCurveTo(gx0 + 2, F * 0.33, gx0 - 2, F * 0.66, gx0 + 1, F);
      mx.stroke();
    }
    if (texRand() < 0.4) {
      mx.globalAlpha = 0.5;
      mx.fillStyle = gray(v - 52);
      mx.beginPath();
      mx.arc(bp + 3 + texRand() * 6, texRand() * F, 1.6, 0, Math.PI * 2);
      mx.fill();
    }
    mx.globalAlpha = 1;
  }
}

/** Bombay-Gothic wall (mumbai-ts): dark basalt block courses under two strong
 *  near-white string courses — the polychrome basalt-and-cream banding that
 *  makes the Fort district read. Texels are grayscale multiplied by the
 *  per-instance colour: the MUMBAI_GOTHIC palette is mid-dark, so the ~74
 *  field lands as dark basalt while the 238 bands read cream against it. */
function wallBasaltBands(mx: CanvasRenderingContext2D, bx2: CanvasRenderingContext2D): void {
  _texSeed = 0x60b1;
  // MEASURED (mumbai-ts first shot round): texels here MULTIPLY the mid-dark
  // MUMBAI_GOTHIC instance colours in linear space — a 70-gray field crushed
  // the whole Fort district to near-black. The field sits at ~145 so the
  // product lands as READABLE dark basalt while the 238 courses still pop.
  mx.fillStyle = gray(118);
  mx.fillRect(0, 0, F, F); // joints
  bx2.fillStyle = gray(190);
  bx2.fillRect(0, 0, F, F);
  // Staggered basalt block courses with per-block tone jitter.
  for (let y = 0; y < F; y += 16) {
    const off = ((y / 16) % 2) * 16;
    for (let x = -32; x < F; x += 32) {
      mx.fillStyle = gray(140 + ((texRand() * 22) | 0));
      mx.fillRect(x + off + 1, y + 1, 30, 14);
      bx2.fillStyle = gray(185 + ((texRand() * 20) | 0));
      bx2.fillRect(x + off + 1, y + 1, 30, 14);
    }
  }
  // The two cream string courses (sited in the sill zones of the 3-row grid).
  for (const y of [F * 0.31, F * 0.645]) {
    mx.fillStyle = gray(238);
    mx.fillRect(0, y, F, 5);
    mx.fillStyle = gray(132);
    mx.fillRect(0, y + 5, F, 1.5); // shadow under the lip
    bx2.fillStyle = gray(248);
    bx2.fillRect(0, y, F, 5); // course stands proud for the raking sun
  }
}

/**
 * Westminster Gothic-revival wall (london-ts): fine Portland ashlar articulated
 * into BAYS by vertical buttress ribs, each capped with a little gabled
 * pinnacle at every storey line — the Palace of Westminster's riverfront
 * language, so the civic blocks flanking the Elizabeth Tower belong to it
 * instead of standing beside it as plain boxes.
 *
 * The texture TILES by world size (patchFacadeTiling, one tile ≈ 7.7 units), so
 * the pinnacles must be part of the REPEAT — they cap each rib at the row
 * boundaries rather than at the building top, which is how a Gothic bay reads
 * at racing distance anyway. Texels multiply LONDON_STONE's warm creams, so the
 * field sits high (~196, finer courses than wallAshlar's 218 blocks) and the
 * ribs carry the contrast: proud in the bump map with a shaded reveal beside
 * them, which is what makes the bays legible when the overcast sun rakes them.
 */
function wallGothicStone(mx: CanvasRenderingContext2D, bx2: CanvasRenderingContext2D): void {
  _texSeed = 0xc0de;
  mx.fillStyle = gray(168);
  mx.fillRect(0, 0, F, F); // joints
  bx2.fillStyle = gray(122);
  bx2.fillRect(0, 0, F, F);
  // Fine ashlar courses — smaller stones than the plain london ashlar, so the
  // wall reads as carved masonry rather than big cast blocks.
  for (let r = 0; r < 12; r++) {
    const off = (r % 2) * 15;
    for (let bp = -30; bp < F; bp += 30) {
      const v = 190 + ((texRand() * 18) | 0);
      mx.fillStyle = gray(v);
      mx.fillRect(bp + off + 1, r * 11 + 1, 28, 9);
      mx.fillStyle = gray(v + 10); // sun-catching top arris
      mx.fillRect(bp + off + 1, r * 11 + 1, 28, 1.5);
      bx2.fillStyle = gray(202 + ((texRand() * 18) | 0));
      bx2.fillRect(bp + off + 1, r * 11 + 1, 28, 9);
    }
  }
  // Buttress ribs on the bay seams (the 4-column window grid), each with a
  // shaded reveal on one side so it reads three-dimensional at distance.
  const rib = (x: number, w: number): void => {
    mx.fillStyle = gray(214);
    mx.fillRect(x, 0, w, F);
    mx.fillStyle = gray(140); // the reveal shadow beside the rib
    mx.fillRect(x + w, 0, 2, F);
    bx2.fillStyle = gray(252);
    bx2.fillRect(x, 0, w, F);
    bx2.fillStyle = gray(70);
    bx2.fillRect(x + w, 0, 2, F);
    // Gabled pinnacle cap at each storey line (4 rows), pointing up. The path
    // is traced into BOTH contexts — canvas paths are per-context, so a single
    // beginPath on the colour map would leave the bump map flat here.
    for (let r = 0; r <= 4; r++) {
      const y = (r * F) / 4;
      for (const [ctx, tone] of [
        [mx, 232],
        [bx2, 255],
      ] as [CanvasRenderingContext2D, number][]) {
        ctx.fillStyle = gray(tone);
        ctx.beginPath();
        ctx.moveTo(x - 1.5, y + 4);
        ctx.lineTo(x + w / 2, y - 4);
        ctx.lineTo(x + w + 1.5, y + 4);
        ctx.closePath();
        ctx.fill();
      }
    }
  };
  for (let c = 0; c < 4; c++) rib(((c + 1) * F) / 4 - 3, 5);
  rib(-2, 5); // the corner buttress, so a bay never runs off the tile edge
}

/** Asakusa machiya wall (tokyo-ts): plaster-and-board infill inside a dark
 *  timber post-and-beam frame. Texels MULTIPLY the dark TOKYO_TIMBER palette
 *  (the wallBasaltBands lesson): the infill field sits high (~206) so the
 *  product lands as the palette's own warm dark wood, while the ~62 frame
 *  strokes read as near-black structural timber against it. */
function wallMachiya(mx: CanvasRenderingContext2D, bx2: CanvasRenderingContext2D): void {
  _texSeed = 0x7a5a;
  mx.fillStyle = gray(206);
  mx.fillRect(0, 0, F, F);
  bx2.fillStyle = gray(150);
  bx2.fillRect(0, 0, F, F);
  // Plaster mottle + faint board grain in the infill panels.
  mx.globalAlpha = 0.09;
  for (let i = 0; i < 70; i++) {
    mx.fillStyle = gray(168 + ((texRand() * 54) | 0));
    mx.fillRect(texRand() * F, texRand() * F, 3 + texRand() * 7, 2 + texRand() * 5);
  }
  mx.globalAlpha = 1;
  // Dark timber rails at the storey lines (aligned to the 2-row window grid)
  // and posts on the tile seams — proud of the plaster so the sun rakes them.
  const rail = (y: number, t: number): void => {
    mx.fillStyle = gray(62);
    mx.fillRect(0, y, F, t);
    mx.fillStyle = gray(44);
    mx.fillRect(0, y + t - 1, F, 1); // shadow under the lip
    bx2.fillStyle = gray(238);
    bx2.fillRect(0, y, F, t);
  };
  const post = (x: number, t: number, v = 62): void => {
    mx.fillStyle = gray(v);
    mx.fillRect(x, 0, t, F);
    bx2.fillStyle = gray(232);
    bx2.fillRect(x, 0, t, F);
  };
  rail(0, 5);
  rail(F * 0.5 - 2, 4);
  rail(F - 5, 5);
  // Posts on the 3-column window seams, so every opening sits in its own bay.
  post(0, 4);
  post(F / 3 - 2, 3);
  post((2 * F) / 3 - 2, 3);
  post(F - 4, 4);
}

const FACADES: Record<string, FacadeCfg> = {
  nyc: {
    cols: 3,
    rows: 4,
    wall: wallBrick,
    win: 'tall',
    lit: 0.5,
    litCols: ['#ffd9a0', '#ffcf86', '#aec4ff'],
    glass: '#2a2f3a',
    frame: '#d0cabb',
    seed: 1,
    mullion: true,
    bands: true,
    ac: true,
  },
  london: {
    cols: 3,
    rows: 4,
    wall: wallAshlar,
    win: 'tall',
    lit: 0.34,
    litCols: ['#ffe6b0', '#dfe8ff'],
    glass: '#3a4150',
    frame: '#efe9df',
    seed: 2,
    mullion: true,
    bands: true,
  },
  napoli: {
    cols: 3,
    rows: 3,
    wall: wallRender(236),
    win: 'tall',
    lit: 0.42,
    litCols: ['#ffdca0', '#ffe6c0'],
    glass: '#3a4252',
    frame: '#7a9a64',
    seed: 3,
    mullion: true,
    bands: true,
  },
  tokyo: {
    cols: 5,
    rows: 6,
    wall: wallPanels,
    win: 'square',
    lit: 0.62,
    litCols: ['#ffd27a', '#7af0ff', '#ff7ad0', '#fff0a0'],
    glass: '#10151f',
    frame: '#1c2330',
    seed: 4,
  },
  rio: {
    cols: 3,
    rows: 4,
    wall: wallRender(238),
    win: 'tall',
    lit: 0.38,
    litCols: ['#ffe2b0', '#cfe8ff'],
    // shared-pass: was '#3a4658' — the navy that read as a flat black VOID on
    // every Lapa terrace. The mechanism is the INSTANCE MULTIPLY, not the
    // colour on its own: facadeMesh multiplies this map by the building's
    // palette colour, and RIO_COLONIAL is saturated pastel — a terracotta
    // 0xe86a4a crushes a blue-biased cell's green and blue channels
    // (70,86,108 -> 64,36,31, about a THIRD of the wall's luminance beside
    // it), so the pane goes black while the wall stays bright. The fix is
    // therefore two moves: LIFT the base (88 -> 118 luminance, half the
    // wall's 238 render instead of a third) and DE-BLUE it (a 38-point r/b
    // spread down to 22) so a warm wall colour can no longer selectively
    // crush it. Rio is the only style that ever used this value, and the
    // style key is the CITY ID (buildBoxes(a.buildings, city.id)), so no
    // other city can be touched by this line. It also lands the painted cells
    // in the same luminance band as the Lapa terraces' 3D joinery panes
    // (zones/rio.ts windowUnit, 0x46566c FLAT — unmultiplied, hence the
    // deliberately higher number here for the same read).
    glass: '#6e7684',
    frame: '#e8e2d2',
    seed: 5,
    mullion: true,
    bands: true,
    ac: true,
  },
  dakar: {
    cols: 3,
    rows: 3,
    wall: wallRender(224),
    win: 'square',
    lit: 0.28,
    litCols: ['#ffe0a0'],
    glass: '#4a4030',
    frame: '#d8c8a8',
    seed: 6,
  },
  lagos: {
    cols: 4,
    rows: 4,
    wall: wallRender(228),
    win: 'square',
    lit: 0.44,
    litCols: ['#ffe0a0', '#bfe0ff'],
    glass: '#33384a',
    frame: '#e0d8c4',
    seed: 7,
    bands: true,
    ac: true,
  },
  mumbai: {
    cols: 4,
    rows: 4,
    wall: wallRender(214),
    win: 'square',
    lit: 0.38,
    litCols: ['#ffdc96', '#cfe2ff'],
    glass: '#3a3a44',
    frame: '#c8bca0',
    seed: 8,
    bands: true,
    ac: true,
  },
  // Asakusa machiya (tokyo-ts): warm two-tone temple-town fronts — a dark
  // timber frame around FEWER, LARGER shoji openings glowing amber paper
  // (only warm litCols: the Shibuya→Asakusa mood shift lives here — the
  // shared 'tokyo' facade is 30 tiny squares in electric pink/cyan).
  // Selected per INSTANCE by palette in buildBoxes (TOKYO_TIMBER), the
  // mumbaiGothic precedent — never by zone plumbing.
  asakusa: {
    cols: 3,
    rows: 2,
    wall: wallMachiya,
    win: 'wide',
    lit: 0.42,
    litCols: ['#ffcf8e', '#ffdca8', '#ffc172'],
    glass: '#241c12',
    frame: '#b39b78',
    seed: 23,
    shoji: true,
  },
  // Westminster's Gothic-revival civic stone (london-ts): tall POINTED-ARCH
  // sashes, small and warm, in bays framed by pinnacled buttress ribs — the
  // Palace's own riverfront language, so the blocks flanking the Elizabeth
  // Tower read as its neighbours rather than as bare boxes beside it. Fewer
  // lit windows than the plain `london` style (0.22 vs 0.34): a Gothic civic
  // frontage at dusk shows a handful of warm rooms, not an office grid.
  // Selected per INSTANCE by palette in buildBoxes (LONDON_STONE — disjoint
  // from The City's GLASS towers and the far-bank SKYLINE, test-pinned), the
  // mumbaiGothic precedent — never by zone plumbing.
  // MEASURED TRAP (caught in this round's facade closeup): `lit` is NOT a
  // probability — the lit test is the deterministic lattice
  // ((col·7 + row·13 + seed) % 100)/100 < lit, so for a given (cols, rows) a
  // badly chosen seed can light ZERO windows however high `lit` is. Seed 24
  // did exactly that here (all 16 cells landed in 0.24-0.84); 54 lands 4 of
  // 16 in the warm band, which is the "handful of occupied rooms" read a
  // Gothic civic frontage wants. facadeLitCount() + its test pin this for
  // every style so the next variant can't ship dark.
  londonGothic: {
    cols: 4,
    rows: 4,
    wall: wallGothicStone,
    win: 'tall',
    lit: 0.22,
    litCols: ['#ffdca8', '#ffe8c4'],
    glass: '#31384a',
    frame: '#efe6d2',
    seed: 54,
    mullion: true,
    bands: true,
    arch: true,
  },
  // The Fort district's Victorian-Gothic civic blocks (mumbai-ts): pointed-arch
  // windows in square cream-stone surrounds over basalt-and-cream banding.
  // Selected per INSTANCE by palette in buildBoxes — never by zone plumbing.
  mumbaiGothic: {
    cols: 3,
    rows: 3,
    wall: wallBasaltBands,
    win: 'tall',
    lit: 0.3,
    litCols: ['#ffdc96', '#ffe8c0'],
    glass: '#2c2a30',
    frame: '#efe4c8',
    seed: 18,
    mullion: true,
    bands: true,
    arch: true,
  },
  patagonia: {
    cols: 2,
    rows: 3,
    wall: wallBoards,
    win: 'wide',
    lit: 0.4,
    litCols: ['#ffe0a0', '#ffd080'],
    glass: '#3a4048',
    frame: '#d8e4ec',
    seed: 9,
    mullion: true,
  },
  generic: {
    cols: 4,
    rows: 4,
    wall: wallRender(228),
    win: 'square',
    lit: 0.4,
    litCols: ['#ffd9a0', '#aec4ff'],
    glass: '#2a2f3a',
    frame: '#cfcabb',
    seed: 0,
  },
};

/** How many of a facade style's window cells actually light up. PURE (no
 *  canvas), and it mirrors cityFacade's own lit test exactly — the point is
 *  that `lit` is a THRESHOLD on a fixed lattice, not a probability, so a style
 *  can silently ship with every window dark (see the londonGothic note). Every
 *  style is pinned above zero in tests/world/londonScenery.test.ts. */
export function facadeLitCount(style: string): { lit: number; cells: number } {
  const cfg = FACADES[style] ?? FACADES.generic;
  let lit = 0;
  for (let r = 0; r < cfg.rows; r++) {
    for (let col = 0; col < cfg.cols; col++) {
      if (((col * 7 + r * 13 + cfg.seed) % 100) / 100 < cfg.lit) lit++;
    }
  }
  return { lit, cells: cfg.rows * cfg.cols };
}

/** Every facade style key (for exhaustive tests). */
export const FACADE_STYLES = Object.keys(FACADES);

interface FacadeMaps {
  map: THREE.CanvasTexture;
  emissive: THREE.CanvasTexture;
  bump: THREE.CanvasTexture;
  rough: THREE.CanvasTexture;
}
const facadeCache = new Map<string, FacadeMaps>();

const WALL_ROUGH = 224; // roughness map green channel: wall ≈ 0.88 → matte
const GLASS_ROUGH = 60; // window glass ≈ 0.24 → glossy, catches the sky

/** M158: curtain fabrics seen through the glass (map colours, then × the
 *  building tint like everything else on the facade). */
const CURTAINS = ['#efe7d6', '#d9c7a4', '#b98a6a', '#9aa58a', '#e6dcd0', '#c4b4a0'];

/** What hangs behind an unlit window, painted inside its clipped opening:
 *  dress < 0.34 plain glass, < 0.6 curtains, < 0.8 a roller blind, else
 *  venetian slats; `drawn` = how far a blind comes down. */
function paintDressing(
  x: CanvasRenderingContext2D,
  dress: number,
  drawn: number,
  fabric: string,
  drape: number,
  gx: number,
  gy: number,
  gw: number,
  gh: number,
): void {
  if (dress < 0.34) {
    // a dim room: the far wall and a floor line, barely there
    x.fillStyle = 'rgba(255,240,220,0.06)';
    x.fillRect(gx, gy + gh * 0.55, gw, gh * 0.45);
    return;
  }
  if (dress < 0.6) {
    x.fillStyle = fabric;
    x.fillRect(gx, gy, drape, gh);
    x.fillRect(gx + gw - drape, gy, drape, gh);
    x.fillStyle = 'rgba(0,0,0,0.16)'; // the folds
    for (let f = 0.9; f < drape; f += 1.3) {
      x.fillRect(gx + f, gy, 0.35, gh);
      x.fillRect(gx + gw - f, gy, 0.35, gh);
    }
    return;
  }
  if (dress < 0.8) {
    x.fillStyle = fabric;
    x.fillRect(gx, gy, gw, gh * drawn);
    x.fillStyle = 'rgba(0,0,0,0.3)'; // the bottom bar
    x.fillRect(gx, gy + gh * drawn - 0.7, gw, 0.7);
    return;
  }
  for (let y = gy; y < gy + gh * drawn; y += 1.05) {
    x.fillStyle = 'rgba(235,230,220,0.55)';
    x.fillRect(gx, y, gw, 0.6);
  }
}

/** The same dressing in the emissive map of a lit window: curtains and the
 *  drawn part of a blind dim the glow (the lamp shows through fabric). */
function litDressing(
  x: CanvasRenderingContext2D,
  dress: number,
  drawn: number,
  drape: number,
  gx: number,
  gy: number,
  gw: number,
  gh: number,
): void {
  // the lamp: a brighter pool high in the room
  const lamp = x.createRadialGradient(gx + gw / 2, gy + gh * 0.28, 0, gx + gw / 2, gy + gh * 0.28, gw * 0.6);
  lamp.addColorStop(0, 'rgba(255,250,235,0.35)');
  lamp.addColorStop(1, 'rgba(255,250,235,0)');
  x.fillStyle = lamp;
  x.fillRect(gx, gy, gw, gh);
  if (dress < 0.34) return;
  x.fillStyle = 'rgba(0,0,0,0.5)';
  if (dress < 0.6) {
    x.fillRect(gx, gy, drape, gh);
    x.fillRect(gx + gw - drape, gy, drape, gh);
  } else if (dress < 0.8) {
    x.fillRect(gx, gy, gw, gh * drawn);
  } else {
    for (let y = gy; y < gy + gh * drawn; y += 1.05) x.fillRect(gx, y + 0.6, gw, 0.45);
  }
}

/** Fine weathering over a wall at the new resolution: grain finer than any
 *  painter drew, and soft rain streaks running down from the top. */
function weather(x: CanvasRenderingContext2D, seed: number): void {
  _texSeed = 0x7ea + seed * 31;
  for (let i = 0; i < 1400; i++) {
    x.fillStyle = texRand() < 0.5 ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)';
    x.fillRect(texRand() * F, texRand() * F, 0.35 + texRand() * 0.5, 0.35 + texRand() * 0.5);
  }
  for (let i = 0; i < 22; i++) {
    const sx = texRand() * F;
    const len = F * (0.2 + texRand() * 0.7);
    const sy = texRand() * F;
    const st = x.createLinearGradient(0, sy, 0, sy + len);
    st.addColorStop(0, 'rgba(30,28,26,0.07)');
    st.addColorStop(1, 'rgba(30,28,26,0)');
    x.fillStyle = st;
    x.fillRect(sx, sy, 0.6 + texRand() * 1.6, len);
  }
}

function cityFacade(style: string): FacadeMaps {
  const hit = facadeCache.get(style);
  if (hit) return hit;
  const cfg = FACADES[style] ?? FACADES.generic;
  const [mc, mx] = c2d(); // colour
  const [bc, bx2] = c2d(); // bump (height)
  const [rc, rx] = c2d(); // roughness
  cfg.wall(mx, bx2); // grayscale wall + relief, multiplied by per-instance colour
  weather(mx, cfg.seed); // M158: fine grain + rain streaks at the new resolution
  rx.fillStyle = gray(WALL_ROUGH);
  rx.fillRect(0, 0, F, F);
  const [ec, ex] = c2d(); // emissive (lit windows)
  ex.fillStyle = '#05070c';
  ex.fillRect(0, 0, F, F);
  const cw = F / cfg.cols;
  const ch = F / cfg.rows;
  const mxp = cfg.win === 'tall' ? 0.29 : cfg.win === 'wide' ? 0.13 : 0.24;
  const myp = cfg.win === 'tall' ? 0.13 : cfg.win === 'wide' ? 0.29 : 0.2;
  // Floor bands (string courses) between rows anchor the storeys.
  if (cfg.bands) {
    for (let r = 1; r < cfg.rows; r++) {
      mx.globalAlpha = 0.35;
      mx.fillStyle = cfg.frame;
      mx.fillRect(0, r * ch - 1, F, 2);
      mx.globalAlpha = 1;
      bx2.fillStyle = gray(235);
      bx2.fillRect(0, r * ch - 1, F, 2);
    }
  }
  _texSeed = 0xfa0 + cfg.seed;
  // Openings in masonry wear a header (lintel) and a bevelled stone surround;
  // curtain walls (Tokyo's panels) and shoji fronts keep their own language.
  const masonry = cfg.wall !== wallPanels && !cfg.shoji;
  for (let r = 0; r < cfg.rows; r++) {
    for (let col = 0; col < cfg.cols; col++) {
      const gx = col * cw + cw * mxp;
      const gy = r * ch + ch * myp;
      const gw = cw * (1 - 2 * mxp);
      const gh = ch * (1 - 2 * myp);
      // Pointed-arch head (Bombay Gothic): the glass opening keeps its square
      // stone surround but rises into a point — the frame-coloured corners
      // left above the shoulders read as carved spandrels. ah = 0 traces the
      // exact rectangle every existing style always drew.
      const ah = cfg.arch ? Math.min(gh * 0.34, gw * 0.62) : 0;
      const shape = (x2d: CanvasRenderingContext2D): void => {
        x2d.beginPath();
        x2d.moveTo(gx, gy + gh);
        x2d.lineTo(gx, gy + ah);
        if (ah > 0) x2d.lineTo(gx + gw / 2, gy);
        x2d.lineTo(gx + gw, gy + ah);
        x2d.lineTo(gx + gw, gy + gh);
        x2d.closePath();
      };
      const trace = (x2d: CanvasRenderingContext2D): void => {
        shape(x2d);
        x2d.fill();
      };
      const lit = ((col * 7 + r * 13 + cfg.seed) % 100) / 100 < cfg.lit;
      // M158: what hangs behind the glass (plain, curtains, a roller blind,
      // venetian slats) and how far the blind is drawn.
      const dress = texRand();
      const drawn = 0.22 + texRand() * 0.5;
      const fabric = CURTAINS[(texRand() * CURTAINS.length) | 0];
      const drape = gw * (0.18 + texRand() * 0.14);
      // Lintel: a header block over masonry openings, its underside in shadow.
      if (masonry && !cfg.arch) {
        mx.fillStyle = cfg.frame;
        mx.fillRect(gx - 3, gy - 5.2, gw + 6, 3.2);
        mx.fillStyle = 'rgba(255,255,255,0.22)';
        mx.fillRect(gx - 3, gy - 5.2, gw + 6, 0.5);
        mx.fillStyle = 'rgba(0,0,0,0.22)';
        mx.fillRect(gx - 3, gy - 2.4, gw + 6, 0.5);
        bx2.fillStyle = gray(245);
        bx2.fillRect(gx - 3, gy - 5.2, gw + 6, 3.2);
      }
      // Surround (bevelled: catch-light top/left, shade bottom/right) + sill.
      mx.fillStyle = cfg.frame;
      mx.fillRect(gx - 2, gy - 2, gw + 4, gh + 4);
      mx.fillRect(gx - 3.5, gy + gh + 1, gw + 7, 3);
      mx.fillStyle = 'rgba(255,255,255,0.2)';
      mx.fillRect(gx - 2, gy - 2, gw + 4, 0.55);
      mx.fillRect(gx - 2, gy - 2, 0.55, gh + 4);
      mx.fillRect(gx - 3.5, gy + gh + 1, gw + 7, 0.6); // the sill's top catches the sun
      mx.fillStyle = 'rgba(0,0,0,0.18)';
      mx.fillRect(gx + gw + 1.45, gy - 2, 0.55, gh + 3);
      // The sill's shadow falling on the wall below it.
      const sg = mx.createLinearGradient(0, gy + gh + 4, 0, gy + gh + 7.5);
      sg.addColorStop(0, 'rgba(0,0,0,0.3)');
      sg.addColorStop(1, 'rgba(0,0,0,0)');
      mx.fillStyle = sg;
      mx.fillRect(gx - 3.5, gy + gh + 4, gw + 7, 3.5);
      bx2.fillStyle = gray(225);
      bx2.fillRect(gx - 2, gy - 2, gw + 4, gh + 4); // frame slightly proud
      bx2.fillStyle = gray(255);
      bx2.fillRect(gx - 3.5, gy + gh + 1, gw + 7, 3); // sill stands out
      // Glass: a dim room behind, the sky's reflection over it.
      const g = mx.createLinearGradient(0, gy, 0, gy + gh);
      g.addColorStop(0, lighten(cfg.glass, 42));
      g.addColorStop(0.45, lighten(cfg.glass, 12));
      g.addColorStop(1, cfg.glass);
      mx.fillStyle = g;
      trace(mx);
      mx.save();
      shape(mx);
      mx.clip();
      paintDressing(mx, dress, drawn, fabric, drape, gx, gy, gw, gh);
      // sheen: the upper pane bright, one diagonal streak
      const sh = mx.createLinearGradient(0, gy, 0, gy + gh * 0.6);
      sh.addColorStop(0, 'rgba(255,255,255,0.16)');
      sh.addColorStop(1, 'rgba(255,255,255,0)');
      mx.fillStyle = sh;
      mx.fillRect(gx, gy, gw, gh);
      mx.fillStyle = 'rgba(255,255,255,0.09)';
      mx.beginPath();
      mx.moveTo(gx + gw * 0.55, gy);
      mx.lineTo(gx + gw * 0.78, gy);
      mx.lineTo(gx + gw * 0.38, gy + gh);
      mx.lineTo(gx + gw * 0.15, gy + gh);
      mx.closePath();
      mx.fill();
      // the reveal: the opening is deep, so its top and left sides shade the glass
      const rv = mx.createLinearGradient(0, gy, 0, gy + 3.5);
      rv.addColorStop(0, 'rgba(0,0,0,0.5)');
      rv.addColorStop(1, 'rgba(0,0,0,0)');
      mx.fillStyle = rv;
      mx.fillRect(gx, gy, gw, 3.5);
      const rl = mx.createLinearGradient(gx, 0, gx + 2.2, 0);
      rl.addColorStop(0, 'rgba(0,0,0,0.38)');
      rl.addColorStop(1, 'rgba(0,0,0,0)');
      mx.fillStyle = rl;
      mx.fillRect(gx, gy, 2.2, gh);
      mx.restore();
      // The sash frame inside the opening.
      mx.strokeStyle = cfg.frame;
      mx.lineWidth = 0.6;
      shape(mx);
      mx.stroke();
      bx2.fillStyle = gray(55);
      trace(bx2); // glass recessed
      rx.fillStyle = gray(GLASS_ROUGH);
      trace(rx); // glass is glossy
      if (cfg.mullion) {
        mx.fillStyle = cfg.frame;
        mx.fillRect(gx + gw / 2 - 0.5, gy, 1, gh);
        if (cfg.win === 'tall') mx.fillRect(gx, gy + gh * 0.42, gw, 1);
        bx2.fillStyle = gray(170);
        bx2.fillRect(gx + gw / 2 - 0.5, gy, 1, gh);
      }
      if (cfg.shoji) {
        // Kumiko lattice: 4×3 paper panes per opening (Asakusa machiya).
        mx.fillStyle = cfg.frame;
        for (let i = 1; i < 4; i++) mx.fillRect(gx + (gw * i) / 4 - 0.5, gy, 1, gh);
        for (let i = 1; i < 3; i++) mx.fillRect(gx, gy + (gh * i) / 3 - 0.5, gw, 1);
      }
      // A window air conditioner under some sills (New York, Lagos, Mumbai).
      if (cfg.ac && texRand() < 0.16) {
        const aw = Math.min(gw * 0.5, 9);
        const ax = gx + (texRand() < 0.5 ? 1 : gw - aw - 1);
        const ay = gy + gh - 5.5;
        mx.fillStyle = '#c9c7c0';
        mx.fillRect(ax, ay, aw, 5);
        mx.fillStyle = 'rgba(0,0,0,0.25)';
        for (let i = 1; i < 6; i++) mx.fillRect(ax + 0.6, ay + i * 0.8, aw - 1.2, 0.3);
        mx.fillStyle = 'rgba(255,255,255,0.35)';
        mx.fillRect(ax, ay, aw, 0.5);
        mx.fillStyle = 'rgba(30,25,20,0.12)'; // its drip stain down the wall
        mx.fillRect(ax + aw * 0.4, gy + gh + 4, 1.4, ch * myp * 1.6);
        bx2.fillStyle = gray(255);
        bx2.fillRect(ax, ay, aw, 5);
        rx.fillStyle = gray(150);
        rx.fillRect(ax, ay, aw, 5);
      }
      // Grime streak bleeding down from some sills (weathering).
      if (texRand() < 0.45) {
        const gsx = gx + texRand() * gw * 0.7;
        const gsl = ch * myp * (1.2 + texRand());
        const gg = mx.createLinearGradient(0, gy + gh + 4, 0, gy + gh + 4 + gsl);
        gg.addColorStop(0, 'rgba(40,36,32,0.18)');
        gg.addColorStop(1, 'rgba(40,36,32,0)');
        mx.fillStyle = gg;
        mx.fillRect(gsx, gy + gh + 4, 1.5 + texRand() * 3, gsl);
      }
      if (lit) {
        // Glowing window: warm two-tone (bright top fading low — a lamp inside).
        const lc = cfg.litCols[(col + r) % cfg.litCols.length];
        const eg = ex.createLinearGradient(0, gy, 0, gy + gh);
        eg.addColorStop(0, lc);
        eg.addColorStop(1, darkenCss(lc, 0.55));
        ex.fillStyle = eg;
        trace(ex); // the glow fills the arched opening, not the spandrels
        ex.save();
        shape(ex);
        ex.clip();
        litDressing(ex, dress, drawn, drape, gx, gy, gw, gh);
        ex.restore();
        if (cfg.mullion) {
          ex.fillStyle = '#05070c'; // the sash bars read against the glow
          ex.fillRect(gx + gw / 2 - 0.5, gy, 1, gh);
          if (cfg.win === 'tall') ex.fillRect(gx, gy + gh * 0.42, gw, 1);
        }
        if (cfg.shoji) {
          ex.fillStyle = '#05070c'; // the kumiko grid reads against the paper
          for (let i = 1; i < 4; i++) ex.fillRect(gx + (gw * i) / 4 - 0.5, gy, 1, gh);
          for (let i = 1; i < 3; i++) ex.fillRect(gx, gy + (gh * i) / 3 - 0.5, gw, 1);
        }
      }
    }
  }
  const map = shareTex(new THREE.CanvasTexture(mc));
  const emissive = shareTex(new THREE.CanvasTexture(ec));
  const bump = shareDataTex(new THREE.CanvasTexture(bc));
  const rough = shareDataTex(new THREE.CanvasTexture(rc));
  for (const t of [map, emissive, bump, rough]) t.anisotropy = FACADE_ANISO; // crisp at grazing angles
  const out = { map, emissive, bump, rough };
  facadeCache.set(style, out);
  return out;
}

/** '#rrggbb' lightened by +d per channel (clamped). */
function lighten(hex: string, d: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.min(255, ((n >> 16) & 255) + d);
  const g = Math.min(255, ((n >> 8) & 255) + d);
  const b = Math.min(255, (n & 255) + d);
  return `rgb(${r},${g},${b})`;
}
/** '#rrggbb' scaled by f (0..1). */
function darkenCss(hex: string, f: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgb(${(((n >> 16) & 255) * f) | 0},${(((n >> 8) & 255) * f) | 0},${((n & 255) * f) | 0})`;
}
/** Data maps (bump/roughness) are linear, never sRGB. */
function shareDataTex(t: THREE.CanvasTexture): THREE.CanvasTexture {
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.NoColorSpace;
  t.userData.shared = true;
  return t;
}

// ---- M158/M158b: ground-floor SHOPFRONTS. Only SOME buildings have a shop
//      floor, and what kind depends on the building: towers get glass lobbies
//      or stone office entrances, high-street mid-rises get boutiques, cafés
//      and banks, back-street low-rises get markets, roller shutters and
//      corner shops — each city with its own mix and colours. Every kind is a
//      row of one atlas (SHOP_W × SHOP_LH logical px per row, spanning two
//      facade tiles × SHOP_H); the facade shader picks a building's row, its
//      starting bay and whether it has a shop floor at all from a hash of the
//      building's POSITION, so no rng draw, no extra draw call. Buildings with
//      no shop floor keep their windows down to the ground. ----
export const SHOP_H = 3.8; // world units: the shop floor + its fascia
const SHOP_MIN_H = 7; // shorter buildings (cottages, kiosks) keep plain walls
const SHOP_TOWER_H = 26; // ≥ this: a tower's lobby floor
const SHOP_MID_H = 13; // 13..26: a high street; 7..13: a back street
const SHOP_W = 256; // logical px across a row (two facade tiles)
const SHOP_LH = 64; // logical px up a row
// The atlas paints a little coarser than the facades (several rows per city).
const SHOP_SCALE = COARSE_POINTER ? 2 : 3;

type ShopKind = 'boutique' | 'neon' | 'cafe' | 'glass' | 'lobby' | 'shutter' | 'market';
/** Per city: fascia/awning colours, the shop kinds each building class may
 *  wear (three slots each, repeats weight a kind) and the chance a building
 *  of that class has a shop floor at all [tower, high street, back street]. */
interface ShopPlan {
  pal: string[];
  tower: [ShopKind, ShopKind, ShopKind];
  mid: [ShopKind, ShopKind, ShopKind];
  low: [ShopKind, ShopKind, ShopKind];
  odds: [number, number, number];
}
const SHOP_PLANS: Record<string, ShopPlan> = {
  nyc: {
    pal: ['#7a1e1e', '#1f3b2c', '#203a5c', '#c8a03a', '#2e2e30', '#8c2f1c'],
    tower: ['glass', 'lobby', 'glass'],
    mid: ['boutique', 'cafe', 'lobby'],
    low: ['shutter', 'boutique', 'shutter'],
    odds: [0.85, 0.5, 0.45],
  },
  london: {
    pal: ['#16352a', '#5a1622', '#1a2740', '#2b2b2b', '#6b4a1e', '#244a3a'],
    tower: ['glass', 'lobby', 'glass'],
    mid: ['boutique', 'cafe', 'lobby'],
    low: ['boutique', 'shutter', 'cafe'],
    odds: [0.8, 0.45, 0.4],
  },
  napoli: {
    pal: ['#2f5d3a', '#8a3324', '#c79a3a', '#3a5a7a', '#7a2e4a', '#556b2f'],
    tower: ['lobby', 'lobby', 'cafe'],
    mid: ['cafe', 'boutique', 'shutter'],
    low: ['market', 'shutter', 'cafe'],
    odds: [0.7, 0.5, 0.5],
  },
  tokyo: {
    pal: ['#c8102e', '#1a1a1a', '#e0b400', '#0a6cb2', '#d14d8e', '#25a55f'],
    tower: ['glass', 'glass', 'neon'],
    mid: ['neon', 'glass', 'shutter'],
    low: ['neon', 'shutter', 'neon'],
    odds: [0.85, 0.6, 0.5],
  },
  rio: {
    pal: ['#d9a400', '#2a8a4a', '#d9502b', '#1f6fae', '#b81a55', '#3d9b9b'],
    tower: ['glass', 'lobby', 'glass'],
    mid: ['cafe', 'boutique', 'shutter'],
    low: ['shutter', 'market', 'cafe'],
    odds: [0.8, 0.5, 0.45],
  },
  lagos: {
    pal: ['#e0b000', '#1565c0', '#2e7d32', '#c62828', '#e06a00', '#6a1b9a'],
    tower: ['glass', 'glass', 'lobby'],
    mid: ['boutique', 'shutter', 'market'],
    low: ['market', 'shutter', 'market'],
    odds: [0.8, 0.55, 0.55],
  },
  mumbai: {
    pal: ['#c62828', '#e09a10', '#1565c0', '#2e7d32', '#6a1b9a', '#00838f'],
    tower: ['lobby', 'glass', 'lobby'],
    mid: ['boutique', 'market', 'shutter'],
    low: ['market', 'shutter', 'market'],
    odds: [0.75, 0.55, 0.55],
  },
  dakar: {
    pal: ['#1f6fb2', '#2e8b57', '#c8960a', '#b03a2e', '#16a085', '#7d3c98'],
    tower: ['lobby', 'lobby', 'glass'],
    mid: ['cafe', 'market', 'shutter'],
    low: ['market', 'shutter', 'cafe'],
    odds: [0.75, 0.5, 0.5],
  },
  generic: {
    pal: ['#7a1e1e', '#1f3b2c', '#203a5c', '#c8a03a', '#2e2e30', '#6b4a1e'],
    tower: ['glass', 'lobby', 'glass'],
    mid: ['boutique', 'cafe', 'lobby'],
    low: ['shutter', 'boutique', 'cafe'],
    odds: [0.8, 0.45, 0.4],
  },
};
const GOODS = ['#d6453a', '#e8b23a', '#4f8fd6', '#5aa85a', '#e9e3d6', '#9a5ab0', '#d8783a'];
const PRODUCE = ['#d8382c', '#f0a020', '#e8d040', '#4f9a3a', '#8a3a8a', '#e86a28'];

/** The four maps of one shop atlas, plus where each building class finds its
 *  rows (for the shader). */
interface ShopAtlas extends FacadeMaps {
  rows: number;
  tower: [number, number, number];
  mid: [number, number, number];
  low: [number, number, number];
  odds: [number, number, number];
}
// One city at a time: building another city's atlas frees the last one.
const shopCache = new Map<string, ShopAtlas>();

/** The four canvases of one atlas row, already shifted to that row. */
interface ShopCtx {
  m: CanvasRenderingContext2D; // colour
  b: CanvasRenderingContext2D; // relief (white = proud)
  r: CanvasRenderingContext2D; // roughness (dark = glossy)
  e: CanvasRenderingContext2D; // night glow
}
function sRect(x: CanvasRenderingContext2D, fill: string | CanvasGradient, rx: number, ry: number, w: number, h: number): void {
  x.fillStyle = fill;
  x.fillRect(rx, ry, w, h);
}
/** Sign lettering marks (never words): a centred run of glyph strokes. */
function signMarks(c: ShopCtx, cx: number, cy: number, n: number, ink: string, glow: boolean, size = 1): void {
  const lw = 3.6 * size;
  let lx = cx - (n * lw) / 2;
  for (let i = 0; i < n; i++) {
    const tall = (4 + texRand() * 2) * size;
    sRect(c.m, ink, lx + 0.4 * size, cy - tall / 2, 2.4 * size, tall);
    if (glow) sRect(c.e, ink, lx + 0.4 * size, cy - tall / 2, 2.4 * size, tall);
    lx += lw;
  }
}
const pick = <T,>(a: readonly T[]): T => a[(texRand() * a.length) | 0];
/** A colour pulled toward grey (faded paint). */
function faded(hex: string, k: number): string {
  const c = new THREE.Color(hex).lerp(new THREE.Color(0x9a958c), k);
  return `#${c.getHexString()}`;
}

/** The classic high-street shop: pilasters, a painted fascia, striped
 *  awnings, a window full of goods, a door. `neon` lights the fascias and
 *  hangs lit blade signs on the pilasters (Tokyo). */
function paintBoutique(c: ShopCtx, pal: string[], neon: boolean): void {
  const { m: mx, b: bx, r: rx, e: ex } = c;
  sRect(mx, '#d4cdbf', 0, 0, SHOP_W, SHOP_LH);
  sRect(bx, gray(225), 0, 0, SHOP_W, SHOP_LH);
  sRect(rx, gray(WALL_ROUGH), 0, 0, SHOP_W, SHOP_LH);
  sRect(ex, '#05070c', 0, 0, SHOP_W, SHOP_LH);
  for (let bay = 0; bay < 4; bay++) {
    const x0 = bay * 64 + 4;
    const bw = 56;
    const col = pick(pal);
    const awning = texRand() < (neon ? 0.25 : 0.6);
    const doorLeft = texRand() < 0.5;
    sRect(mx, col, x0, 2.5, bw, 10.5); // the fascia
    sRect(mx, 'rgba(255,255,255,0.16)', x0, 2.5, bw, 0.6);
    sRect(mx, 'rgba(0,0,0,0.3)', x0, 12.4, bw, 0.6);
    sRect(bx, gray(240), x0, 2.5, bw, 10.5);
    sRect(rx, gray(130), x0, 2.5, bw, 10.5);
    signMarks(c, x0 + bw / 2, 7.75, 5 + ((texRand() * 5) | 0), texRand() < 0.5 ? '#f2e6c4' : '#e8c25a', neon);
    if (neon) {
      ex.globalAlpha = 0.35;
      sRect(ex, col, x0, 2.5, bw, 10.5);
      ex.globalAlpha = 1;
    }
    const top = awning ? 21 : 14.5;
    const dw = 13;
    const dx = doorLeft ? x0 + 2 : x0 + bw - dw - 2;
    const wx = doorLeft ? dx + dw + 1.5 : x0 + 2;
    const ww = bw - dw - 5.5;
    const wb = 55;
    sRect(mx, '#2c2824', wx - 1, top - 1, ww + 2, wb - top + 2); // window frame
    const room = mx.createLinearGradient(0, top, 0, wb);
    room.addColorStop(0, '#6b5644');
    room.addColorStop(1, '#3a2e25');
    sRect(mx, room, wx, top, ww, wb - top);
    for (let lx = wx + ww * 0.25; lx < wx + ww; lx += ww * 0.5) {
      sRect(mx, '#fff1d0', lx - 1, top + 3, 2, 1.2);
      sRect(ex, '#fff1d0', lx - 1, top + 3, 2, 1.2);
    }
    for (let sy = top + (wb - top) * 0.55; sy < wb - 1; sy += 7) {
      sRect(mx, 'rgba(255,240,220,0.3)', wx, sy, ww, 0.6); // a shelf
      for (let gx = wx + 1.5; gx < wx + ww - 3; gx += 3.2 + texRand() * 3) {
        const gh = 1.8 + texRand() * 2.6;
        mx.globalAlpha = 0.85;
        sRect(mx, pick(GOODS), gx, sy - gh, 1.6 + texRand() * 1.2, gh);
        mx.globalAlpha = 1;
      }
    }
    sRect(mx, '#2c2824', wx + ww / 2 - 0.5, top, 1, wb - top); // mullion
    const gl = mx.createLinearGradient(0, top, 0, wb);
    gl.addColorStop(0, 'rgba(255,255,255,0.2)');
    gl.addColorStop(0.5, 'rgba(255,255,255,0.04)');
    gl.addColorStop(1, 'rgba(255,255,255,0.1)');
    sRect(mx, gl, wx, top, ww, wb - top);
    sRect(ex, neon ? '#7a5a40' : '#6a4c30', wx, top, ww, wb - top);
    const lg = ex.createLinearGradient(0, top, 0, wb);
    lg.addColorStop(0, 'rgba(255,226,180,0.32)');
    lg.addColorStop(1, 'rgba(255,236,200,0)');
    sRect(ex, lg, wx, top, ww, wb - top);
    sRect(ex, '#05070c', wx + ww / 2 - 0.5, top, 1, wb - top);
    sRect(bx, gray(70), wx, top, ww, wb - top);
    sRect(rx, gray(GLASS_ROUGH), wx, top, ww, wb - top);
    sRect(mx, '#26221e', dx, top, dw, 60 - top); // the door
    sRect(mx, '#4a3c30', dx + 1.5, top + 1.5, dw - 3, (60 - top) * 0.55);
    sRect(mx, col, dx + 1.5, 52, dw - 3, 6);
    sRect(mx, '#d8c48a', doorLeft ? dx + dw - 3 : dx + 2, 40, 1, 3);
    sRect(ex, '#5a4430', dx + 1.5, top + 1.5, dw - 3, (60 - top) * 0.55);
    sRect(bx, gray(90), dx, top, dw, 60 - top);
    sRect(mx, col, wx - 1, wb + 1, ww + 2, 5); // the stall riser
    sRect(mx, 'rgba(0,0,0,0.3)', wx - 1, wb + 1, ww + 2, 0.6);
    if (awning) {
      for (let ax = x0; ax < x0 + bw; ax += 4) {
        sRect(mx, ((ax - x0) / 4) % 2 === 0 ? col : '#f1ede2', ax, 13, Math.min(4, x0 + bw - ax), 8);
      }
      const ag = mx.createLinearGradient(0, 13, 0, 21);
      ag.addColorStop(0, 'rgba(255,255,255,0.18)');
      ag.addColorStop(1, 'rgba(0,0,0,0.28)');
      sRect(mx, ag, x0, 13, bw, 8);
      for (let ax = x0; ax < x0 + bw; ax += 4) {
        mx.fillStyle = ((ax - x0) / 4) % 2 === 0 ? col : '#f1ede2';
        mx.beginPath();
        mx.arc(ax + 2, 21, 2, 0, Math.PI);
        mx.fill();
      }
      const sh = mx.createLinearGradient(0, 22.5, 0, 30);
      sh.addColorStop(0, 'rgba(0,0,0,0.4)');
      sh.addColorStop(1, 'rgba(0,0,0,0)');
      sRect(mx, sh, x0, 22.5, bw, 7.5);
      sRect(ex, 'rgba(5,7,12,0.55)', x0, 21, bw, 6);
      const bg = bx.createLinearGradient(0, 13, 0, 21);
      bg.addColorStop(0, gray(255));
      bg.addColorStop(1, gray(205));
      sRect(bx, bg, x0, 13, bw, 8);
      sRect(rx, gray(245), x0, 13, bw, 8);
    }
  }
  for (let p = 0; p <= 4; p++) {
    const px = p * 64 - 4;
    sRect(mx, '#c4bcad', px, 0, 8, SHOP_LH); // rusticated pilasters
    mx.fillStyle = 'rgba(0,0,0,0.14)';
    for (let y = 6; y < SHOP_LH; y += 6) mx.fillRect(px, y, 8, 0.45);
    mx.fillRect(px + 7.4, 0, 0.6, SHOP_LH);
    sRect(mx, 'rgba(255,255,255,0.25)', px, 0, 0.6, SHOP_LH);
    sRect(bx, gray(248), px, 0, 8, SHOP_LH);
    if (neon && p > 0 && p < 4 && texRand() < 0.7) {
      // a lit blade sign down the pilaster, glyphs stacked
      const col = pick(pal);
      const by = 15 + texRand() * 4;
      const bh = 26 + texRand() * 12;
      sRect(mx, '#141418', px + 1, by, 6, bh);
      sRect(mx, col, px + 1.6, by + 0.6, 4.8, bh - 1.2);
      sRect(ex, col, px + 1.6, by + 0.6, 4.8, bh - 1.2);
      for (let gy = by + 2; gy < by + bh - 4; gy += 5) {
        sRect(mx, '#fff4e0', px + 2.6, gy, 2.8, 3.2);
        sRect(ex, '#fff4e0', px + 2.6, gy, 2.8, 3.2);
      }
      sRect(bx, gray(255), px + 1, by, 6, bh);
      sRect(rx, gray(110), px + 1, by, 6, bh);
    }
  }
  sRect(mx, '#d2cbbd', 0, 0, SHOP_W, 2.5); // the cornice over the fascia
  sRect(mx, 'rgba(0,0,0,0.25)', 0, 2.5, SHOP_W, 0.6);
  sRect(bx, gray(255), 0, 0, SHOP_W, 2.5);
  sRect(mx, '#5a554e', 0, 60, SHOP_W, 4); // the plinth
  sRect(bx, gray(235), 0, 60, SHOP_W, 4);
}

/** A café / bistro: painted timber frontage, a plain canvas drop awning, big
 *  small-paned windows onto a warm room of round tables. Two fronts per row. */
function paintCafe(c: ShopCtx, pal: string[]): void {
  const { m: mx, b: bx, r: rx, e: ex } = c;
  sRect(ex, '#05070c', 0, 0, SHOP_W, SHOP_LH);
  sRect(rx, gray(190), 0, 0, SHOP_W, SHOP_LH);
  for (let u = 0; u < 2; u++) {
    const x0 = u * 128;
    const col = pick(pal);
    const canvas = texRand() < 0.5 ? pick(pal) : '#ece4d2';
    sRect(mx, col, x0, 0, 128, SHOP_LH); // painted timber
    sRect(bx, gray(215), x0, 0, 128, SHOP_LH);
    sRect(mx, 'rgba(0,0,0,0.25)', x0, 0, 1, SHOP_LH);
    sRect(mx, 'rgba(255,255,255,0.12)', x0 + 1, 0, 1, SHOP_LH);
    sRect(mx, 'rgba(0,0,0,0.22)', x0 + 2, 2, 124, 10); // the sign panel
    signMarks(c, x0 + 64, 7, 6 + ((texRand() * 4) | 0), '#e8c25a', false, 0.9);
    // the drop awning, its valance, its shade on the glass
    const ag = mx.createLinearGradient(0, 12, 0, 21);
    ag.addColorStop(0, canvas);
    ag.addColorStop(1, faded(canvas, 0.35));
    sRect(mx, ag, x0 + 1, 12, 126, 9);
    sRect(mx, 'rgba(0,0,0,0.22)', x0 + 1, 20.5, 126, 3.5); // valance
    signMarks(c, x0 + 64, 22.2, 7, canvas === '#ece4d2' ? '#5a2a1e' : '#f4efe2', false, 0.4);
    const bg = bx.createLinearGradient(0, 12, 0, 21);
    bg.addColorStop(0, gray(255));
    bg.addColorStop(1, gray(200));
    sRect(bx, bg, x0 + 1, 12, 126, 12);
    sRect(rx, gray(250), x0 + 1, 12, 126, 12);
    const sh = mx.createLinearGradient(0, 24, 0, 33);
    sh.addColorStop(0, 'rgba(0,0,0,0.42)');
    sh.addColorStop(1, 'rgba(0,0,0,0)');
    // two windows and the door between them
    for (const [wx, ww] of [
      [x0 + 5, 47],
      [x0 + 76, 47],
    ] as const) {
      const wt = 25;
      const wb = 55;
      const room = mx.createLinearGradient(0, wt, 0, wb);
      room.addColorStop(0, '#8a6644');
      room.addColorStop(1, '#4a3424');
      sRect(mx, room, wx, wt, ww, wb - wt);
      for (let t = 0; t < 3; t++) {
        // round café tables + bentwood chairs
        const tx = wx + 7 + t * 15 + texRand() * 3;
        sRect(mx, '#2a1d14', tx - 4, 46, 8, 1.4);
        sRect(mx, '#2a1d14', tx - 0.5, 46, 1, 8);
        sRect(mx, '#2a1d14', tx - 7, 44, 1, 10);
        sRect(mx, '#f2ece0', tx - 1.5, 44.6, 1.6, 1.4);
      }
      for (let lx = wx + 8; lx < wx + ww; lx += 16) {
        sRect(mx, '#3a2a1c', lx, wt, 0.5, 5);
        sRect(mx, '#ffe9b8', lx - 1.2, wt + 5, 2.9, 1.6);
        sRect(ex, '#ffe9b8', lx - 1.2, wt + 5, 2.9, 1.6);
      }
      sRect(ex, '#7a5434', wx, wt, ww, wb - wt);
      for (let gx = 1; gx < 4; gx++) sRect(mx, col, wx + (gx * ww) / 4 - 0.6, wt, 1.2, wb - wt); // glazing bars
      sRect(mx, col, wx, wt + (wb - wt) * 0.42, ww, 1.2);
      sRect(ex, '#05070c', wx, wt + (wb - wt) * 0.42, ww, 1.2);
      const gl = mx.createLinearGradient(0, wt, 0, wb);
      gl.addColorStop(0, 'rgba(255,255,255,0.18)');
      gl.addColorStop(1, 'rgba(255,255,255,0.03)');
      sRect(mx, gl, wx, wt, ww, wb - wt);
      sRect(mx, sh, wx, wt, ww, 9);
      sRect(bx, gray(80), wx, wt, ww, wb - wt);
      sRect(rx, gray(GLASS_ROUGH), wx, wt, ww, wb - wt);
      sRect(mx, 'rgba(0,0,0,0.3)', wx - 1, wb, ww + 2, 1); // sill
    }
    const dx = x0 + 55;
    sRect(mx, '#2a201a', dx, 25, 18, 35);
    sRect(mx, '#6a4a30', dx + 2, 27, 14, 18);
    sRect(ex, '#6a4a30', dx + 2, 27, 14, 18);
    sRect(mx, 'rgba(255,255,255,0.12)', dx + 2, 27, 14, 18);
    sRect(mx, '#c9a860', dx + 14, 44, 1, 3);
    sRect(bx, gray(70), dx, 25, 18, 35);
    sRect(rx, gray(120), dx + 2, 27, 14, 18);
    sRect(mx, '#1d1a17', x0 + 2, 56, 124, 4); // the riser
  }
  sRect(mx, '#4a4640', 0, 60, SHOP_W, 4);
  sRect(bx, gray(235), 0, 60, SHOP_W, 4);
}

/** A modern glass frontage: an anodised frame, a backlit sign band, a steel
 *  canopy line, floor-to-ceiling glass that mirrors the sky, glass doors. */
function paintGlass(c: ShopCtx, pal: string[]): void {
  const { m: mx, b: bx, r: rx, e: ex } = c;
  sRect(mx, '#25292e', 0, 0, SHOP_W, SHOP_LH);
  sRect(bx, gray(210), 0, 0, SHOP_W, SHOP_LH);
  sRect(rx, gray(120), 0, 0, SHOP_W, SHOP_LH);
  sRect(ex, '#05070c', 0, 0, SHOP_W, SHOP_LH);
  const accent = pick(pal);
  // the sign band: dark, with lit lettering and an accent stripe
  sRect(mx, '#16181b', 0, 2, SHOP_W, 9);
  for (let u = 0; u < 2; u++) {
    if (texRand() < 0.75) signMarks(c, u * 128 + 40 + texRand() * 48, 6.5, 5 + ((texRand() * 4) | 0), texRand() < 0.6 ? '#f4f6f8' : accent, true, 0.9);
  }
  sRect(mx, accent, 0, 10.2, SHOP_W, 0.8);
  sRect(ex, accent, 0, 10.2, SHOP_W, 0.8);
  sRect(mx, '#9aa2aa', 0, 11, SHOP_W, 2.2); // the canopy
  sRect(mx, 'rgba(255,255,255,0.35)', 0, 11, SHOP_W, 0.5);
  sRect(bx, gray(255), 0, 11, SHOP_W, 2.2);
  sRect(rx, gray(70), 0, 11, SHOP_W, 2.2);
  // the glass: sky reflection on top, a dim lobby behind
  const gt = 13.5;
  const gb = 58;
  const g = mx.createLinearGradient(0, gt, 0, gb);
  g.addColorStop(0, '#7d95a4');
  g.addColorStop(0.45, '#46586a');
  g.addColorStop(1, '#2a333c');
  sRect(mx, g, 0, gt, SHOP_W, gb - gt);
  for (let k = 0; k < 7; k++) {
    // planters and furniture silhouettes inside
    const fx = texRand() * SHOP_W;
    sRect(mx, 'rgba(18,22,26,0.55)', fx, gb - 9 - texRand() * 6, 5 + texRand() * 9, 15);
  }
  mx.save();
  mx.globalAlpha = 0.16;
  mx.fillStyle = '#ffffff';
  for (let sx = -40; sx < SHOP_W; sx += 34 + texRand() * 20) {
    mx.beginPath(); // diagonal sky glints
    mx.moveTo(sx, gb);
    mx.lineTo(sx + 9, gb);
    mx.lineTo(sx + 31, gt);
    mx.lineTo(sx + 22, gt);
    mx.fill();
  }
  mx.restore();
  sRect(ex, '#5a4a36', 0, gt, SHOP_W, gb - gt);
  sRect(ex, '#d8dee6', 0, gt + 1.2, SHOP_W, 0.8); // the lobby ceiling strip
  sRect(bx, gray(90), 0, gt, SHOP_W, gb - gt);
  sRect(rx, gray(GLASS_ROUGH - 20), 0, gt, SHOP_W, gb - gt);
  for (let x = 0; x <= SHOP_W; x += 16) {
    sRect(mx, '#3a3f45', x - 0.6, gt, 1.2, gb - gt); // mullions
    sRect(bx, gray(255), x - 0.6, gt, 1.2, gb - gt);
    sRect(ex, '#05070c', x - 0.6, gt, 1.2, gb - gt);
  }
  // glass double doors with long steel pulls in one bay
  const dx = 96 + 32 * ((texRand() * 3) | 0);
  sRect(mx, '#1c2126', dx, 24, 32, 34);
  sRect(mx, 'rgba(160,190,210,0.35)', dx + 1.5, 25.5, 13.5, 32.5);
  sRect(mx, 'rgba(160,190,210,0.35)', dx + 17, 25.5, 13.5, 32.5);
  sRect(mx, '#c8ced4', dx + 13, 34, 0.8, 12);
  sRect(mx, '#c8ced4', dx + 18.2, 34, 0.8, 12);
  sRect(ex, '#4a4236', dx + 1.5, 25.5, 29, 32.5);
  sRect(bx, gray(60), dx, 24, 32, 34);
  sRect(mx, '#9aa2aa', dx - 2, 22, 36, 2); // the door canopy
  sRect(bx, gray(255), dx - 2, 22, 36, 2);
  sRect(mx, '#38393c', 0, 58, SHOP_W, 6); // polished granite plinth
  for (let k = 0; k < 120; k++) sRect(mx, texRand() < 0.5 ? '#4c4d51' : '#2a2b2e', texRand() * SHOP_W, 58 + texRand() * 6, 0.6, 0.6);
  sRect(bx, gray(240), 0, 58, SHOP_W, 6);
  sRect(rx, gray(90), 0, 58, SHOP_W, 6);
}

/** A stone office / apartment entrance: rusticated ashlar, an arched doorway
 *  with a lit fanlight and panelled doors, barred windows, brass lamps. */
function paintLobby(c: ShopCtx, _pal: string[]): void {
  const { m: mx, b: bx, r: rx, e: ex } = c;
  const stone = pick(['#c9c0ae', '#b8ad98', '#d2c8b4', '#a9a294']);
  sRect(mx, stone, 0, 0, SHOP_W, SHOP_LH);
  sRect(bx, gray(235), 0, 0, SHOP_W, SHOP_LH);
  sRect(rx, gray(WALL_ROUGH), 0, 0, SHOP_W, SHOP_LH);
  sRect(ex, '#05070c', 0, 0, SHOP_W, SHOP_LH);
  // rusticated courses: deep joints, a soft bevel on each block
  for (let y = 12, row = 0; y < 60; y += 8, row++) {
    sRect(mx, 'rgba(0,0,0,0.32)', 0, y, SHOP_W, 1.1);
    sRect(bx, gray(60), 0, y, SHOP_W, 1.1);
    sRect(mx, 'rgba(255,255,255,0.12)', 0, y + 1.1, SHOP_W, 0.7);
    for (let x = row % 2 ? 12 : 0; x < SHOP_W; x += 24) {
      sRect(mx, 'rgba(0,0,0,0.3)', x, y, 1.1, 8);
      sRect(bx, gray(60), x, y, 1.1, 8);
      sRect(mx, `rgba(${texRand() < 0.5 ? '0,0,0' : '255,255,255'},${(texRand() * 0.06).toFixed(3)})`, x + 1.1, y + 1.1, 22.9, 6.9);
    }
  }
  sRect(mx, faded(stone, -0.0), 0, 0, SHOP_W, 12); // the smooth band + cornice
  sRect(mx, 'rgba(255,255,255,0.18)', 0, 0, SHOP_W, 2);
  sRect(mx, 'rgba(0,0,0,0.28)', 0, 11.4, SHOP_W, 0.8);
  sRect(bx, gray(250), 0, 0, SHOP_W, 2);
  for (let u = 0; u < 2; u++) {
    const cx = u * 128 + 64;
    // the carved name band over the door
    mx.globalAlpha = 0.5;
    signMarks(c, cx, 6.5, 7, '#6d6556', false, 0.8);
    mx.globalAlpha = 1;
    // the arched doorway: voussoirs, a lit fanlight, panelled doors
    const r = 16;
    const ay = 30;
    mx.fillStyle = 'rgba(0,0,0,0.18)';
    mx.beginPath();
    mx.arc(cx, ay, r + 4, Math.PI, 0);
    mx.fill();
    sRect(mx, 'rgba(0,0,0,0.18)', cx - r - 4, ay, 2 * r + 8, 60 - ay);
    mx.fillStyle = '#1a1714';
    mx.beginPath();
    mx.arc(cx, ay, r, Math.PI, 0);
    mx.fill();
    sRect(mx, '#1a1714', cx - r, ay, 2 * r, 60 - ay);
    bx.fillStyle = gray(40);
    bx.beginPath();
    bx.arc(cx, ay, r, Math.PI, 0);
    bx.fill();
    sRect(bx, gray(40), cx - r, ay, 2 * r, 60 - ay);
    for (const [x, gfill] of [
      [mx, '#8a6a40'],
      [ex, '#c89a58'],
    ] as const) {
      x.fillStyle = gfill;
      x.beginPath();
      x.arc(cx, ay, r - 2, Math.PI, 0);
      x.fill();
    }
    mx.strokeStyle = '#2a2420';
    mx.lineWidth = 0.8;
    for (let k = 1; k < 6; k++) {
      const a = Math.PI + (k * Math.PI) / 6; // fanlight glazing bars
      mx.beginPath();
      mx.moveTo(cx, ay);
      mx.lineTo(cx + Math.cos(a) * (r - 2), ay + Math.sin(a) * (r - 2));
      mx.stroke();
    }
    sRect(mx, '#d8cfbc', cx - 1.6, ay - r - 4, 3.2, 5); // keystone
    const door = pick(['#2d1f15', '#1d2a22', '#22262e', '#3a1c18']);
    for (const s of [-1, 1]) {
      const dx = s < 0 ? cx - r + 1 : cx + 0.5;
      sRect(mx, door, dx, ay + 1, r - 1.5, 59 - ay);
      sRect(mx, 'rgba(255,255,255,0.08)', dx + 2, ay + 4, r - 5.5, 10);
      sRect(mx, 'rgba(255,255,255,0.08)', dx + 2, ay + 17, r - 5.5, 10);
      sRect(mx, '#d6b56a', s < 0 ? cx - 2.2 : cx + 1.4, ay + 14, 0.8, 1.6);
    }
    sRect(rx, gray(140), cx - r, ay, 2 * r, 60 - ay);
    for (const s of [-1, 1]) {
      // brass lamps beside the door
      const lx = cx + s * (r + 7);
      sRect(mx, '#2a2520', lx - 0.4, 28, 0.8, 4);
      sRect(mx, '#ffe1a0', lx - 1.4, 24.5, 2.8, 3.6);
      sRect(ex, '#ffe1a0', lx - 1.4, 24.5, 2.8, 3.6);
      // barred windows
      const wx = cx + s * 46 - 9;
      sRect(mx, 'rgba(0,0,0,0.25)', wx - 1.5, 21.5, 21, 30);
      sRect(mx, '#2a3036', wx, 23, 18, 27);
      sRect(ex, '#3a2c1e', wx, 23, 18, 27);
      sRect(mx, 'rgba(255,255,255,0.14)', wx, 23, 18, 8);
      for (let bxp = wx + 3; bxp < wx + 18; bxp += 3) sRect(mx, '#15171a', bxp, 23, 0.7, 27);
      sRect(mx, '#15171a', wx, 36, 18, 0.7);
      sRect(bx, gray(70), wx, 23, 18, 27);
      sRect(rx, gray(GLASS_ROUGH), wx, 23, 18, 27);
      sRect(mx, '#ddd4c2', wx - 2, 50, 22, 1.6); // sill
      sRect(bx, gray(255), wx - 2, 50, 22, 1.6);
    }
  }
  sRect(mx, '#5d5850', 0, 60, SHOP_W, 4); // granite plinth
  sRect(bx, gray(235), 0, 60, SHOP_W, 4);
}

/** Back-street lock-ups: roller shutters (some half raised on a lit shop),
 *  small sign boxes, tags on the closed ones, a concrete surround. */
function paintShutter(c: ShopCtx, pal: string[]): void {
  const { m: mx, b: bx, r: rx, e: ex } = c;
  sRect(mx, '#b4aea3', 0, 0, SHOP_W, SHOP_LH);
  sRect(bx, gray(215), 0, 0, SHOP_W, SHOP_LH);
  sRect(rx, gray(WALL_ROUGH), 0, 0, SHOP_W, SHOP_LH);
  sRect(ex, '#05070c', 0, 0, SHOP_W, SHOP_LH);
  for (let k = 0; k < 18; k++) {
    // grime streaks down the concrete
    const sx = texRand() * SHOP_W;
    const g = mx.createLinearGradient(0, 0, 0, SHOP_LH);
    g.addColorStop(0, 'rgba(40,34,28,0.22)');
    g.addColorStop(1, 'rgba(40,34,28,0)');
    sRect(mx, g, sx, 0, 1 + texRand() * 3, 20 + texRand() * 40);
  }
  for (let bay = 0; bay < 4; bay++) {
    const x0 = bay * 64 + 5;
    const bw = 54;
    const col = pick(pal);
    if (texRand() < 0.75) {
      const sw = 30 + texRand() * 20; // a small sign box
      const sx = x0 + (bw - sw) / 2;
      sRect(mx, col, sx, 1.5, sw, 7);
      sRect(mx, 'rgba(255,255,255,0.16)', sx, 1.5, sw, 0.6);
      signMarks(c, sx + sw / 2, 5, 4 + ((texRand() * 4) | 0), '#f4efe2', texRand() < 0.5, 0.8);
      sRect(bx, gray(250), sx, 1.5, sw, 7);
    }
    sRect(mx, '#6e7174', x0, 9.5, bw, 4.5); // the shutter box
    sRect(mx, 'rgba(255,255,255,0.2)', x0, 9.5, bw, 0.6);
    sRect(bx, gray(250), x0, 9.5, bw, 4.5);
    const open = texRand() < 0.4;
    const bottom = open ? 26 + texRand() * 10 : 59;
    if (open) {
      // the lit shop under the raised shutter
      sRect(mx, '#5e4a38', x0, bottom, bw, 59 - bottom);
      for (let sy = bottom + 6; sy < 58; sy += 6) {
        for (let gx = x0 + 1; gx < x0 + bw - 2; gx += 2.8 + texRand() * 2) {
          const gh = 1.6 + texRand() * 3;
          sRect(mx, pick(GOODS), gx, sy - gh, 1.4 + texRand(), gh);
        }
      }
      sRect(ex, '#8a6a46', x0, bottom, bw, 59 - bottom);
      sRect(ex, '#fff6dc', x0 + 4, bottom + 1, bw - 8, 0.7);
      sRect(bx, gray(70), x0, bottom, bw, 59 - bottom);
    }
    const metal = texRand() < 0.5 ? '#8d9296' : faded(col, 0.45);
    sRect(mx, metal, x0, 14, bw, bottom - 14);
    for (let y = 14; y < bottom - 0.5; y += 2.2) {
      sRect(mx, 'rgba(0,0,0,0.28)', x0, y + 1.6, bw, 0.6); // ribs
      sRect(mx, 'rgba(255,255,255,0.14)', x0, y, bw, 0.5);
      sRect(bx, gray(150), x0, y + 1.4, bw, 0.8);
    }
    sRect(rx, gray(150), x0, 14, bw, bottom - 14);
    sRect(mx, '#3a3c3e', x0, bottom - 1.8, bw, 1.8); // the bottom bar + handle
    sRect(mx, '#c9ccd0', x0 + bw / 2 - 2, bottom - 3.4, 4, 1);
    if (!open && texRand() < 0.6) {
      // a tag or two: bubble loops in spray colours (no letters)
      mx.save();
      mx.lineWidth = 1.5;
      mx.lineCap = 'round';
      for (let t = 0; t < 2; t++) {
        mx.strokeStyle = pick(['#e0306a', '#30b0e0', '#f0d020', '#40c060', '#ffffff', '#a050e0']);
        mx.globalAlpha = 0.75;
        mx.beginPath();
        let px = x0 + 6 + texRand() * 18;
        const py = 30 + texRand() * 14;
        mx.moveTo(px, py);
        for (let s = 0; s < 6; s++) {
          px += 3 + texRand() * 3;
          mx.quadraticCurveTo(px - 2, py - 5 - texRand() * 4, px, py + (texRand() - 0.5) * 4);
        }
        mx.stroke();
      }
      mx.restore();
    }
  }
  sRect(mx, '#7d7870', 0, 59, SHOP_W, 5); // the kerb plinth
  sRect(bx, gray(235), 0, 59, SHOP_W, 5);
}

/** Open-fronted market stalls: canvas awnings, no glass, shelves packed to
 *  the ceiling, produce heaped in crates at the front, a tube light inside. */
function paintMarket(c: ShopCtx, pal: string[]): void {
  const { m: mx, b: bx, r: rx, e: ex } = c;
  const wall = pick(['#d9c39a', '#cfb08a', '#c9b8a0', '#d6c2a8']);
  sRect(mx, wall, 0, 0, SHOP_W, SHOP_LH);
  sRect(bx, gray(215), 0, 0, SHOP_W, SHOP_LH);
  sRect(rx, gray(WALL_ROUGH), 0, 0, SHOP_W, SHOP_LH);
  sRect(ex, '#05070c', 0, 0, SHOP_W, SHOP_LH);
  for (let k = 0; k < 10; k++) {
    // peeling plaster patches
    sRect(mx, 'rgba(120,100,80,0.18)', texRand() * SHOP_W, texRand() * 60, 4 + texRand() * 10, 3 + texRand() * 6);
  }
  for (let bay = 0; bay < 4; bay++) {
    const x0 = bay * 64 + 3;
    const bw = 58;
    const top = 17;
    // the dark stall and its tube light
    sRect(mx, '#1f1a14', x0, top, bw, 60 - top);
    sRect(bx, gray(60), x0, top, bw, 60 - top);
    sRect(rx, gray(240), x0, top, bw, 60 - top);
    sRect(ex, '#3a2e20', x0, top, bw, 60 - top);
    sRect(mx, '#f4f8ff', x0 + 8, top + 2, bw - 16, 0.8);
    sRect(ex, '#f4f8ff', x0 + 8, top + 2, bw - 16, 0.8);
    // shelves packed to the ceiling
    for (let sy = top + 9; sy < 46; sy += 6.5) {
      sRect(mx, 'rgba(255,230,200,0.18)', x0, sy, bw, 0.6);
      for (let gx = x0 + 1; gx < x0 + bw - 2; gx += 2.2 + texRand() * 1.6) {
        const gh = 2 + texRand() * 3.4;
        sRect(mx, pick(GOODS), gx, sy - gh, 1.5 + texRand(), gh);
      }
    }
    if (texRand() < 0.6) {
      // goods hanging from the awning rail
      for (let hx = x0 + 3; hx < x0 + bw - 3; hx += 4 + texRand() * 3) {
        const hl = 4 + texRand() * 6;
        sRect(mx, '#3a2e22', hx, top, 0.4, hl);
        mx.fillStyle = pick(PRODUCE);
        mx.beginPath();
        mx.ellipse(hx + 0.2, top + hl, 1.4, 2, 0, 0, Math.PI * 2);
        mx.fill();
      }
    }
    // crates heaped with produce, a sack or two
    for (let cx = x0 + 1; cx < x0 + bw - 8; cx += 10 + texRand() * 2) {
      sRect(mx, '#8a6238', cx, 51, 9, 9);
      sRect(mx, 'rgba(0,0,0,0.3)', cx, 54, 9, 0.6);
      sRect(mx, 'rgba(0,0,0,0.3)', cx, 57, 9, 0.6);
      sRect(bx, gray(230), cx, 51, 9, 9);
      const fruit = pick(PRODUCE);
      for (let k = 0; k < 7; k++) {
        mx.fillStyle = fruit;
        mx.beginPath();
        mx.arc(cx + 1.2 + texRand() * 6.6, 50.5 - texRand() * 2.2, 1.1, 0, Math.PI * 2);
        mx.fill();
      }
      bx.fillStyle = gray(255);
      bx.fillRect(cx, 48, 9, 3);
    }
    // the canvas awning: two tones, sun-faded, ragged hem
    const ca = pick(pal);
    const cb = texRand() < 0.5 ? '#ece2c8' : faded(pick(pal), 0.3);
    sRect(mx, faded(ca, 0.25), x0 - 1, 6, bw + 2, 6);
    sRect(mx, cb, x0 - 1, 12, bw + 2, 5);
    const ag = mx.createLinearGradient(0, 6, 0, 17);
    ag.addColorStop(0, 'rgba(255,255,255,0.15)');
    ag.addColorStop(1, 'rgba(0,0,0,0.3)');
    sRect(mx, ag, x0 - 1, 6, bw + 2, 11);
    mx.fillStyle = cb;
    for (let tx = x0 - 1; tx < x0 + bw + 1; tx += 3) {
      mx.beginPath();
      mx.moveTo(tx, 17);
      mx.lineTo(tx + 3, 17);
      mx.lineTo(tx + 1.5, 18.6 + texRand() * 0.8);
      mx.fill();
    }
    const bg = bx.createLinearGradient(0, 6, 0, 17);
    bg.addColorStop(0, gray(255));
    bg.addColorStop(1, gray(200));
    sRect(bx, bg, x0 - 1, 6, bw + 2, 11);
    sRect(rx, gray(250), x0 - 1, 6, bw + 2, 11);
    const sh = mx.createLinearGradient(0, 17, 0, 27);
    sh.addColorStop(0, 'rgba(0,0,0,0.45)');
    sh.addColorStop(1, 'rgba(0,0,0,0)');
    sRect(mx, sh, x0, 17, bw, 10);
    if (texRand() < 0.6) {
      const sw = 26 + texRand() * 20; // a painted board above
      sRect(mx, pick(pal), x0 + (bw - sw) / 2, 0.8, sw, 4.6);
      signMarks(c, x0 + bw / 2, 3.1, 5, '#f8f2e0', false, 0.6);
    }
  }
  sRect(mx, '#6a6258', 0, 60, SHOP_W, 4);
  sRect(bx, gray(235), 0, 60, SHOP_W, 4);
}

function paintShopRow(c: ShopCtx, kind: ShopKind, pal: string[]): void {
  if (kind === 'boutique' || kind === 'neon') paintBoutique(c, pal, kind === 'neon');
  else if (kind === 'cafe') paintCafe(c, pal);
  else if (kind === 'glass') paintGlass(c, pal);
  else if (kind === 'lobby') paintLobby(c, pal);
  else if (kind === 'shutter') paintShutter(c, pal);
  else paintMarket(c, pal);
}

function shopFacade(style: string): ShopAtlas | null {
  const plan = SHOP_PLANS[style];
  if (!plan) return null;
  const hit = shopCache.get(style);
  if (hit) return hit;
  for (const [k, old] of shopCache) {
    // one city at a time: free the last city's atlas from the GPU
    for (const t of [old.map, old.emissive, old.bump, old.rough]) t.dispose();
    shopCache.delete(k);
  }
  const kinds: ShopKind[] = [];
  const slot = (k: ShopKind): number => {
    const i = kinds.indexOf(k);
    if (i >= 0) return i;
    kinds.push(k);
    return kinds.length - 1;
  };
  const tower = plan.tower.map(slot) as [number, number, number];
  const mid = plan.mid.map(slot) as [number, number, number];
  const low = plan.low.map(slot) as [number, number, number];
  const rows = kinds.length;
  const [mc, mx] = c2d(SHOP_W, SHOP_LH * rows, SHOP_SCALE);
  const [bc, bx] = c2d(SHOP_W, SHOP_LH * rows, SHOP_SCALE);
  const [rc, rx] = c2d(SHOP_W, SHOP_LH * rows, SHOP_SCALE);
  const [ec, ex] = c2d(SHOP_W, SHOP_LH * rows, SHOP_SCALE);
  let h = 7;
  for (let i = 0; i < style.length; i++) h = (h * 31 + style.charCodeAt(i)) >>> 0;
  kinds.forEach((kind, row) => {
    _texSeed = 0x5409 + h + row * 977;
    const all = [mx, bx, rx, ex];
    for (const x of all) {
      x.save();
      x.translate(0, row * SHOP_LH);
      x.beginPath();
      x.rect(0, 0, SHOP_W, SHOP_LH);
      x.clip();
    }
    paintShopRow({ m: mx, b: bx, r: rx, e: ex }, kind, plan.pal);
    for (const x of all) x.restore();
  });
  const out: ShopAtlas = {
    map: shareTex(new THREE.CanvasTexture(mc)),
    emissive: shareTex(new THREE.CanvasTexture(ec)),
    bump: shareDataTex(new THREE.CanvasTexture(bc)),
    rough: shareDataTex(new THREE.CanvasTexture(rc)),
    rows,
    tower,
    mid,
    low,
    odds: plan.odds,
  };
  for (const t of [out.map, out.emissive, out.bump, out.rough]) {
    t.wrapT = THREE.ClampToEdgeWrapping; // rows stack up, each repeats across
    t.anisotropy = FACADE_ANISO;
  }
  shopCache.set(style, out);
  return out;
}

/** Tile ALL the facade maps (colour, emissive, bump, roughness) by each
 *  instance's WORLD size, so bricks/windows stay a consistent real size on
 *  every building. M158: buildings with a shop floor wear one row of the shop
 *  atlas over their bottom SHOP_H with the window tile starting above it
 *  (M158b: which buildings, which row and which bay comes first are hashed
 *  from the building's position), and the walls darken where they meet the
 *  ground, at their corners and under the parapet, with a pale coping along
 *  the top. */
/** M158c (owner): night cities whose streets are lit — the ground floors
 *  and the first storeys catch the warm street light (fading out by ~10 m),
 *  riding the wall's own colour. Display only. */
export const STREET_GLOW: Record<string, number> = {
  tokyo: 0xb88a58,
  asakusa: 0xb07a48,
};

function patchFacadeTiling(
  mat: THREE.MeshStandardMaterial,
  shop: ShopAtlas | null,
  glow?: number,
): void {
  const s = shop;
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.streetGlow = { value: new THREE.Color(glow ?? 0x000000) };
    shader.uniforms.shopOn = { value: s ? 1 : 0 };
    shader.uniforms.shopRows = { value: s ? s.rows : 1 };
    shader.uniforms.shopTower = { value: new THREE.Vector3(...(s ? s.tower : [0, 0, 0])) };
    shader.uniforms.shopMid = { value: new THREE.Vector3(...(s ? s.mid : [0, 0, 0])) };
    shader.uniforms.shopLow = { value: new THREE.Vector3(...(s ? s.low : [0, 0, 0])) };
    shader.uniforms.shopOdds = { value: new THREE.Vector3(...(s ? s.odds : [0, 0, 0])) };
    shader.uniforms.shopMap = { value: s ? s.map : mat.map };
    shader.uniforms.shopEmissive = { value: s ? s.emissive : mat.emissiveMap };
    shader.uniforms.shopBump = { value: s ? s.bump : mat.bumpMap };
    shader.uniforms.shopRough = { value: s ? s.rough : mat.roughnessMap };
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
        uniform float shopOn;
        uniform vec3 shopTower;
        uniform vec3 shopMid;
        uniform vec3 shopLow;
        uniform vec3 shopOdds;
        varying vec2 vFac;
        varying vec2 vFacSize;
        varying float vFacShop;
        varying float vShopRow;
        varying float vShopShift;
        float shopHash(vec2 p, vec2 k, float m) { return fract(sin(dot(p, k)) * m); }`,
      )
      .replace(
        '#include <uv_vertex>',
        `#include <uv_vertex>
      #ifdef USE_INSTANCING
        vec2 instSize = vec2(
          max(length(instanceMatrix[0].xyz), length(instanceMatrix[2].xyz)),
          length(instanceMatrix[1].xyz)
        );
        vFac = uv * instSize;
        vFacSize = instSize;
        // which buildings have a shop floor, which kind, which bay comes first
        vec2 ip = floor(instanceMatrix[3].xz * 4.0) * 0.25;
        float h1 = shopHash(ip, vec2(12.9898, 78.233), 43758.5453);
        float h2 = shopHash(ip, vec2(39.3468, 11.1351), 24634.6345);
        float h3 = shopHash(ip, vec2(73.1560, 52.2350), 37425.1230);
        float tall = step(${SHOP_TOWER_H.toFixed(1)}, instSize.y);
        float midH = step(${SHOP_MID_H.toFixed(1)}, instSize.y) * (1.0 - tall);
        float lowH = 1.0 - tall - midH;
        vec3 pool = shopTower * tall + shopMid * midH + shopLow * lowH;
        float odds = shopOdds.x * tall + shopOdds.y * midH + shopOdds.z * lowH;
        vFacShop = shopOn * step(${SHOP_MIN_H.toFixed(1)}, instSize.y) * step(h1, odds);
        float pk = h2 * 3.0;
        vShopRow = pk < 1.0 ? pool.x : (pk < 2.0 ? pool.y : pool.z);
        vShopShift = floor(h3 * 4.0) * 0.25;
        vec2 facUv = uv * instSize * 0.13 - vec2(0.0, vFacShop * ${(SHOP_H * 0.13).toFixed(4)});
        #ifdef USE_MAP
          vMapUv = facUv;
        #endif
        #ifdef USE_EMISSIVEMAP
          vEmissiveMapUv = facUv;
        #endif
        #ifdef USE_BUMPMAP
          vBumpMapUv = facUv;
        #endif
        #ifdef USE_ROUGHNESSMAP
          vRoughnessMapUv = facUv;
        #endif
      #else
        vFac = uv;
        vFacSize = vec2(1.0);
        vFacShop = 0.0;
        vShopRow = 0.0;
        vShopShift = 0.0;
      #endif`,
      );
    const H = SHOP_H.toFixed(2);
    const pre = `
      uniform sampler2D shopMap;
      uniform sampler2D shopEmissive;
      uniform sampler2D shopBump;
      uniform sampler2D shopRough;
      uniform float shopRows;
      uniform vec3 streetGlow;
      varying vec2 vFac;
      varying vec2 vFacSize;
      varying float vFacShop;
      varying float vShopRow;
      varying float vShopShift;
      // a tiled facade uv -> world height, the shop atlas uv (this building's
      // row, its own first bay), the shop band
      float facH(vec2 uv) { return uv.y / 0.13 + vFacShop * ${H}; }
      vec2 shopUv(vec2 uv) {
        float f = clamp(facH(uv) / ${H}, 0.01, 0.99);
        return vec2(uv.x * 0.5 + vShopShift, (shopRows - 1.0 - vShopRow + f) / shopRows);
      }
      float shopBand(vec2 uv) { return vFacShop * (1.0 - step(${H}, facH(uv))); }
    `;
    const bumpPars = THREE.ShaderChunk.bumpmap_pars_fragment
      .replace(
        'uniform float bumpScale;',
        `uniform float bumpScale;
        float facadeHeight(vec2 uv) {
          return mix(texture2D(bumpMap, uv).x, texture2D(shopBump, shopUv(uv)).x, shopBand(uv));
        }`,
      )
      .replace(/texture2D\( bumpMap, ([^)]*)\)\.x/g, 'facadeHeight( $1 )');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>${pre}`)
      .replace('#include <bumpmap_pars_fragment>', bumpPars)
      .replace(
        '#include <map_fragment>',
        `float fShop = 0.0;
        #ifdef USE_MAP
          fShop = shopBand(vMapUv);
          vec4 sampledDiffuseColor = texture2D( map, vMapUv );
          vec4 shopTexel = texture2D( shopMap, shopUv( vMapUv ) );
          #if defined( USE_COLOR ) || defined( USE_COLOR_ALPHA )
            // the shopfronts keep their own paint: only a touch of the building tint
            shopTexel.rgb *= mix( vec3( 1.0 ), vColor.rgb, 0.15 ) / max( vColor.rgb, vec3( 0.05 ) );
          #endif
          diffuseColor *= mix( sampledDiffuseColor, shopTexel, fShop );
        #endif`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        {
          float fh = vFac.y;
          float edge = min( vFac.x, vFacSize.x - vFac.x );
          float ao = mix( 0.66, 1.0, smoothstep( 0.0, 2.6, fh ) ); // the ground
          ao *= mix( 0.8, 1.0, smoothstep( 0.0, 1.2, edge ) ); // the corners
          ao *= mix( 0.86, 1.0, smoothstep( 0.0, 1.5, vFacSize.y - fh ) ); // under the parapet
          diffuseColor.rgb *= ao;
          float coping = step( vFacSize.y - 0.3, fh ) * ( 1.0 - fShop );
          diffuseColor.rgb = mix( diffuseColor.rgb, diffuseColor.rgb * 1.18 + 0.02, coping );
        }`,
      )
      // (the includes are still unexpanded here: swap each for its patched chunk)
      .replace(
        '#include <emissivemap_fragment>',
        THREE.ShaderChunk.emissivemap_fragment.replace(
          'vec4 emissiveColor = texture2D( emissiveMap, vEmissiveMapUv );',
          'vec4 emissiveColor = mix( texture2D( emissiveMap, vEmissiveMapUv ), texture2D( shopEmissive, shopUv( vEmissiveMapUv ) ), shopBand( vEmissiveMapUv ) );',
        ) +
          `
        totalEmissiveRadiance += streetGlow * diffuseColor.rgb * ( 1.0 - smoothstep( 0.0, 10.0, vFac.y ) );`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        THREE.ShaderChunk.roughnessmap_fragment.replace(
          'vec4 texelRoughness = texture2D( roughnessMap, vRoughnessMapUv );',
          'vec4 texelRoughness = mix( texture2D( roughnessMap, vRoughnessMapUv ), texture2D( shopRough, shopUv( vRoughnessMapUv ) ), shopBand( vRoughnessMapUv ) );',
        ),
      );
  };
}

const HILL_GEO = new THREE.SphereGeometry(1, 10, 7);

// ---- rio-ts: the Tijuca jungle domes (the sanctioned hill-texture hook) ----
// QA (rio finding 9): the rainforest domes rendered as smooth flat-shaded
// spheres — big visible facets, papercraft at close range, and half-buried
// tree canopies read as "scribble streaks" against the featureless faces.
// Jungle hills now split out of the shared mesh BY PALETTE (the exact
// buildBoxes/MUMBAI_GOTHIC precedent): only Rio pushes RIO_JUNGLE-coloured
// hills — disjointness is test-pinned (tests/world/rioScenery.test.ts) — so
// every other city's hills render through the untouched plain path below,
// and the PLACEMENT arrays are identical everywhere by construction.
const JUNGLE_HILL_SET = new Set<number>(RIO_JUNGLE);

let jungleHillGeoCache: THREE.BufferGeometry | null = null;
/** A denser dome with gentle deterministic radial clump noise. The
 *  displacement is position-derived sines (never rng — layouts stay
 *  byte-identical) and TAPERS TO ZERO at the ground rim, so the dome's
 *  footprint at the rail is exactly `r`: the hills.ts corridor contract
 *  (edge = |lateral| − r) stays honest to the centimetre. Seam/pole
 *  duplicates displace identically (position-only input) — sealed. */
function jungleHillGeo(): THREE.BufferGeometry {
  if (jungleHillGeoCache) return jungleHillGeoCache;
  const g = new THREE.SphereGeometry(1, 20, 13);
  const pos = g.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    if (y <= 0.02) continue; // buried half + ground rim stay exact
    const t = Math.min(1, (y - 0.02) / 0.3);
    const n =
      Math.sin(x * 5.3 + z * 2.1 + 1.7) * Math.sin(z * 4.7 - x * 1.3 + 0.6) * 0.6 +
      Math.sin((x + z) * 7.9 + y * 3.1 + 2.9) * 0.4;
    const k = 1 + n * 0.06 * t;
    pos.setXYZ(i, x * k, y * (1 + n * 0.045 * t), z * k);
  }
  pos.needsUpdate = true;
  jungleHillGeoCache = g;
  return g;
}

let canopyTex: THREE.CanvasTexture | null = null;
/** Canopy-clump tint: overlapping soft crown blobs, each lit top-right and
 *  shaded low-left, in GRAYSCALE so the per-instance jungle palette colour
 *  multiplies through (the roofTexture convention). Doubles as the bump map
 *  (clumps read proud). Drawn wrapped on both axes so the sphere's u-seam
 *  and the RepeatWrapping tiles are seamless. */
function canopyClumpTexture(): THREE.CanvasTexture {
  if (canopyTex) return canopyTex;
  _texSeed = 0x7107;
  const [c, x] = texCanvas(128);
  x.fillStyle = gray(186);
  x.fillRect(0, 0, 128, 128);
  for (let i = 0; i < 64; i++) {
    const bx = texRand() * 128;
    const by = texRand() * 128;
    const r = 8 + texRand() * 13;
    const v = 146 + ((texRand() * 92) | 0);
    x.globalAlpha = 0.9;
    for (const ox of [-128, 0, 128]) {
      for (const oy of [-128, 0, 128]) {
        const g2 = x.createRadialGradient(
          bx + ox + r * 0.28,
          by + oy - r * 0.28,
          r * 0.12,
          bx + ox,
          by + oy,
          r,
        );
        g2.addColorStop(0, gray(Math.min(255, v + 38)));
        g2.addColorStop(0.62, gray(v));
        g2.addColorStop(1, gray(Math.max(0, v - 42)));
        x.fillStyle = g2;
        x.beginPath();
        x.arc(bx + ox, by + oy, r, 0, Math.PI * 2);
        x.fill();
      }
    }
  }
  x.globalAlpha = 1;
  canopyTex = shareTex(new THREE.CanvasTexture(c));
  return canopyTex;
}

/** Tile a map AND its bump twin together by the instance's world x/z size —
 *  clump crowns land at a real ~6u scale on a 22u hill and a 46u one alike,
 *  and (shared-2b) dune/moraine grain and prop stone/plank get the same
 *  size-invariant treatment. Both UVs must come from ONE expression or the
 *  relief slides off the colour (patchGroundTiling only writes vMapUv). */
function patchInstanceTiling(
  mat: THREE.MeshStandardMaterial,
  tile = 0.11,
  world = false,
): void {
  mat.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader.replace(
      '#include <uv_vertex>',
      `#include <uv_vertex>
      #ifdef USE_INSTANCING
        vec2 hillUv = ${
          world
            ? `( modelMatrix * instanceMatrix * vec4( position, 1.0 ) ).xz * ${tile.toFixed(3)}`
            : `uv * vec2(length(instanceMatrix[0].xyz), length(instanceMatrix[2].xyz)) * ${tile.toFixed(3)}`
        };
        #ifdef USE_MAP
          vMapUv = hillUv;
        #endif
        #ifdef USE_BUMPMAP
          vBumpMapUv = hillUv;
        #endif
        #ifdef USE_ROUGHNESSMAP
          vRoughnessMapUv = hillUv;
        #endif
      #endif`,
    );
  };
}

// ---- shared-2b: the BARE-TERRAIN domes (dunes / moraine / snow) ------------
// QA flagged the same thing in Dakar AND Patagonia: the non-jungle hill path
// is `flatShading: true` with NO map, so every dune, moraine bank and snow
// mound rendered as a faceted untextured ball — the "papercraft" read. This is
// the jungle treatment's sibling, selected the same way: BY COLOUR, so the
// placement arrays and every other city are untouched by construction.
//
// The key is the SET being unambiguous. Measured across all 9 cities × 2 seeds
// (tests/world/sharedHills.test.ts pins it): only Dakar pushes DAKAR_SAND,
// only Patagonia pushes MORAINE + the snow triad, and no colour is shared
// between any two cities' hills. NYC's HILL_GREEN and Rio's RIO_JUNGLE stay
// out of it — NYC keeps the plain path, Rio keeps its canopy path.
/** Patagonia's snow-cap triad, a literal in zones/patagonia.ts (the snowline
 *  hills). Kept in sync with that push site. */
const SNOW_HILLS = [0xdfe8ee, 0xd0dde6, 0xc2d2dc];
const TERRAIN_HILL_SET = new Set<number>([...DAKAR_SAND, ...MORAINE, ...SNOW_HILLS]);

/** The colour keys the textured-terrain hill treatment claims (exported for
 *  the disjointness test — the mumbaiGothic/asakusa/londonGothic precedent). */
export const TERRAIN_HILL_COLORS: readonly number[] = [...TERRAIN_HILL_SET];

let terrainHillGeoCache: THREE.BufferGeometry | null = null;
/** A denser, SMOOTH-shaded dome with gentle deterministic swell — dunes and
 *  moraine are rounded landforms, not facets. Same contract as the jungle
 *  dome: the displacement is position-derived (never rng) and TAPERS TO ZERO
 *  at the ground rim, so the hills.ts corridor rule (edge = |lateral| − r)
 *  stays exact, and seam/pole duplicates displace identically (sealed).
 *  The baked Y gradient is the M36 top-light: crest bright, flanks shaded. */
function terrainHillGeo(): THREE.BufferGeometry {
  if (terrainHillGeoCache) return terrainHillGeoCache;
  const g = new THREE.SphereGeometry(1, 24, 14);
  const pos = g.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    if (y <= 0.02) continue;
    const t = Math.min(1, (y - 0.02) / 0.35);
    // Two long wavelengths (the dune/ridge swell) + one short (surface roll).
    const n =
      Math.sin(x * 2.3 - z * 1.7 + 0.9) * 0.62 +
      Math.sin(z * 3.1 + x * 0.8 + 2.4) * 0.3 +
      Math.sin((x - z) * 6.1 + y * 2.2) * 0.16;
    const k = 1 + n * 0.05 * t;
    pos.setXYZ(i, x * k, y * (1 + n * 0.035 * t), z * k);
  }
  pos.needsUpdate = true;
  g.computeVertexNormals(); // smooth: the swell has to read as a landform
  bakeYGradient(g, 0.78, 1.12);
  terrainHillGeoCache = g;
  return g;
}

let terrainGrainTex: THREE.CanvasTexture | null = null;
/** Wind-drift grain for bare terrain: long soft ripples across the slope
 *  (dune combing / snow sastrugi / moraine bedding) + a fine speckle so the
 *  surface never goes glassy in the mip chain. Grayscale — the per-instance
 *  sand/rock/snow colour multiplies through. Doubles as the bump map. */
function terrainGrainTexture(): THREE.CanvasTexture {
  if (terrainGrainTex) return terrainGrainTex;
  _texSeed = 0x7e44;
  const [c, x] = texCanvas(128);
  x.fillStyle = gray(206);
  x.fillRect(0, 0, 128, 128);
  // Broad drifts (seamless: sine phases on integer cycles).
  const img = x.getImageData(0, 0, 128, 128);
  for (let j = 0; j < 128; j++) {
    for (let i = 0; i < 128; i++) {
      const u = (i / 128) * Math.PI * 2;
      const v = (j / 128) * Math.PI * 2;
      const h =
        Math.sin(v * 3 + Math.cos(u * 2) * 1.1) * 18 +
        Math.sin(v * 7 + Math.cos(u * 3) * 0.7) * 9 +
        Math.sin((u + v) * 5) * 5;
      const o = (j * 128 + i) * 4;
      const val = Math.max(0, Math.min(255, 206 + h));
      img.data[o] = img.data[o + 1] = img.data[o + 2] = val;
      img.data[o + 3] = 255;
    }
  }
  x.putImageData(img, 0, 0);
  // Grit speckle over the drifts.
  x.globalAlpha = 0.34;
  for (let i = 0; i < 1400; i++) {
    x.fillStyle = gray(texRand() < 0.5 ? 168 + ((texRand() * 26) | 0) : 226 + ((texRand() * 24) | 0));
    x.fillRect(texRand() * 128, texRand() * 128, 1, 1);
  }
  x.globalAlpha = 1;
  terrainGrainTex = shareTex(new THREE.CanvasTexture(c));
  return terrainGrainTex;
}

// ---- nyc-lake: the Central Park MEADOW domes -------------------------------
// The owner's correction round reverts Central Park's reservoir to "grass and
// hills (texturized well)". The hills are the half of that which was never a
// revert: NYC's park domes were the ONLY thing in the game still rendering
// through the plain untextured `HILL_GEO` sphere, which is literally why the
// previous round measured them as "bare flat kelly-green slabs" — 10×7 facets,
// one flat colour, no map, no bump. A green slab is exactly the M118 "flat dark
// apron" complaint in dome form.
//
// Same mechanism as the jungle + terrain splits, by PALETTE: only NYC pushes
// HILL_GREEN hills (test-pinned in tests/world/sharedHills.test.ts), so the
// placement arrays are untouched by construction and every other city's plain
// path is byte-identical.
const MEADOW_HILL_SET = new Set<number>(HILL_GREEN);

/** The colour keys the meadow treatment claims (exported for the disjointness
 *  test — the TERRAIN_HILL_COLORS precedent). */
export const MEADOW_HILL_COLORS: readonly number[] = [...MEADOW_HILL_SET];

let meadowHillGeoCache: THREE.BufferGeometry | null = null;
/** A parkland KNOLL: dense, smooth-shaded, with a long gentle swell plus a
 *  finer roll. Deliberately shallower than the dune/moraine displacement — a
 *  mown Central Park rise is a soft landform, not a drift. Same two contracts
 *  as its siblings: the displacement is position-derived (never rng, so layouts
 *  stay byte-identical and seam/pole duplicates move together — sealed) and it
 *  TAPERS TO ZERO at the ground rim, so hills.ts's corridor rule
 *  (edge = |lateral| − r) and hills.domeMeshY's tree-lift stay exact. */
function meadowHillGeo(): THREE.BufferGeometry {
  if (meadowHillGeoCache) return meadowHillGeoCache;
  const g = new THREE.SphereGeometry(1, 22, 13);
  const pos = g.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    if (y <= 0.02) continue;
    const t = Math.min(1, (y - 0.02) / 0.4);
    const n =
      Math.sin(x * 1.9 + z * 1.3 + 0.4) * 0.7 +
      Math.sin(z * 2.7 - x * 1.1 + 1.9) * 0.34 +
      Math.sin((x + z) * 5.3 + y * 1.7) * 0.13;
    const k = 1 + n * 0.036 * t;
    pos.setXYZ(i, x * k, y * (1 + n * 0.028 * t), z * k);
  }
  pos.needsUpdate = true;
  g.computeVertexNormals(); // smooth: a knoll must not read as a faceted ball
  bakeYGradient(g, 0.8, 1.1);
  meadowHillGeoCache = g;
  return g;
}

let meadowGrainTex: THREE.CanvasTexture | null = null;
/** Turf for a park slope: broad mown bands (the mower's arcs), soft clumping,
 *  and a blade speckle so a crest never goes glassy in the mip chain.
 *  Grayscale — the per-instance HILL_GREEN multiplies through, and it doubles
 *  as the bump map. Deliberately a DIFFERENT canvas from groundTexture('grass')
 *  (128 px of blade streaks): a dome is seen at 20-40 u across, where the flat
 *  apron's fine streaks vanish and only the broad banding reads. */
function meadowGrainTexture(): THREE.CanvasTexture {
  if (meadowGrainTex) return meadowGrainTex;
  _texSeed = 0x3c19;
  const [c, x] = texCanvas(128);
  // Broad mown banding + clumping (seamless: integer sine cycles).
  const img = x.getImageData(0, 0, 128, 128);
  for (let j = 0; j < 128; j++) {
    for (let i = 0; i < 128; i++) {
      const u = (i / 128) * Math.PI * 2;
      const v = (j / 128) * Math.PI * 2;
      const h =
        Math.sin(u * 2 + Math.sin(v * 2) * 0.6) * 13 +
        Math.sin(v * 5 + Math.cos(u * 3) * 0.8) * 8 +
        Math.sin((u - v) * 8) * 4;
      const o = (j * 128 + i) * 4;
      const val = Math.max(0, Math.min(255, 200 + h));
      img.data[o] = img.data[o + 1] = img.data[o + 2] = val;
      img.data[o + 3] = 255;
    }
  }
  x.putImageData(img, 0, 0);
  // Soft darker clumps (uneven turf), then a blade speckle over the top.
  for (let i = 0; i < 20; i++) {
    x.globalAlpha = 0.14;
    x.fillStyle = gray(150 + ((texRand() * 46) | 0));
    x.beginPath();
    x.arc(texRand() * 128, texRand() * 128, 9 + texRand() * 22, 0, Math.PI * 2);
    x.fill();
  }
  x.globalAlpha = 0.42;
  for (let i = 0; i < 900; i++) {
    x.strokeStyle = gray(texRand() < 0.5 ? 158 + ((texRand() * 38) | 0) : 216 + ((texRand() * 30) | 0));
    x.lineWidth = 1;
    const bx = texRand() * 128;
    const by = texRand() * 128;
    x.beginPath();
    x.moveTo(bx, by);
    x.lineTo(bx + (texRand() - 0.5) * 2, by - 2 - texRand() * 2.5);
    x.stroke();
  }
  x.globalAlpha = 1;
  meadowGrainTex = shareTex(new THREE.CanvasTexture(c));
  return meadowGrainTex;
}

/** One instanced dome field (the pre-split buildHills body). */
function hillMesh(insts: HillInst[], geo: THREE.BufferGeometry, mat: THREE.MeshStandardMaterial): THREE.InstancedMesh {
  const mesh = new THREE.InstancedMesh(geo, mat, insts.length);
  insts.forEach((h, i) => {
    P.set(h.x, h.y, h.z); // centre at ground → the top hemisphere is the mound
    S.set(h.r, h.h, h.r);
    mesh.setMatrixAt(i, M.compose(P, ID_Q, S));
    mesh.setColorAt(i, C.set(h.color));
  });
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  return mesh;
}

/** Rolling hills (half-buried domes). One draw call for a city whose hills
 *  are all one family — two when a city mixes families (Rio's jungle canopy
 *  domes beside plain ones). Selection is BY COLOUR, so nothing about where
 *  the hills sit changes: the same instances land in the same places, split
 *  across meshes. */
export function buildHills(insts: HillInst[]): THREE.Object3D {
  const plainMat = (): THREE.MeshStandardMaterial =>
    new THREE.MeshStandardMaterial({ flatShading: true, roughness: 1 });
  const jungle = insts.filter((h) => JUNGLE_HILL_SET.has(h.color));
  const terrain = insts.filter((h) => TERRAIN_HILL_SET.has(h.color));
  const meadow = insts.filter((h) => MEADOW_HILL_SET.has(h.color));
  const rest = insts.filter(
    (h) =>
      !JUNGLE_HILL_SET.has(h.color) &&
      !TERRAIN_HILL_SET.has(h.color) &&
      !MEADOW_HILL_SET.has(h.color),
  );
  if (!jungle.length && !terrain.length && !meadow.length) {
    return hillMesh(insts, HILL_GEO, plainMat());
  }

  const parts: THREE.Object3D[] = [];
  if (jungle.length) {
    const jungleMat = new THREE.MeshStandardMaterial({
      flatShading: true,
      roughness: 1,
      map: canopyClumpTexture(),
      bumpMap: canopyClumpTexture(),
      bumpScale: 0.7,
    });
    patchInstanceTiling(jungleMat);
    parts.push(hillMesh(jungle, jungleHillGeo(), jungleMat));
  }
  if (terrain.length) {
    const terrainMat = new THREE.MeshStandardMaterial({
      roughness: 1,
      vertexColors: true, // the baked crest→flank gradient × the instance colour
      map: terrainGrainTexture(),
      bumpMap: terrainGrainTexture(),
      bumpScale: 0.9,
    });
    // WORLD-XZ UVs, not the sphere's own (MEASURED): a dome's uv seam+pole
    // converge at its apex, and a low-frequency drift map smears there into a
    // radial STARBURST — plainly visible looking down on a Patagonia moraine
    // dome. A top-down world projection has no singularity on a landform that
    // is mostly horizontal, and it makes the drift field CONTINUOUS across
    // overlapping domes (the patchGroundTiling `world` lesson).
    patchInstanceTiling(terrainMat, 0.055, true);
    parts.push(hillMesh(terrain, terrainHillGeo(), terrainMat));
  }
  if (meadow.length) {
    const meadowMat = new THREE.MeshStandardMaterial({
      roughness: 1,
      vertexColors: true, // the baked crest→flank gradient × the instance colour
      map: meadowGrainTexture(),
      bumpMap: meadowGrainTexture(),
      bumpScale: 0.6,
    });
    // WORLD-XZ UVs for the same measured reason as the terrain domes: a
    // sphere's uv seam + pole converge at the apex and smear a low-frequency
    // map into a radial starburst, and a top-down projection also makes the
    // turf CONTINUOUS across the park's overlapping knolls.
    patchInstanceTiling(meadowMat, 0.07, true);
    parts.push(hillMesh(meadow, meadowHillGeo(), meadowMat));
  }
  if (rest.length) parts.push(hillMesh(rest, HILL_GEO, plainMat()));
  if (parts.length === 1) return parts[0];
  const group = new THREE.Group();
  for (const p of parts) group.add(p);
  return group;
}

let cachedAdTextures: THREE.CanvasTexture[] | null = null;
/**
 * Ad-screen textures for the Times Square board mosaic (and Tokyo's Shibuya
 * screens). Indices 0-6 are the ABSTRACT patterns — shapes only, no words;
 * 7-11 are the BRAND boards below.
 *
 * ORDER IS A CONTRACT. Tokyo picks its screen with `rng.int(0, 6)`
 * (zones/tokyo.ts), so it can only ever draw the abstract seven — appending
 * the brand boards leaves every Tokyo screen byte-identical. NYC's mosaic
 * picker spreads across the whole set via AD_TEX_COUNT in zones.ts; that
 * constant must equal this array's length.
 */
export function adTextures(): THREE.CanvasTexture[] {
  if (cachedAdTextures) return cachedAdTextures;
  cachedAdTextures = [
    makeAdTexture('#e0186a', '#ffffff', 'rings'),
    makeAdTexture('#1850e0', '#ffe23c', 'stripes'),
    makeAdTexture('#12c06a', '#08130c', 'dots'),
    makeAdTexture('#7a18e0', '#ffffff', 'chevrons'),
    makeAdTexture('#ff8a18', '#241204', 'grid'),
    makeAdTexture('#0d1018', '#18d0ff', 'triangles'),
    makeAdTexture('#ff2d6a', '#fff0a0', 'burst'),
    ...BRAND_ADS.map(makeBrandAdTexture),
  ];
  return cachedAdTextures;
}
function makeAdTexture(bg: string, fg: string, kind: string): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  // tokyo-ts: one resolution notch up (128×192 → 256×384) — the giant
  // Shibuya screens read soft at chase distance. The painter below still
  // draws in the original 128×192 space via the 2× context scale.
  c.width = 256;
  c.height = 384;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('Scenery: no 2D context for ad texture');
  ctx.scale(2, 2);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, 128, 192);
  ctx.fillStyle = fg;
  ctx.strokeStyle = fg;
  if (kind === 'rings') {
    ctx.lineWidth = 9;
    for (let r = 14; r < 120; r += 20) {
      ctx.beginPath();
      ctx.arc(64, 96, r, 0, Math.PI * 2);
      ctx.stroke();
    }
  } else if (kind === 'stripes') {
    ctx.lineWidth = 15;
    for (let i = -2; i < 12; i++) {
      ctx.beginPath();
      ctx.moveTo(i * 26, 0);
      ctx.lineTo(i * 26 + 90, 192);
      ctx.stroke();
    }
  } else if (kind === 'dots') {
    for (let y = 0; y < 6; y++)
      for (let x = 0; x < 4; x++) {
        ctx.beginPath();
        ctx.arc(20 + x * 30, 22 + y * 31, 10, 0, Math.PI * 2);
        ctx.fill();
      }
  } else if (kind === 'chevrons') {
    ctx.lineWidth = 13;
    for (let y = 6; y < 200; y += 30) {
      ctx.beginPath();
      ctx.moveTo(8, y);
      ctx.lineTo(64, y + 26);
      ctx.lineTo(120, y);
      ctx.stroke();
    }
  } else if (kind === 'grid') {
    for (let y = 0; y < 6; y++)
      for (let x = 0; x < 4; x++)
        if ((x + y) % 2 === 0) ctx.fillRect(8 + x * 30, 10 + y * 31, 24, 24);
  } else if (kind === 'burst') {
    for (let a = 0; a < 12; a++) {
      ctx.save();
      ctx.translate(64, 96);
      ctx.rotate((a / 12) * Math.PI * 2);
      ctx.fillRect(-5, 18, 10, 60);
      ctx.restore();
    }
    ctx.beginPath();
    ctx.arc(64, 96, 16, 0, Math.PI * 2);
    ctx.fill();
  } else {
    // triangles
    for (let i = 0; i < 6; i++) {
      const y = 8 + i * 30;
      ctx.beginPath();
      ctx.moveTo(64, y);
      ctx.lineTo(112, y + 26);
      ctx.lineTo(16, y + 26);
      ctx.closePath();
      ctx.fill();
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

// ---- Brand boards (Times Square) ------------------------------------------
//
// The mosaic gave every tower a stack of panels, but every panel wore an
// abstract test pattern, so the square still read as a corridor of colour
// swatches rather than advertising. These are the ADS: a product shape and a
// wordmark, in invented game-universe brands.
//
// NO REAL TRADEMARKS. Every name, strap line and mark here is made up for this
// game; nothing traces a real logo or slogan.
//
// THREE MEASURED CONSTRAINTS SHAPE THE ART:
//
//  1. ASPECT. buildAdScreens maps the texture's u across a board's WIDTH and v
//     up its HEIGHT with no aspect correction, and the mosaic's slots run from
//     ~14:1 tall columns (pattern 2's corner column on a 50-u tower) to ~2.2:1
//     wide boards (pattern 1's top board on a short, deep one). On the
//     abstract boards' 2:3 portrait canvas a wide slot smears the art 3.3x
//     horizontally — which is survivable for stripes and fatal for type.
//     These are SQUARE, which halves that worst case to 2.2x; the cost is
//     taller letters on the columns, and tall heavy letters still read as
//     signage where wide-smeared thin ones do not.
//  2. COMPOSITION follows from 1: BANDS, never a centred motif. A kicker strip
//     at the top, a field carrying an icon and a horizontal wordmark, a plinth
//     strap at the bottom. Stretched wide it reads as a billboard; stretched
//     tall it reads as a vertical banner. Nothing load-bearing is a circle —
//     a circle becomes an ellipse the moment a board is not square, so the
//     tyre brand wears a tread BLOCK and the fuel brand a numeral PLATE.
//  3. NO PURE WHITE. On the sign material (MeshBasic, toneMapped:false) a
//     255-white panel blows out into a featureless slab — the trap the Tokyo
//     glyph columns hit, and the NYC round measured it again at board size,
//     where one accent panel read as a HOLE punched in the facade. Paper white
//     (#efe6d4 and friends) is the ceiling here; nothing exceeds 0xef.

type AdIcon = 'bottle' | 'can' | 'plate' | 'boot' | 'tread';

interface BrandAd {
  /** The field behind the lockup. */
  bg: string;
  /** Kicker strip + plinth colour. */
  band: string;
  /** Small type on the bands. */
  bandInk: string;
  /** The wordmark on the field. */
  ink: string;
  icon: AdIcon;
  iconInk: string;
  /** Optional glyph for the 'plate' icon. */
  glyph?: string;
  kicker: string;
  /** Split on spaces into at most two stacked lines. */
  word: string;
  strap: string;
}

const BRAND_ADS: BrandAd[] = [
  {
    bg: '#a8121f',
    band: '#efe6d4',
    bandInk: '#8c0e19',
    ink: '#efe6d4',
    icon: 'bottle',
    iconInk: '#efe6d4',
    kicker: 'ICE COLD',
    word: 'DRIBBLE COLA',
    strap: 'TASTE THE SPEED',
  },
  {
    bg: '#0d1428',
    band: '#18c8e0',
    bandInk: '#06121c',
    ink: '#dfeef4',
    icon: 'can',
    iconInk: '#18c8e0',
    kicker: 'FUEL YOUR RUN',
    word: 'RACER ENERGY',
    strap: 'DOUBLE SHOT',
  },
  {
    bg: '#e07a12',
    band: '#1a1206',
    bandInk: '#e8b24a',
    ink: '#1a1206',
    icon: 'plate',
    iconInk: '#1a1206',
    glyph: '9',
    kicker: 'FILL UP',
    word: 'TURBO',
    strap: 'HIGH OCTANE',
  },
  {
    bg: '#0f6a3a',
    band: '#e8b23a',
    bandInk: '#123021',
    ink: '#efe6d4',
    icon: 'boot',
    iconInk: '#e8b23a',
    kicker: 'PRO STUDS',
    word: 'KICK STAR',
    strap: 'OWN THE PITCH',
  },
  {
    bg: '#171a20',
    band: '#d81e6a',
    bandInk: '#efe6d4',
    ink: '#efe6d4',
    icon: 'tread',
    iconInk: '#d81e6a',
    kicker: 'GRIP TESTED',
    word: 'NOVA TYRES',
    strap: 'STICK THE LINE',
  },
];

/** Heavy uppercase, auto-shrunk to fit `maxW`, optionally letter-tracked. */
function adText(
  x: CanvasRenderingContext2D,
  s: string,
  cx: number,
  cy: number,
  maxW: number,
  px: number,
  track = 0,
): void {
  const fam = '"Arial Black", "Arial Bold", Arial, sans-serif';
  let size = px;
  const width = (): number => {
    x.font = `900 ${size}px ${fam}`;
    return x.measureText(s).width + track * Math.max(0, s.length - 1);
  };
  while (width() > maxW && size > 3) size -= 0.5;
  x.textBaseline = 'middle';
  if (track === 0) {
    x.textAlign = 'center';
    x.fillText(s, cx, cy);
    return;
  }
  x.textAlign = 'left';
  let px2 = cx - width() / 2;
  for (const ch of s) {
    x.fillText(ch, px2, cy);
    px2 += x.measureText(ch).width + track;
  }
}

/** Blocky product silhouettes — no circles carry identity (see note 2 above). */
function adIcon(x: CanvasRenderingContext2D, a: BrandAd, bx: number, by: number, bw: number, bh: number): void {
  x.fillStyle = a.iconInk;
  const cx = bx + bw / 2;
  if (a.icon === 'bottle') {
    x.fillRect(cx - bw * 0.15, by, bw * 0.3, bh * 0.08); // cap
    x.fillRect(cx - bw * 0.1, by + bh * 0.08, bw * 0.2, bh * 0.14); // neck
    x.beginPath(); // shoulder
    x.moveTo(cx - bw * 0.1, by + bh * 0.22);
    x.lineTo(cx + bw * 0.1, by + bh * 0.22);
    x.lineTo(cx + bw * 0.32, by + bh * 0.4);
    x.lineTo(cx - bw * 0.32, by + bh * 0.4);
    x.closePath();
    x.fill();
    x.fillRect(cx - bw * 0.32, by + bh * 0.4, bw * 0.64, bh * 0.6); // body
    x.fillStyle = a.bg; // label band knocked out of the body
    x.fillRect(cx - bw * 0.32, by + bh * 0.56, bw * 0.64, bh * 0.2);
  } else if (a.icon === 'can') {
    x.fillRect(cx - bw * 0.34, by + bh * 0.06, bw * 0.68, bh * 0.06); // top rim
    x.fillRect(cx - bw * 0.28, by + bh * 0.12, bw * 0.56, bh * 0.76); // body
    x.fillRect(cx - bw * 0.34, by + bh * 0.88, bw * 0.68, bh * 0.06); // base rim
    x.fillStyle = a.bg; // bolt knocked out of the body
    x.beginPath();
    x.moveTo(cx + bw * 0.12, by + bh * 0.2);
    x.lineTo(cx - bw * 0.06, by + bh * 0.52);
    x.lineTo(cx + bw * 0.05, by + bh * 0.52);
    x.lineTo(cx - bw * 0.1, by + bh * 0.82);
    x.lineTo(cx + bw * 0.16, by + bh * 0.46);
    x.lineTo(cx + bw * 0.04, by + bh * 0.46);
    x.closePath();
    x.fill();
  } else if (a.icon === 'plate') {
    x.fillRect(bx, by + bh * 0.1, bw, bh * 0.8); // number plate
    x.fillStyle = a.bg;
    adText(x, a.glyph ?? '9', cx, by + bh * 0.5, bw * 0.82, bh * 0.78);
  } else if (a.icon === 'boot') {
    x.beginPath(); // upper + sole in one blocky boot profile
    x.moveTo(bx + bw * 0.06, by + bh * 0.4);
    x.lineTo(bx + bw * 0.5, by + bh * 0.28);
    x.lineTo(bx + bw * 0.96, by + bh * 0.52);
    x.lineTo(bx + bw * 0.96, by + bh * 0.72);
    x.lineTo(bx + bw * 0.06, by + bh * 0.72);
    x.closePath();
    x.fill();
    x.fillStyle = a.bg; // lace slash
    x.fillRect(bx + bw * 0.32, by + bh * 0.34, bw * 0.06, bh * 0.3);
    x.fillStyle = a.iconInk;
    for (let i = 0; i < 4; i++) x.fillRect(bx + bw * (0.14 + i * 0.21), by + bh * 0.74, bw * 0.11, bh * 0.1); // studs
  } else {
    x.fillRect(bx, by + bh * 0.16, bw, bh * 0.68); // tread block
    x.fillStyle = a.bg;
    for (let i = 0; i < 5; i++) x.fillRect(bx + bw * (0.08 + i * 0.19), by + bh * 0.16, bw * 0.09, bh * 0.68); // grooves
    x.fillStyle = a.iconInk;
    x.fillRect(bx, by + bh * 0.06, bw, bh * 0.08); // sidewall
    x.fillRect(bx, by + bh * 0.86, bw, bh * 0.08);
  }
}

function makeBrandAdTexture(a: BrandAd): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  // 256x256 device, painted in a 128x128 space at 2x — square by design
  // (see the aspect note above) and cheaper than the abstract boards' 256x384.
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('Scenery: no 2D context for brand ad texture');
  ctx.scale(2, 2);
  ctx.fillStyle = a.bg;
  ctx.fillRect(0, 0, 128, 128);
  // Kicker strip.
  ctx.fillStyle = a.band;
  ctx.fillRect(0, 0, 128, 19);
  ctx.fillStyle = a.bandInk;
  adText(ctx, a.kicker, 64, 10, 116, 11, 1.6);
  // Plinth strap.
  ctx.fillStyle = a.band;
  ctx.fillRect(0, 100, 128, 28);
  ctx.fillStyle = a.bandInk;
  adText(ctx, a.strap, 64, 113, 118, 12, 0.8);
  // Field: product left, wordmark right.
  adIcon(ctx, a, 7, 28, 33, 64);
  ctx.fillStyle = a.ink;
  const parts = a.word.split(' ');
  if (parts.length > 1) {
    adText(ctx, parts[0], 85, 47, 76, 21);
    adText(ctx, parts.slice(1).join(' '), 85, 74, 76, 21);
  } else {
    adText(ctx, parts[0], 85, 60, 76, 30);
  }
  // Panel joints: the boards are modular LED cabinets, and the grid is what
  // sells "screen" over "painted sign" up close. Kept coarse (16 px = one
  // cabinet) — a fine grid moires against the mipmap chain at mid distance.
  ctx.globalAlpha = 0.2;
  ctx.fillStyle = '#05070c';
  for (let gx = 16; gx < 128; gx += 16) ctx.fillRect(gx, 0, 1, 128);
  for (let gy = 16; gy < 128; gy += 16) ctx.fillRect(0, gy, 128, 1);
  ctx.globalAlpha = 1;
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** Giant glowing ad-screens on building faces, grouped per texture so it's a
 *  few draw calls. Each is a thin panel facing the road. */
export function buildAdScreens(insts: AdScreenInst[]): THREE.InstancedMesh[] {
  const texs = adTextures();
  const out: THREE.InstancedMesh[] = [];
  for (let ti = 0; ti < texs.length; ti++) {
    const group = insts.filter((a) => a.tex === ti);
    if (!group.length) continue;
    const mat = new THREE.MeshBasicMaterial({ map: texs[ti], toneMapped: false });
    const mesh = new THREE.InstancedMesh(UNIT_BOX, mat, group.length);
    group.forEach((a, i) => {
      P.set(a.x, a.y, a.z);
      S.set(0.5, a.h, a.w); // thin in X → the big faces front the street
      mesh.setMatrixAt(i, M.compose(P, yawQ(a.rotY), S));
    });
    mesh.instanceMatrix.needsUpdate = true;
    out.push(mesh);
  }
  return out;
}

let cachedGlyphTextures: THREE.CanvasTexture[] | null = null;
/** Vertical neon sign-columns: abstract glyph-like strokes (no real characters),
 *  bright sign colours — the Tokyo back-street look. */
export function glyphTextures(): THREE.CanvasTexture[] {
  if (cachedGlyphTextures) return cachedGlyphTextures;
  const palettes: [string, string][] = [
    ['#d8102a', '#fff6e8'],
    ['#0a0d18', '#18d0ff'],
    ['#f5e62a', '#1a1408'],
    // tokyo-ts: was pure '#ffffff' — on the toneMapped:false sign material a
    // 255-white column BLOOMS into a featureless blown slab at close range
    // (shibuya-tower-road QA shot). Warm paper keeps the red-on-white read.
    ['#efe6d4', '#d8102a'],
    ['#0a1228', '#ff4cf0'],
  ];
  cachedGlyphTextures = palettes.map(([bg, fg], pi) => {
    const c = document.createElement('canvas');
    // tokyo-ts: one resolution notch up (48×192 → 96×384), drawn in the
    // original space via the 2× context scale — crisper glyph strokes.
    c.width = 96;
    c.height = 384;
    const ctx = c.getContext('2d');
    if (!ctx) throw new Error('Scenery: no 2D context for glyph texture');
    ctx.scale(2, 2);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, 48, 192);
    ctx.strokeStyle = fg;
    ctx.fillStyle = fg;
    ctx.lineWidth = 5;
    ctx.lineCap = 'round';
    // 4 stacked pseudo-glyphs built from deterministic stroke patterns.
    for (let gy = 0; gy < 4; gy++) {
      const oy = 10 + gy * 46;
      const v = (pi * 7 + gy * 13) % 5;
      ctx.beginPath();
      if (v === 0) {
        ctx.moveTo(10, oy + 4);
        ctx.lineTo(38, oy + 4);
        ctx.moveTo(24, oy + 4);
        ctx.lineTo(24, oy + 34);
        ctx.moveTo(12, oy + 22);
        ctx.lineTo(36, oy + 22);
      } else if (v === 1) {
        ctx.moveTo(12, oy + 6);
        ctx.lineTo(36, oy + 6);
        ctx.lineTo(36, oy + 34);
        ctx.lineTo(12, oy + 34);
        ctx.closePath();
        ctx.moveTo(12, oy + 20);
        ctx.lineTo(36, oy + 20);
      } else if (v === 2) {
        ctx.moveTo(24, oy + 2);
        ctx.lineTo(10, oy + 34);
        ctx.moveTo(24, oy + 2);
        ctx.lineTo(38, oy + 34);
        ctx.moveTo(15, oy + 22);
        ctx.lineTo(33, oy + 22);
      } else if (v === 3) {
        ctx.moveTo(10, oy + 8);
        ctx.lineTo(38, oy + 8);
        ctx.moveTo(14, oy + 8);
        ctx.lineTo(14, oy + 34);
        ctx.moveTo(34, oy + 8);
        ctx.lineTo(34, oy + 34);
        ctx.moveTo(14, oy + 34);
        ctx.lineTo(34, oy + 34);
      } else {
        ctx.moveTo(12, oy + 4);
        ctx.lineTo(36, oy + 16);
        ctx.lineTo(12, oy + 28);
        ctx.moveTo(24, oy + 16);
        ctx.lineTo(24, oy + 36);
      }
      ctx.stroke();
    }
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  });
  return cachedGlyphTextures;
}

/** Vertical glowing sign columns mounted off building faces (Tokyo). */
export function buildVertSigns(insts: AdScreenInst[]): THREE.InstancedMesh[] {
  const texs = glyphTextures();
  const out: THREE.InstancedMesh[] = [];
  for (let ti = 0; ti < texs.length; ti++) {
    const group = insts.filter((a) => a.tex === ti);
    if (!group.length) continue;
    const mat = new THREE.MeshBasicMaterial({ map: texs[ti], toneMapped: false });
    const mesh = new THREE.InstancedMesh(UNIT_BOX, mat, group.length);
    group.forEach((a, i) => {
      P.set(a.x, a.y, a.z);
      S.set(0.45, a.h, a.w);
      mesh.setMatrixAt(i, M.compose(P, yawQ(a.rotY), S));
    });
    mesh.instanceMatrix.needsUpdate = true;
    out.push(mesh);
  }
  return out;
}

let cachedWaveTexture: THREE.CanvasTexture | null = null;
/** The Copacabana promenade: the famous black-and-white Burle Marx wave. */
function waveTexture(): THREE.CanvasTexture {
  if (cachedWaveTexture) return cachedWaveTexture;
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 64;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('Scenery: no 2D context for wave texture');
  ctx.fillStyle = '#e8e4d8';
  ctx.fillRect(0, 0, 128, 64);
  ctx.strokeStyle = '#181a1c';
  ctx.lineWidth = 11;
  for (const oy of [-22, 10, 42, 74]) {
    ctx.beginPath();
    for (let x = 0; x <= 128; x += 4) {
      const y = oy + Math.sin((x / 128) * Math.PI * 2) * 14;
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(1, 4); // small waves flowing along the band
  tex.colorSpace = THREE.SRGBColorSpace;
  cachedWaveTexture = tex;
  return tex;
}

/** Patterned pavement band (flat, textured). */
export function buildPromenade(insts: FlatInst[]): THREE.InstancedMesh {
  const mat = new THREE.MeshStandardMaterial({ map: waveTexture(), roughness: 0.9 });
  const mesh = new THREE.InstancedMesh(UNIT_BOX, mat, insts.length);
  insts.forEach((f, i) => {
    P.set(f.x, f.y + 0.06, f.z);
    S.set(f.w, 0.1, f.d);
    mesh.setMatrixAt(i, M.compose(P, yawQ(f.rotY), S));
  });
  mesh.instanceMatrix.needsUpdate = true;
  return mesh;
}

const CAB_BODY = new THREE.BoxGeometry(1.9, 1.0, 4.2);
const CAB_CABIN = new THREE.BoxGeometry(1.7, 0.85, 2.2);

/** Low-poly parked cars / cabs as two instanced meshes (body + cabin).
 *  cabinColor: dark glass by default; e.g. yellow for Mumbai's kaali-peeli. */
export function buildCabs(insts: CarInst[], cabinColor = 0x171a22): THREE.InstancedMesh[] {
  const bodyMat = new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.55 });
  const cabinMat = new THREE.MeshStandardMaterial({
    color: cabinColor,
    flatShading: true,
    roughness: 0.4,
  });
  const bodies = new THREE.InstancedMesh(CAB_BODY, bodyMat, insts.length);
  const cabins = new THREE.InstancedMesh(CAB_CABIN, cabinMat, insts.length);
  const sc = S.set(1, 1, 1);
  insts.forEach((car, i) => {
    const q = yawQ(car.rotY);
    bodies.setMatrixAt(i, M.compose(P.set(car.x, car.y + 0.65, car.z), q, sc));
    bodies.setColorAt(i, C.set(car.color));
    cabins.setMatrixAt(i, M.compose(P.set(car.x, car.y + 1.35, car.z), q, sc));
  });
  bodies.instanceMatrix.needsUpdate = true;
  if (bodies.instanceColor) bodies.instanceColor.needsUpdate = true;
  cabins.instanceMatrix.needsUpdate = true;
  return [bodies, cabins];
}

const BUS_BODY = new THREE.BoxGeometry(2.5, 3.7, 9);
const BUS_WHEEL = new THREE.CylinderGeometry(0.48, 0.48, 0.32, 10);
const BUS_HUB = new THREE.CylinderGeometry(0.22, 0.22, 0.34, 8);

/** Double-decker canvas sets cached per route-board string (mumbai-nits) —
 *  London and Mumbai share the decker painters but wear different blinds;
 *  repainting an identical sign per city load would be waste. */
const deckerSetCache = new Map<string, ReturnType<typeof doubleDeckerCanvases>>();
function deckerCanvasSet(route: string, dest: string): ReturnType<typeof doubleDeckerCanvases> {
  const key = `${route} ${dest}`;
  let set = deckerSetCache.get(key);
  if (!set) {
    set = doubleDeckerCanvases({ route, dest, seed: 11 });
    deckerSetCache.set(key, set);
  }
  return set;
}

/**
 * Parked buses (GS4b) — London's most-repeated rudimentary object, rebuilt on
 * the shared propKit canvases: painted flanks (two lit window decks, mullions,
 * route board), a distinct FRONT (windscreen, destination blind, headlamps),
 * open rear, lighter roof cap, and real wheel cylinders in the painted
 * arches. Lagos' danfos ride the same builder with their own canvas set
 * (yellow, black stripe, heavy grime). Still 3 instanced meshes per colour
 * group (body / tyres / hubs) — each city only ever places ONE colour.
 * mumbai-nits: a fleet may carry a city-true route SIGN (PlaceInst.sign —
 * Mumbai's BEST "1 · COLABA"); the grouping key includes it so a signed fleet
 * never shares a canvas set with the London default.
 */
export function buildBuses(insts: PlaceInst[]): THREE.InstancedMesh[] {
  const out: THREE.InstancedMesh[] = [];
  // Group by colour + sign so the canvas set matches the fleet (per-city this
  // is a single group; the split costs nothing and keeps the danfo yellow and
  // each city's destination blind honest).
  const groups = new Map<string, { color: number; sign?: { route: string; dest: string }; list: PlaceInst[] }>();
  for (const b of insts) {
    const color = b.color ?? 0xc81f2a;
    const key = `${color}|${b.sign ? `${b.sign.route} ${b.sign.dest}` : ''}`;
    const g = groups.get(key);
    if (g) g.list.push(b);
    else groups.set(key, { color, sign: b.sign, list: [b] });
  }
  for (const { color, sign, list } of groups.values()) {
    const danfo = color === 0xf2c422; // palettes.DANFO_YELLOW
    const css = `#${color.toString(16).padStart(6, '0')}`;
    const set = danfo
      ? {
          side: vehicleSideCanvas({
            w: 512,
            h: 256,
            paint: css,
            skirt: '#6a5410',
            bands: [{ top: 0.22, height: 0.3, panes: 5, lit: 0.35, warm: true }],
            stripes: [{ y: 0.58, h: 0.09, color: '#191512' }], // the Lagos black stripe
            grime: 0.55,
            arches: [0.2, 0.8],
            archR: 0.14,
            seed: 31,
          }),
          front: vehicleFrontCanvas({
            w: 128,
            h: 128,
            paint: css,
            screens: [{ top: 0.2, height: 0.28, split: 2 }],
            lamps: { y: 0.72, r: 0.05, spread: 0.6 },
            bumper: { h: 0.12 },
            grille: { y: 0.58, h: 0.1 },
            grime: 0.5,
            seed: 32,
          }),
          rear: vehicleFrontCanvas({
            w: 128,
            h: 128,
            paint: css,
            screens: [{ top: 0.22, height: 0.26, split: 1, inset: 0.14 }],
            lamps: { y: 0.78, r: 0.045, spread: 0.62, color: '#ff3540' },
            bumper: { h: 0.12 },
            grime: 0.6,
            seed: 33,
          }),
          roof: vehicleRoofCanvas({ paint: '#d8c9a8', vents: 1, seams: 2, grime: 0.5, seed: 34 }),
        }
      : deckerCanvasSet(sign?.route ?? '23', sign?.dest ?? 'WESTMINSTER');
    const mats = paintedBoxMaterials({
      side: set.side,
      front: set.front,
      rear: set.rear,
      roof: set.roof,
      roughness: 0.55,
      emissiveIntensity: 0.5,
    });
    const h = danfo ? 2.6 : 3.7;
    const bodies = new THREE.InstancedMesh(
      danfo ? new THREE.BoxGeometry(2.4, 2.6, 6.2) : BUS_BODY,
      mats,
      list.length,
    );
    const tyres = new THREE.InstancedMesh(
      BUS_WHEEL,
      new THREE.MeshStandardMaterial({ color: 0x17181c, roughness: 0.92 }),
      list.length * 4,
    );
    const hubs = new THREE.InstancedMesh(
      BUS_HUB,
      new THREE.MeshStandardMaterial({ color: 0x9aa1ab, roughness: 0.35, metalness: 0.6 }),
      list.length * 4,
    );
    const sc = S.set(1, 1, 1);
    const axleZ = danfo ? 2.0 : 2.9; // matches the painted arch fractions
    list.forEach((b, i) => {
      const q = yawQ(b.rotY);
      // Body lifted so the painted arches meet the wheels below.
      bodies.setMatrixAt(i, M.compose(P.set(b.x, b.y + h / 2 + 0.32, b.z), q, sc));
      const cos = Math.cos(b.rotY);
      const sin = Math.sin(b.rotY);
      let w = 0;
      for (const wx of [-1.12, 1.12]) {
        for (const wz of [axleZ, -axleZ]) {
          // Local (wx, wz) → world under the bus yaw (§6.1 right/forward).
          P.set(b.x + cos * wx + sin * wz, b.y + 0.48, b.z - sin * wx + cos * wz);
          E.set(0, b.rotY, Math.PI / 2); // cylinder axis across the bus
          Q.setFromEuler(E);
          const xf = M.compose(P, Q, sc);
          tyres.setMatrixAt(i * 4 + w, xf);
          hubs.setMatrixAt(i * 4 + w, xf);
          w++;
        }
      }
    });
    bodies.instanceMatrix.needsUpdate = true;
    tyres.instanceMatrix.needsUpdate = true;
    hubs.instanceMatrix.needsUpdate = true;
    out.push(bodies, tyres, hubs);
  }
  return out;
}

const PHONE_BODY = new THREE.BoxGeometry(1.25, 2.9, 1.25);
const PHONE_ROOF = new THREE.BoxGeometry(1.45, 0.45, 1.45);

/** Classic red phone boxes (body + roof cap). */
export function buildPhoneBoxes(insts: PlaceInst[]): THREE.InstancedMesh[] {
  const redMat = new THREE.MeshStandardMaterial({
    color: 0xc4151f,
    flatShading: true,
    roughness: 0.5,
  });
  const bodies = new THREE.InstancedMesh(PHONE_BODY, redMat, insts.length);
  const roofs = new THREE.InstancedMesh(PHONE_ROOF, redMat, insts.length);
  const sc = S.set(1, 1, 1);
  insts.forEach((b, i) => {
    const q = yawQ(b.rotY);
    bodies.setMatrixAt(i, M.compose(P.set(b.x, b.y + 1.45, b.z), q, sc));
    roofs.setMatrixAt(i, M.compose(P.set(b.x, b.y + 3.1, b.z), q, sc));
  });
  bodies.instanceMatrix.needsUpdate = true;
  roofs.instanceMatrix.needsUpdate = true;
  return [bodies, roofs];
}

const SCOOTER_BODY = new THREE.BoxGeometry(0.7, 0.7, 1.9);
const SCOOTER_SEAT = new THREE.BoxGeometry(0.55, 0.3, 0.95);

/** Scooters (Vespas / okadas) parked at the curb — colourful little wedges. */
export function buildScooters(insts: PlaceInst[]): THREE.InstancedMesh[] {
  const bodyMat = new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.45 });
  const seatMat = new THREE.MeshStandardMaterial({
    color: 0x1c1d22,
    flatShading: true,
    roughness: 0.7,
  });
  const bodies = new THREE.InstancedMesh(SCOOTER_BODY, bodyMat, insts.length);
  const seats = new THREE.InstancedMesh(SCOOTER_SEAT, seatMat, insts.length);
  const sc = S.set(1, 1, 1);
  insts.forEach((b, i) => {
    const q = yawQ(b.rotY);
    bodies.setMatrixAt(i, M.compose(P.set(b.x, b.y + 0.5, b.z), q, sc));
    bodies.setColorAt(i, C.set(b.color ?? 0x3aa6c8));
    seats.setMatrixAt(i, M.compose(P.set(b.x, b.y + 0.95, b.z), q, sc));
  });
  bodies.instanceMatrix.needsUpdate = true;
  if (bodies.instanceColor) bodies.instanceColor.needsUpdate = true;
  seats.instanceMatrix.needsUpdate = true;
  return [bodies, seats];
}

/**
 * shared-2b (dakar + lagos flag): a pirogue is a LOFTED shell, not a box.
 *
 * The old hull was a 1.5 × 0.7 × 6.2 BoxGeometry with a darker box sunk into
 * it — from any angle a floating brick, and the dead giveaway at the Lagos
 * stilt village and the Dakar lagoon where a dozen sit side by side. The
 * silhouette a dugout actually has is: a pointed STEM at both ends, a
 * rockered keel that lifts out of the water toward those stems, and SHEER —
 * the gunwale sweeping up to them.
 *
 * `inset` builds the same shell one plank smaller and lower, which is the
 * dark interior (replacing the old inner box). Stations run bow→stern; each
 * carries a 5-point V section [port gunwale, port bilge, keel, stbd bilge,
 * stbd gunwale], and at t = ±1 the half-width collapses to zero so the five
 * points stack into a vertical STEM POST — the prow and stern for free, with
 * no cap geometry. Max footprint is unchanged (1.5 wide × 6.2 long); only the
 * gunwale ends lift, by 0.16, which is the sheer.
 */
function pirogueGeo(inset: boolean): THREE.BufferGeometry {
  const N = 15;
  const HALF_W = inset ? 0.54 : 0.75;
  const KEEL = inset ? -0.23 : -0.35;
  const ROCKER = 0.3; // keel rises this much toward each stem
  const GUN = inset ? 0.3 : 0.35;
  const SHEER = 0.16; // gunwale lifts this much at the stems
  const LEN = inset ? 2.5 : 3.1;
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i < N; i++) {
    const s = i / (N - 1); // 0 = stern, 1 = bow
    const t = s * 2 - 1;
    const z = LEN * t;
    // Fine at both ends, fullest just aft of midships (a working pirogue).
    const hw = HALF_W * Math.pow(Math.sin(Math.PI * s), 0.42) * (1 - 0.08 * t);
    const keel = KEEL + ROCKER * t * t;
    const gun = GUN + SHEER * t * t;
    const bilge = keel + (gun - keel) * 0.42;
    const ring = [
      [-hw, gun],
      [-hw * 0.72, bilge],
      [0, keel],
      [hw * 0.72, bilge],
      [hw, gun],
    ];
    ring.forEach((p, k) => {
      pos.push(p[0], p[1], z);
      uv.push(k / 4, s * 3.2); // planks run along the boat
    });
  }
  for (let i = 0; i < N - 1; i++) {
    for (let k = 0; k < 4; k++) {
      const a0 = i * 5 + k;
      const b0 = a0 + 5;
      idx.push(a0, b0, a0 + 1, a0 + 1, b0, b0 + 1);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(uv), 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}
const BOAT_HULL = pirogueGeo(false);
const BOAT_INNER = pirogueGeo(true);

/** Long open boats (pirogues / fishing boats), floating on water. */
export function buildBoats(insts: PlaceInst[]): THREE.InstancedMesh[] {
  const hullMat = new THREE.MeshStandardMaterial({
    roughness: 0.78,
    side: THREE.DoubleSide, // an open shell: both banks of the hull must read
    map: propTexture('plank'), // shared with the stilt village's timber
    bumpMap: propTexture('plank'),
    bumpScale: 0.35,
  });
  const innerMat = new THREE.MeshStandardMaterial({
    color: 0x2a2118,
    roughness: 0.92,
    side: THREE.DoubleSide,
    map: propTexture('plank'),
  });
  const hulls = new THREE.InstancedMesh(BOAT_HULL, hullMat, insts.length);
  const inners = new THREE.InstancedMesh(BOAT_INNER, innerMat, insts.length);
  insts.forEach((b, i) => {
    const s = b.s ?? 1;
    const q = yawQ(b.rotY);
    hulls.setMatrixAt(i, M.compose(P.set(b.x, b.y + 0.35, b.z), q, S.set(s, 1, s)));
    hulls.setColorAt(i, C.set(b.color ?? 0xd8563a));
    // Same origin as the hull: pirogueGeo(true) already sits a plank higher
    // and narrower inside it, so the dark interior shows through the opening.
    inners.setMatrixAt(i, M.compose(P.set(b.x, b.y + 0.35, b.z), q, S.set(s, 1, s)));
  });
  hulls.instanceMatrix.needsUpdate = true;
  if (hulls.instanceColor) hulls.instanceColor.needsUpdate = true;
  inners.instanceMatrix.needsUpdate = true;
  return [hulls, inners];
}

const STALL_TABLE = new THREE.BoxGeometry(2.4, 1.0, 2.4);

/**
 * shared-2b (dakar flag): the awning is CLOTH, so it sags.
 *
 * It used to be a 3.0 × 0.16 × 2.8 box — a rigid board, and the one prop the
 * Dakar market shows a dozen of at kerb distance. This is the same 3.0 × 2.8
 * footprint as a zero-thickness sheet whose height is driven by where the
 * corner POSTS hold it up (dressStall in zones/dakar.ts pitches four at local
 * ±1.32 × ±1.22): taut at the post ring, dipping between the posts, and
 * drooping past them into an eave the way a tied-down tarp does.
 *
 * The ring sits `RING_DROP` below the sheet's own origin so that with the
 * 0.16 tilt the HIGHEST point of cloth over a post is 2.434 — just under the
 * 2.44 the Dakar posts run to. That keeps the round's contract intact: the
 * posts always touch the cloth (never the floating-canopy gap), with a few
 * centimetres of pole head showing, exactly as before.
 */
function stallAwningGeo(): THREE.BufferGeometry {
  const NX = 9;
  const NZ = 9;
  const HX = 1.5;
  const HZ = 1.4;
  const POST_U = 1.32 / HX; // where the posts grip, in half-width units
  const POST_V = 1.22 / HZ;
  const RING_DROP = 0.06; // cloth height at the post ring (see the header)
  const SAG_IN = 0.13; // dip at the centre of the four posts
  const SAG_EAVE = 0.17; // extra droop from the post ring out to the hem
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  for (let j = 0; j < NZ; j++) {
    const v = (j / (NZ - 1)) * 2 - 1;
    for (let i = 0; i < NX; i++) {
      const u = (i / (NX - 1)) * 2 - 1;
      // Distance from the stall centre in "post ring" units: 1 = on the ring.
      const r = Math.max(Math.abs(u) / POST_U, Math.abs(v) / POST_V);
      const y =
        r <= 1
          ? -RING_DROP - SAG_IN * Math.cos((r * Math.PI) / 2)
          : -RING_DROP - SAG_EAVE * Math.min(1, (r - 1) / (1 / POST_U - 1));
      pos.push(u * HX, y, v * HZ);
      uv.push((u + 1) * 1.1, (v + 1) * 1.1);
    }
  }
  for (let j = 0; j < NZ - 1; j++) {
    for (let i = 0; i < NX - 1; i++) {
      const a0 = j * NX + i;
      const b0 = a0 + NX;
      idx.push(a0, b0, a0 + 1, a0 + 1, b0, b0 + 1);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(uv), 2));
  g.setIndex(idx);
  g.computeVertexNormals(); // smooth — a faceted tarp is not a tarp
  return g;
}
const STALL_AWNING = stallAwningGeo();

/** Awning tilt euler — 'YXZ' (yaw FIRST, then pitch about the awning's own
 *  lateral axis), the FROND_E precedent. MEASURED BUG: the old code fed the
 *  shared 'XYZ' scratch euler, whose X rotation is applied AFTER the yaw —
 *  i.e. about world X — so two identical stalls facing different ways tipped
 *  in different directions and their undersides landed anywhere in 2.0-2.44.
 *  Dedicated scratch object; never change the shared E's order. */
const AWNING_E = new THREE.Euler(0, 0, 0, 'YXZ');

/** Market stalls: a goods table under a bright sagging awning. */
export function buildStalls(insts: PlaceInst[]): THREE.InstancedMesh[] {
  const tableMat = new THREE.MeshStandardMaterial({
    color: 0x7a5a38,
    flatShading: true,
    roughness: 0.95,
  });
  const awningMat = new THREE.MeshStandardMaterial({
    roughness: 0.88,
    side: THREE.DoubleSide, // you stand UNDER a market awning
  });
  const tables = new THREE.InstancedMesh(STALL_TABLE, tableMat, insts.length);
  const awnings = new THREE.InstancedMesh(STALL_AWNING, awningMat, insts.length);
  const sc = S.set(1, 1, 1);
  insts.forEach((b, i) => {
    tables.setMatrixAt(i, M.compose(P.set(b.x, b.y + 0.5, b.z), yawQ(b.rotY), sc));
    AWNING_E.set(0.16, b.rotY, 0); // awning tips down-course, the same way on every stall
    Q.setFromEuler(AWNING_E);
    awnings.setMatrixAt(i, M.compose(P.set(b.x, b.y + 2.3, b.z), Q, sc));
    awnings.setColorAt(i, C.set(b.color ?? 0xe0a818));
  });
  tables.instanceMatrix.needsUpdate = true;
  awnings.instanceMatrix.needsUpdate = true;
  if (awnings.instanceColor) awnings.instanceColor.needsUpdate = true;
  return [tables, awnings];
}

const UMBRELLA_POLE = new THREE.CylinderGeometry(0.06, 0.06, 2.6, 5);
const UMBRELLA_TOP = new THREE.CylinderGeometry(0, 1.9, 0.8, 8);

/** Parasols — beach or market. Bright canopy colour per instance. */
export function buildUmbrellas(insts: PlaceInst[]): THREE.InstancedMesh[] {
  const poleMat = new THREE.MeshStandardMaterial({
    color: 0xe8e2d2,
    flatShading: true,
    roughness: 0.8,
  });
  const topMat = new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.8 });
  const poles = new THREE.InstancedMesh(UMBRELLA_POLE, poleMat, insts.length);
  const tops = new THREE.InstancedMesh(UMBRELLA_TOP, topMat, insts.length);
  insts.forEach((b, i) => {
    const s = b.s ?? 1;
    poles.setMatrixAt(i, M.compose(P.set(b.x, b.y + 1.3, b.z), ID_Q, S.set(1, s, 1)));
    tops.setMatrixAt(i, M.compose(P.set(b.x, b.y + 2.6 * s, b.z), ID_Q, S.set(s, s, s)));
    tops.setColorAt(i, C.set(b.color ?? 0xff5a3c));
  });
  poles.instanceMatrix.needsUpdate = true;
  tops.instanceMatrix.needsUpdate = true;
  if (tops.instanceColor) tops.instanceColor.needsUpdate = true;
  return [poles, tops];
}

const STILT_LEG = new THREE.BoxGeometry(0.3, 3.4, 0.3);
const STILT_BODY = new THREE.BoxGeometry(4.6, 2.6, 3.6);

/** Stilt-village houses standing IN the lagoon (Makoko): legs + cabin + roof. */
export function buildStiltHouses(insts: PlaceInst[]): THREE.InstancedMesh[] {
  const legMat = new THREE.MeshStandardMaterial({
    color: 0x4a3a28,
    flatShading: true,
    roughness: 1,
  });
  // shared-2b (lagos flag): the hut walls carry the shared PLANK canvas, so
  // the zone round's dressing (verandas, doorways, batten strips) sits on a
  // surface that reads as sawn timber instead of a flat colour chip. Same
  // canvas the pirogues wear — one village, one timber.
  const bodyMat = new THREE.MeshStandardMaterial({
    flatShading: true,
    roughness: 0.95,
    map: propTexture('plank'),
    bumpMap: propTexture('plank'),
    bumpScale: 0.45,
  });
  patchInstanceTiling(bodyMat, 0.55);
  const roofMat = new THREE.MeshStandardMaterial({
    color: 0x8a4a33,
    flatShading: true,
    roughness: 0.8,
    map: roofTexture(), // corrugated sheet — the Makoko roof
  });
  patchRoofTiling(roofMat);
  const legs = new THREE.InstancedMesh(STILT_LEG, legMat, insts.length * 4);
  const bodies = new THREE.InstancedMesh(STILT_BODY, bodyMat, insts.length);
  const roofs = new THREE.InstancedMesh(
    new THREE.CylinderGeometry(0, 3.4, 1.6, 4),
    roofMat,
    insts.length,
  );
  insts.forEach((b, i) => {
    const q = yawQ(b.rotY);
    const cosr = Math.cos(b.rotY);
    const sinr = Math.sin(b.rotY);
    let leg = 0;
    for (const lx of [-1.8, 1.8])
      for (const lz of [-1.3, 1.3]) {
        const wx = b.x + lx * cosr + lz * sinr;
        const wz = b.z - lx * sinr + lz * cosr;
        legs.setMatrixAt(i * 4 + leg++, M.compose(P.set(wx, b.y + 1.7, wz), q, S.set(1, 1, 1)));
      }
    bodies.setMatrixAt(i, M.compose(P.set(b.x, b.y + 4.6, b.z), q, S.set(1, 1, 1)));
    bodies.setColorAt(i, C.set(b.color ?? 0x9a8a6a));
    E.set(0, b.rotY + Math.PI / 4, 0);
    Q.setFromEuler(E);
    roofs.setMatrixAt(i, M.compose(P.set(b.x, b.y + 6.7, b.z), Q, S.set(1.6, 1, 1.3)));
  });
  legs.instanceMatrix.needsUpdate = true;
  bodies.instanceMatrix.needsUpdate = true;
  if (bodies.instanceColor) bodies.instanceColor.needsUpdate = true;
  roofs.instanceMatrix.needsUpdate = true;
  return [legs, bodies, roofs];
}

const LANTERN_GEO = new THREE.CylinderGeometry(0.5, 0.5, 0.85, 8);
const LANTERN_CAP = new THREE.CylinderGeometry(0.55, 0.55, 0.12, 8);

/** Glowing red paper lanterns (hung at the given y). */
export function buildLanterns(insts: PlaceInst[]): THREE.InstancedMesh[] {
  const glowMat = new THREE.MeshStandardMaterial({
    color: 0xe83a2a,
    emissive: 0xff4a30,
    emissiveIntensity: 0.95,
    flatShading: true,
    roughness: 0.6,
  });
  const capMat = new THREE.MeshStandardMaterial({
    color: 0x201410,
    flatShading: true,
    roughness: 0.8,
  });
  const bodies = new THREE.InstancedMesh(LANTERN_GEO, glowMat, insts.length);
  const caps = new THREE.InstancedMesh(LANTERN_CAP, capMat, insts.length * 2);
  insts.forEach((b, i) => {
    const s = b.s ?? 1;
    bodies.setMatrixAt(i, M.compose(P.set(b.x, b.y, b.z), ID_Q, S.set(s, s, s)));
    caps.setMatrixAt(i * 2, M.compose(P.set(b.x, b.y + 0.5 * s, b.z), ID_Q, S.set(s, s, s)));
    caps.setMatrixAt(i * 2 + 1, M.compose(P.set(b.x, b.y - 0.5 * s, b.z), ID_Q, S.set(s, s, s)));
  });
  bodies.instanceMatrix.needsUpdate = true;
  caps.instanceMatrix.needsUpdate = true;
  return [bodies, caps];
}

// shared-pass: `buildLaundry` lived here and is GONE. Napoli's washing moved to
// scenery/laundryNapoli.ts (a real sagging catenary of rolled segments, its own
// CLOTH_COLORS), Scenery.ts builds `a.laundry` from there, and nothing in the
// repo referenced this copy any more. The `laundry` array itself is untouched.

const FENCE_RAIL = new THREE.BoxGeometry(0.09, 0.09, 6.6);
const FENCE_POST = new THREE.BoxGeometry(0.16, 1.3, 0.16);

/** Estancia fence segments running along the roadside (Patagonia). */
export function buildFences(insts: PlaceInst[]): THREE.InstancedMesh[] {
  const wood = new THREE.MeshStandardMaterial({ color: 0x6a5844, flatShading: true, roughness: 1 });
  const rails = new THREE.InstancedMesh(FENCE_RAIL, wood, insts.length * 2);
  const posts = new THREE.InstancedMesh(FENCE_POST, wood, insts.length * 3);
  insts.forEach((b, i) => {
    const q = yawQ(b.rotY);
    rails.setMatrixAt(i * 2, M.compose(P.set(b.x, b.y + 0.85, b.z), q, S.set(1, 1, 1)));
    rails.setMatrixAt(i * 2 + 1, M.compose(P.set(b.x, b.y + 1.2, b.z), q, S.set(1, 1, 1)));
    const cosr = Math.cos(b.rotY);
    const sinr = Math.sin(b.rotY);
    for (let k = 0; k < 3; k++) {
      const lz = (k - 1) * 3.0;
      const wx = b.x + lz * sinr;
      const wz = b.z + lz * cosr;
      posts.setMatrixAt(i * 3 + k, M.compose(P.set(wx, b.y + 0.65, wz), q, S.set(1, 1, 1)));
    }
  });
  rails.instanceMatrix.needsUpdate = true;
  posts.instanceMatrix.needsUpdate = true;
  return [rails, posts];
}

const ICEBERG_GEO = new THREE.IcosahedronGeometry(1, 0);

/** Floating glacial ice — white-blue shards sitting in the water. */
export function buildIcebergs(insts: PlaceInst[]): THREE.InstancedMesh {
  const ice = new THREE.MeshStandardMaterial({
    color: 0xeaf6ff,
    emissive: 0x9fd8f0,
    emissiveIntensity: 0.22,
    flatShading: true,
    roughness: 0.35,
  });
  const mesh = new THREE.InstancedMesh(ICEBERG_GEO, ice, insts.length);
  insts.forEach((b, i) => {
    const s = b.s ?? 1;
    E.set(0, b.rotY, (i % 3) * 0.16 - 0.16);
    Q.setFromEuler(E);
    mesh.setMatrixAt(i, M.compose(P.set(b.x, b.y, b.z), Q, S.set(s * 1.6, s * 0.9, s)));
  });
  mesh.instanceMatrix.needsUpdate = true;
  return mesh;
}

/**
 * shared-2b (dakar flag): a salt heap is CRYSTAL, not a smooth cone.
 *
 * The old prop was a bare 7-sided cone in matte off-white — at the Lac Rose
 * shore, where these are the signature, it read as a party hat. Salt piled by
 * hand slumps into a lumpy heap and the faces catch light in glints, so the
 * cone now carries deterministic radial lumps (position-derived, never rng,
 * tapering to zero at the ground rim so the footprint is exactly what it was)
 * and the material gets a crystal speckle map doubling as a ROUGHNESS map —
 * bright grains go glossy against a matte heap, which is what makes salt
 * sparkle as the camera moves.
 */
function saltGeo(): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(0, 1.1, 1.4, 11, 4);
  const pos = g.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const t = Math.min(1, (y + 0.7) / 1.4); // 0 at the base rim, 1 at the peak
    if (t <= 0.02) continue;
    const n = Math.sin(x * 7.7 + z * 5.3) * 0.6 + Math.sin(z * 9.1 - x * 3.7 + 1.3) * 0.4;
    const k = 1 + n * 0.13 * Math.min(1, t * 2.4);
    pos.setXYZ(i, x * k, y + n * 0.05 * t, z * k);
  }
  pos.needsUpdate = true;
  g.computeVertexNormals();
  return g;
}
const SALT_GEO = saltGeo();

let saltTexPair: [THREE.CanvasTexture, THREE.CanvasTexture] | null = null;
/** Coarse salt as a PAIR of aligned maps: [colour, roughness]. A bright
 *  crystal field over damp packed salt — and the roughness map is that field
 *  INVERTED, because a roughness map's bright values mean MATTE. So the
 *  grains that read brightest are also the glossiest, which is exactly the
 *  glint that makes a salt heap read as salt as the camera moves. */
function saltTextures(): [THREE.CanvasTexture, THREE.CanvasTexture] {
  if (saltTexPair) return saltTexPair;
  _texSeed = 0x5a17;
  const [c, x] = texCanvas(64);
  // NOTE the base is BRIGHT: this map multiplies the near-white salt colour,
  // so a dark base (the first pass used gray(150)) turns a salt heap gray.
  // Keep the mean high and buy the crystal read from the SPREAD instead.
  x.fillStyle = gray(190); // packed damp salt between the crystals
  x.fillRect(0, 0, 64, 64);
  for (let i = 0; i < 900; i++) {
    const s = 1 + texRand() * 2.2;
    x.fillStyle = gray(212 + ((texRand() * 43) | 0));
    x.fillRect(texRand() * 64, texRand() * 64, s, s);
  }
  // A few big flakes that catch a real highlight.
  for (let i = 0; i < 40; i++) {
    x.fillStyle = gray(255);
    x.fillRect(texRand() * 64, texRand() * 64, 2 + texRand() * 2, 1);
  }
  const [rc, rx] = texCanvas(64);
  const src = x.getImageData(0, 0, 64, 64);
  const dst = rx.createImageData(64, 64);
  for (let i = 0; i < src.data.length; i += 4) {
    // Invert, then compress into 0.45..1 of the material roughness so even a
    // flake keeps some diffuse (a mirror flake goes black against night IBL —
    // the M114 metal-tyre lesson).
    const inv = 255 - src.data[i];
    const v = 115 + (inv * 140) / 255;
    dst.data[i] = dst.data[i + 1] = dst.data[i + 2] = v;
    dst.data[i + 3] = 255;
  }
  rx.putImageData(dst, 0, 0);
  saltTexPair = [shareTex(new THREE.CanvasTexture(c)), shareDataTex(new THREE.CanvasTexture(rc))];
  return saltTexPair;
}

/** Salt-harvest mounds along the pink lake shore (Dakar). */
export function buildSaltMounds(insts: PlaceInst[]): THREE.InstancedMesh {
  const [saltMap, saltRough] = saltTextures();
  const salt = new THREE.MeshStandardMaterial({
    color: 0xf2efe6,
    roughness: 0.95, // × the (inverted) roughness map → 0.43 flake … 0.95 heap
    map: saltMap,
    roughnessMap: saltRough,
    bumpMap: saltMap,
    bumpScale: 0.18, // enough to catch the sun on the grains; more reads as rock
  });
  patchInstanceTiling(salt, 0.6);
  const mesh = new THREE.InstancedMesh(SALT_GEO, salt, insts.length);
  insts.forEach((b, i) => {
    const s = b.s ?? 1;
    mesh.setMatrixAt(i, M.compose(P.set(b.x, b.y + 0.7 * s, b.z), yawQ(b.rotY), S.set(s, s, s)));
  });
  mesh.instanceMatrix.needsUpdate = true;
  return mesh;
}

// ---- Leaning overhang trees (M98) — the "environment bleeding onto the
//      track" effect: trunks on the verge tilted toward the road, canopies
//      hanging HIGH over it (underside ≥ ~6, so the chase-cam's view of the
//      ball is never covered — see scenery/leanTrees.ts for the placement +
//      the shared shape maths these transforms mirror). ----

const LEAN_TRUNK_GEO = new THREE.CylinderGeometry(0.17, 0.36, 1, 8);
LEAN_TRUNK_GEO.translate(0, 0.5, 0); // base at the origin; scaled to length
scaleUv(LEAN_TRUNK_GEO, 2, 3.5); // unit height → stretched to ~8-10 units
const LEAN_SEG_GEO = new THREE.CylinderGeometry(0.15, 0.27, 1, 8);
LEAN_SEG_GEO.translate(0, 0.5, 0);
scaleUv(LEAN_SEG_GEO, 2, 1.3); // one palm trunk segment ≈ 3.3 units

export function buildLeanTrees(trees: LeanTree[]): THREE.Object3D[] {
  const blobTrees = trees.filter((t) => t.species !== 'palm');
  const palms = trees.filter((t) => t.species === 'palm');
  const out: THREE.Object3D[] = [];

  if (blobTrees.length) {
    const trunkMat = trunkMaterial(0xffffff); // per-instance species colour below
    const foliageMat = leafMaterial(foliageAtlas());
    // Sakura hangs over a NIGHT city — unlit it silhouettes into dark blobs
    // against the neon (measured), so the blossom is softly self-lit
    // (illuminated hanami, matching Tokyo's lantern glow language).
    const sakuraMat = leafMaterial(foliageAtlas(), {
      emissive: 0xff9ec8,
      emissiveIntensity: 0.26,
    });
    let blobCount = 0;
    let sakuraCount = 0;
    for (const t of blobTrees) {
      const n = LEAN_SHAPES[t.species as Exclude<LeanSpecies, 'palm'>].blobs.length;
      if (t.species === 'sakura') sakuraCount += n;
      else blobCount += n;
    }
    const trunks = new THREE.InstancedMesh(LEAN_TRUNK_GEO, trunkMat, blobTrees.length);
    const foliage = new THREE.InstancedMesh(FOLIAGE_GEO, foliageMat, Math.max(1, blobCount));
    const sakura = new THREE.InstancedMesh(FOLIAGE_GEO, sakuraMat, Math.max(1, sakuraCount));
    foliage.count = blobCount;
    sakura.count = sakuraCount;
    let bi = 0;
    let si = 0;
    blobTrees.forEach((t, i) => {
      const shape = LEAN_SHAPES[t.species as Exclude<LeanSpecies, 'palm'>];
      const L = shape.trunkLen * t.s;
      // Tilt about the local forward axis, toward the road (same Euler pattern
      // as the standing palms: yaw first, then the Z-axis lean).
      E.set(0, t.dir, t.side * t.lean);
      Q.setFromEuler(E);
      P.set(t.x, t.y, t.z);
      trunks.setMatrixAt(i, M.compose(P, Q, S.set(t.s, L, t.s)));
      trunks.setColorAt(i, barkTint(shape.trunkColor)); // bark map darkens; compensate
      // Canopy blobs hang off the trunk tip, reaching further over the road.
      const inX = -t.side * Math.cos(t.dir); // inward = toward the centreline
      const inZ = t.side * Math.sin(t.dir);
      const tipReach = Math.sin(t.lean) * L;
      const tipRise = Math.cos(t.lean) * L;
      const target = t.species === 'sakura' ? sakura : foliage;
      for (const b of shape.blobs) {
        const reach = tipReach + b.dx * t.s;
        P.set(t.x + inX * reach, t.y + tipRise + b.dy * t.s, t.z + inZ * reach);
        const r = (b.r * t.s) / 1.55; // FOLIAGE_GEO radius is 1.55
        const idx = t.species === 'sakura' ? si++ : bi++;
        target.setMatrixAt(idx, M.compose(P, yawQ(hash(t.seed + idx) * Math.PI * 2), S.set(r, r * b.sy, r)));
        target.setColorAt(idx, C.set(t.color).multiplyScalar(0.86 + hash(t.seed * 3 + idx) * 0.26));
      }
    });
    trunks.instanceMatrix.needsUpdate = true;
    if (trunks.instanceColor) trunks.instanceColor.needsUpdate = true;
    for (const mesh of [foliage, sakura]) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
    out.push(trunks);
    if (blobCount) out.push(foliage);
    if (sakuraCount) out.push(sakura);
  }

  if (palms.length) {
    const trunkMat = trunkMaterial(LEAN_PALM.trunkColor);
    const frondMat = leafMaterial(palmFrondTexture(), {
      color: 0x3aa050,
      vertexColors: false, // PALM_FROND carries no baked colour attribute
    });
    const segs = new THREE.InstancedMesh(LEAN_SEG_GEO, trunkMat, palms.length * 3);
    const fronds = new THREE.InstancedMesh(PALM_FROND, frondMat, palms.length * LEAN_PALM_FRONDS);
    palms.forEach((t, i) => {
      const inX = -t.side * Math.cos(t.dir);
      const inZ = t.side * Math.sin(t.dir);
      // Three stacked segments bending progressively toward the road (the
      // 0.55/1.0/1.45 curve — the same numbers tipOf() integrates).
      let px = t.x;
      let py = t.y;
      let pz = t.z;
      [0.55, 1.0, 1.45].forEach((k, j) => {
        const ang = t.lean * k;
        const len = LEAN_PALM.segLen * t.s;
        E.set(0, t.dir, t.side * ang);
        Q.setFromEuler(E);
        segs.setMatrixAt(i * 3 + j, M.compose(P.set(px, py, pz), Q, S.set(t.s, len, t.s)));
        px += inX * Math.sin(ang) * len;
        py += Math.cos(ang) * len;
        pz += inZ * Math.sin(ang) * len;
      });
      // Crown: the age-banded fan from leanTrees.leanPalmFronds — a rising
      // young spear, arching mature blades, and short hard-drooping old ones,
      // at jittered azimuths. The old fan was seven IDENTICAL blades at one
      // shallow pitch on an exact 2π/7 ring, and that symmetry (not the leaf)
      // is what read as a flat green PAPER STAR from the road and from above.
      // The pose model owns the envelope too — reach 3.86 -> 3.77 ×s, dip
      // 1.25 -> 1.22 ×s, both INSIDE the crown it replaces — so
      // canopyClearance's 1.35·s promise, and therefore every placement, is
      // untouched. Pinned in tests/world/leanPalmCrown.test.ts.
      leanPalmFronds(t.seed).forEach((q, f) => {
        FROND_E.set(q.pitch, q.yaw, 0);
        Q.setFromEuler(FROND_E);
        P.set(
          px + Math.sin(q.yaw) * q.radial * t.s,
          py - 0.1 * t.s,
          pz + Math.cos(q.yaw) * q.radial * t.s,
        );
        fronds.setMatrixAt(i * LEAN_PALM_FRONDS + f, M.compose(P, Q, S.set(t.s, t.s, t.s * q.len)));
      });
    });
    segs.instanceMatrix.needsUpdate = true;
    fronds.instanceMatrix.needsUpdate = true;
    out.push(segs, fronds);
  }
  return out;
}
